/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function arrayMap(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return assocIndexOf(this.__data__, key) > -1;
}

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache.prototype.clear = listCacheClear;
ListCache.prototype['delete'] = listCacheDelete;
ListCache.prototype.get = listCacheGet;
ListCache.prototype.has = listCacheHas;
ListCache.prototype.set = listCacheSet;

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear() {
  this.__data__ = new ListCache;
  this.size = 0;
}

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function stackDelete(key) {
  var data = this.__data__,
      result = data['delete'](key);

  this.size = data.size;
  return result;
}

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function stackGet(key) {
  return this.__data__.get(key);
}

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function stackHas(key) {
  return this.__data__.has(key);
}

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof global == 'object' && global && global.Object === Object && global;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/** Built-in value references. */
var Symbol = root.Symbol;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/** Built-in value references. */
var symToStringTag = Symbol ? Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag),
      tag = value[symToStringTag];

  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString$1.call(value);
}

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag$1 = Symbol ? Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag$1 && symToStringTag$1 in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  if (!isObject(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = baseGetTag(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}

/** Used to detect overreaching core-js shims. */
var coreJsData = root['__core-js_shared__'];

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

/** Used for built-in method references. */
var funcProto = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto$1 = Function.prototype,
    objectProto$2 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$2.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString$1.call(hasOwnProperty$1).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject(value) || isMasked(value)) {
    return false;
  }
  var pattern = isFunction(value) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource(value));
}

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = getValue(object, key);
  return baseIsNative(value) ? value : undefined;
}

/* Built-in method references that are verified to be native. */
var Map = getNative(root, 'Map');

/* Built-in method references that are verified to be native. */
var nativeCreate = getNative(Object, 'create');

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = nativeCreate ? nativeCreate(null) : {};
  this.size = 0;
}

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$3.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty$2.call(data, key) ? data[key] : undefined;
}

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return nativeCreate ? (data[key] !== undefined) : hasOwnProperty$3.call(data, key);
}

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (nativeCreate && value === undefined) ? HASH_UNDEFINED$1 : value;
  return this;
}

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash.prototype.clear = hashClear;
Hash.prototype['delete'] = hashDelete;
Hash.prototype.get = hashGet;
Hash.prototype.has = hashHas;
Hash.prototype.set = hashSet;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new Hash,
    'map': new (Map || ListCache),
    'string': new Hash
  };
}

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  var result = getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return getMapData(this, key).get(key);
}

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return getMapData(this, key).has(key);
}

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  var data = getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache.prototype.clear = mapCacheClear;
MapCache.prototype['delete'] = mapCacheDelete;
MapCache.prototype.get = mapCacheGet;
MapCache.prototype.has = mapCacheHas;
MapCache.prototype.set = mapCacheSet;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet(key, value) {
  var data = this.__data__;
  if (data instanceof ListCache) {
    var pairs = data.__data__;
    if (!Map || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new MapCache(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack(entries) {
  var data = this.__data__ = new ListCache(entries);
  this.size = data.size;
}

// Add methods to `Stack`.
Stack.prototype.clear = stackClear;
Stack.prototype['delete'] = stackDelete;
Stack.prototype.get = stackGet;
Stack.prototype.has = stackHas;
Stack.prototype.set = stackSet;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$2 = '__lodash_hash_undefined__';

/**
 * Adds `value` to the array cache.
 *
 * @private
 * @name add
 * @memberOf SetCache
 * @alias push
 * @param {*} value The value to cache.
 * @returns {Object} Returns the cache instance.
 */
function setCacheAdd(value) {
  this.__data__.set(value, HASH_UNDEFINED$2);
  return this;
}

/**
 * Checks if `value` is in the array cache.
 *
 * @private
 * @name has
 * @memberOf SetCache
 * @param {*} value The value to search for.
 * @returns {number} Returns `true` if `value` is found, else `false`.
 */
function setCacheHas(value) {
  return this.__data__.has(value);
}

/**
 *
 * Creates an array cache object to store unique values.
 *
 * @private
 * @constructor
 * @param {Array} [values] The values to cache.
 */
function SetCache(values) {
  var index = -1,
      length = values == null ? 0 : values.length;

  this.__data__ = new MapCache;
  while (++index < length) {
    this.add(values[index]);
  }
}

// Add methods to `SetCache`.
SetCache.prototype.add = SetCache.prototype.push = setCacheAdd;
SetCache.prototype.has = setCacheHas;

/**
 * A specialized version of `_.some` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */
function arraySome(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }
  return false;
}

/**
 * Checks if a `cache` value for `key` exists.
 *
 * @private
 * @param {Object} cache The cache to query.
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function cacheHas(cache, key) {
  return cache.has(key);
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG = 1,
    COMPARE_UNORDERED_FLAG = 2;

/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `array` and `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */
function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
      arrLength = array.length,
      othLength = other.length;

  if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
    return false;
  }
  // Assume cyclic values are equal.
  var stacked = stack.get(array);
  if (stacked && stack.get(other)) {
    return stacked == other;
  }
  var index = -1,
      result = true,
      seen = (bitmask & COMPARE_UNORDERED_FLAG) ? new SetCache : undefined;

  stack.set(array, other);
  stack.set(other, array);

  // Ignore non-index properties.
  while (++index < arrLength) {
    var arrValue = array[index],
        othValue = other[index];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, arrValue, index, other, array, stack)
        : customizer(arrValue, othValue, index, array, other, stack);
    }
    if (compared !== undefined) {
      if (compared) {
        continue;
      }
      result = false;
      break;
    }
    // Recursively compare arrays (susceptible to call stack limits).
    if (seen) {
      if (!arraySome(other, function(othValue, othIndex) {
            if (!cacheHas(seen, othIndex) &&
                (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
              return seen.push(othIndex);
            }
          })) {
        result = false;
        break;
      }
    } else if (!(
          arrValue === othValue ||
            equalFunc(arrValue, othValue, bitmask, customizer, stack)
        )) {
      result = false;
      break;
    }
  }
  stack['delete'](array);
  stack['delete'](other);
  return result;
}

/** Built-in value references. */
var Uint8Array = root.Uint8Array;

/**
 * Converts `map` to its key-value pairs.
 *
 * @private
 * @param {Object} map The map to convert.
 * @returns {Array} Returns the key-value pairs.
 */
function mapToArray(map) {
  var index = -1,
      result = Array(map.size);

  map.forEach(function(value, key) {
    result[++index] = [key, value];
  });
  return result;
}

/**
 * Converts `set` to an array of its values.
 *
 * @private
 * @param {Object} set The set to convert.
 * @returns {Array} Returns the values.
 */
function setToArray(set) {
  var index = -1,
      result = Array(set.size);

  set.forEach(function(value) {
    result[++index] = value;
  });
  return result;
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$1 = 1,
    COMPARE_UNORDERED_FLAG$1 = 2;

/** `Object#toString` result references. */
var boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    mapTag = '[object Map]',
    numberTag = '[object Number]',
    regexpTag = '[object RegExp]',
    setTag = '[object Set]',
    stringTag = '[object String]',
    symbolTag = '[object Symbol]';

var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]';

/** Used to convert symbols to primitives and strings. */
var symbolProto = Symbol ? Symbol.prototype : undefined,
    symbolValueOf = symbolProto ? symbolProto.valueOf : undefined;

/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
  switch (tag) {
    case dataViewTag:
      if ((object.byteLength != other.byteLength) ||
          (object.byteOffset != other.byteOffset)) {
        return false;
      }
      object = object.buffer;
      other = other.buffer;

    case arrayBufferTag:
      if ((object.byteLength != other.byteLength) ||
          !equalFunc(new Uint8Array(object), new Uint8Array(other))) {
        return false;
      }
      return true;

    case boolTag:
    case dateTag:
    case numberTag:
      // Coerce booleans to `1` or `0` and dates to milliseconds.
      // Invalid dates are coerced to `NaN`.
      return eq(+object, +other);

    case errorTag:
      return object.name == other.name && object.message == other.message;

    case regexpTag:
    case stringTag:
      // Coerce regexes to strings and treat strings, primitives and objects,
      // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
      // for more details.
      return object == (other + '');

    case mapTag:
      var convert = mapToArray;

    case setTag:
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG$1;
      convert || (convert = setToArray);

      if (object.size != other.size && !isPartial) {
        return false;
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(object);
      if (stacked) {
        return stacked == other;
      }
      bitmask |= COMPARE_UNORDERED_FLAG$1;

      // Recursively compare objects (susceptible to call stack limits).
      stack.set(object, other);
      var result = equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
      stack['delete'](object);
      return result;

    case symbolTag:
      if (symbolValueOf) {
        return symbolValueOf.call(object) == symbolValueOf.call(other);
      }
  }
  return false;
}

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */
function arrayPush(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */
function baseGetAllKeys(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray(object) ? result : arrayPush(result, symbolsFunc(object));
}

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function arrayFilter(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */
function stubArray() {
  return [];
}

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$5.propertyIsEnumerable;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbols = !nativeGetSymbols ? stubArray : function(object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return arrayFilter(nativeGetSymbols(object), function(symbol) {
    return propertyIsEnumerable.call(object, symbol);
  });
};

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

/** `Object#toString` result references. */
var argsTag = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments(value) {
  return isObjectLike(value) && baseGetTag(value) == argsTag;
}

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$6.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$6.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
  return isObjectLike(value) && hasOwnProperty$4.call(value, 'callee') &&
    !propertyIsEnumerable$1.call(value, 'callee');
};

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}

/** Detect free variable `exports`. */
var freeExports = typeof exports == 'object' && exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && typeof module == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? root.Buffer : undefined;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */
var isBuffer = nativeIsBuffer || stubFalse;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$1;
}

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]',
    arrayTag = '[object Array]',
    boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    errorTag$1 = '[object Error]',
    funcTag$1 = '[object Function]',
    mapTag$1 = '[object Map]',
    numberTag$1 = '[object Number]',
    objectTag = '[object Object]',
    regexpTag$1 = '[object RegExp]',
    setTag$1 = '[object Set]',
    stringTag$1 = '[object String]',
    weakMapTag = '[object WeakMap]';

var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$1 = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag] =
typedArrayTags[arrayBufferTag$1] = typedArrayTags[boolTag$1] =
typedArrayTags[dataViewTag$1] = typedArrayTags[dateTag$1] =
typedArrayTags[errorTag$1] = typedArrayTags[funcTag$1] =
typedArrayTags[mapTag$1] = typedArrayTags[numberTag$1] =
typedArrayTags[objectTag] = typedArrayTags[regexpTag$1] =
typedArrayTags[setTag$1] = typedArrayTags[stringTag$1] =
typedArrayTags[weakMapTag] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray(value) {
  return isObjectLike(value) &&
    isLength(value.length) && !!typedArrayTags[baseGetTag(value)];
}

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function(value) {
    return func(value);
  };
}

/** Detect free variable `exports`. */
var freeExports$1 = typeof exports == 'object' && exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule$1 = freeExports$1 && typeof module == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports$1 = freeModule$1 && freeModule$1.exports === freeExports$1;

/** Detect free variable `process` from Node.js. */
var freeProcess = moduleExports$1 && freeGlobal.process;

/** Used to access faster Node.js helpers. */
var nodeUtil = (function() {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule$1 && freeModule$1.require && freeModule$1.require('util').types;

    if (types) {
      return types;
    }

    // Legacy `process.binding('util')` for Node.js < 10.
    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}());

/* Node.js helper references. */
var nodeIsTypedArray = nodeUtil && nodeUtil.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$7.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys(value, inherited) {
  var isArr = isArray(value),
      isArg = !isArr && isArguments(value),
      isBuff = !isArr && !isArg && isBuffer(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$5.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           isIndex(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$8;

  return value === proto;
}

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys = overArg(Object.keys, Object);

/** Used for built-in method references. */
var objectProto$9 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$9.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys(object) {
  if (!isPrototype(object)) {
    return nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$6.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength(value.length) && !isFunction(value);
}

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys(object) {
  return isArrayLike(object) ? arrayLikeKeys(object) : baseKeys(object);
}

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeys(object) {
  return baseGetAllKeys(object, keys, getSymbols);
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$2 = 1;

/** Used for built-in method references. */
var objectProto$a = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$a.hasOwnProperty;

/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$2,
      objProps = getAllKeys(object),
      objLength = objProps.length,
      othProps = getAllKeys(other),
      othLength = othProps.length;

  if (objLength != othLength && !isPartial) {
    return false;
  }
  var index = objLength;
  while (index--) {
    var key = objProps[index];
    if (!(isPartial ? key in other : hasOwnProperty$7.call(other, key))) {
      return false;
    }
  }
  // Assume cyclic values are equal.
  var stacked = stack.get(object);
  if (stacked && stack.get(other)) {
    return stacked == other;
  }
  var result = true;
  stack.set(object, other);
  stack.set(other, object);

  var skipCtor = isPartial;
  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
        othValue = other[key];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, objValue, key, other, object, stack)
        : customizer(objValue, othValue, key, object, other, stack);
    }
    // Recursively compare objects (susceptible to call stack limits).
    if (!(compared === undefined
          ? (objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack))
          : compared
        )) {
      result = false;
      break;
    }
    skipCtor || (skipCtor = key == 'constructor');
  }
  if (result && !skipCtor) {
    var objCtor = object.constructor,
        othCtor = other.constructor;

    // Non `Object` object instances with different constructors are not equal.
    if (objCtor != othCtor &&
        ('constructor' in object && 'constructor' in other) &&
        !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
          typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      result = false;
    }
  }
  stack['delete'](object);
  stack['delete'](other);
  return result;
}

/* Built-in method references that are verified to be native. */
var DataView = getNative(root, 'DataView');

/* Built-in method references that are verified to be native. */
var Promise = getNative(root, 'Promise');

/* Built-in method references that are verified to be native. */
var Set = getNative(root, 'Set');

/* Built-in method references that are verified to be native. */
var WeakMap = getNative(root, 'WeakMap');

/** `Object#toString` result references. */
var mapTag$2 = '[object Map]',
    objectTag$1 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag$2 = '[object Set]',
    weakMapTag$1 = '[object WeakMap]';

var dataViewTag$2 = '[object DataView]';

/** Used to detect maps, sets, and weakmaps. */
var dataViewCtorString = toSource(DataView),
    mapCtorString = toSource(Map),
    promiseCtorString = toSource(Promise),
    setCtorString = toSource(Set),
    weakMapCtorString = toSource(WeakMap);

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
var getTag = baseGetTag;

// Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
if ((DataView && getTag(new DataView(new ArrayBuffer(1))) != dataViewTag$2) ||
    (Map && getTag(new Map) != mapTag$2) ||
    (Promise && getTag(Promise.resolve()) != promiseTag) ||
    (Set && getTag(new Set) != setTag$2) ||
    (WeakMap && getTag(new WeakMap) != weakMapTag$1)) {
  getTag = function(value) {
    var result = baseGetTag(value),
        Ctor = result == objectTag$1 ? value.constructor : undefined,
        ctorString = Ctor ? toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString: return dataViewTag$2;
        case mapCtorString: return mapTag$2;
        case promiseCtorString: return promiseTag;
        case setCtorString: return setTag$2;
        case weakMapCtorString: return weakMapTag$1;
      }
    }
    return result;
  };
}

var getTag$1 = getTag;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$3 = 1;

/** `Object#toString` result references. */
var argsTag$2 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    objectTag$2 = '[object Object]';

/** Used for built-in method references. */
var objectProto$b = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$b.hasOwnProperty;

/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} [stack] Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
  var objIsArr = isArray(object),
      othIsArr = isArray(other),
      objTag = objIsArr ? arrayTag$1 : getTag$1(object),
      othTag = othIsArr ? arrayTag$1 : getTag$1(other);

  objTag = objTag == argsTag$2 ? objectTag$2 : objTag;
  othTag = othTag == argsTag$2 ? objectTag$2 : othTag;

  var objIsObj = objTag == objectTag$2,
      othIsObj = othTag == objectTag$2,
      isSameTag = objTag == othTag;

  if (isSameTag && isBuffer(object)) {
    if (!isBuffer(other)) {
      return false;
    }
    objIsArr = true;
    objIsObj = false;
  }
  if (isSameTag && !objIsObj) {
    stack || (stack = new Stack);
    return (objIsArr || isTypedArray(object))
      ? equalArrays(object, other, bitmask, customizer, equalFunc, stack)
      : equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
  }
  if (!(bitmask & COMPARE_PARTIAL_FLAG$3)) {
    var objIsWrapped = objIsObj && hasOwnProperty$8.call(object, '__wrapped__'),
        othIsWrapped = othIsObj && hasOwnProperty$8.call(other, '__wrapped__');

    if (objIsWrapped || othIsWrapped) {
      var objUnwrapped = objIsWrapped ? object.value() : object,
          othUnwrapped = othIsWrapped ? other.value() : other;

      stack || (stack = new Stack);
      return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
    }
  }
  if (!isSameTag) {
    return false;
  }
  stack || (stack = new Stack);
  return equalObjects(object, other, bitmask, customizer, equalFunc, stack);
}

/**
 * The base implementation of `_.isEqual` which supports partial comparisons
 * and tracks traversed objects.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Unordered comparison
 *  2 - Partial comparison
 * @param {Function} [customizer] The function to customize comparisons.
 * @param {Object} [stack] Tracks traversed `value` and `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */
function baseIsEqual(value, other, bitmask, customizer, stack) {
  if (value === other) {
    return true;
  }
  if (value == null || other == null || (!isObjectLike(value) && !isObjectLike(other))) {
    return value !== value && other !== other;
  }
  return baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$4 = 1,
    COMPARE_UNORDERED_FLAG$2 = 2;

/**
 * The base implementation of `_.isMatch` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to inspect.
 * @param {Object} source The object of property values to match.
 * @param {Array} matchData The property names, values, and compare flags to match.
 * @param {Function} [customizer] The function to customize comparisons.
 * @returns {boolean} Returns `true` if `object` is a match, else `false`.
 */
function baseIsMatch(object, source, matchData, customizer) {
  var index = matchData.length,
      length = index,
      noCustomizer = !customizer;

  if (object == null) {
    return !length;
  }
  object = Object(object);
  while (index--) {
    var data = matchData[index];
    if ((noCustomizer && data[2])
          ? data[1] !== object[data[0]]
          : !(data[0] in object)
        ) {
      return false;
    }
  }
  while (++index < length) {
    data = matchData[index];
    var key = data[0],
        objValue = object[key],
        srcValue = data[1];

    if (noCustomizer && data[2]) {
      if (objValue === undefined && !(key in object)) {
        return false;
      }
    } else {
      var stack = new Stack;
      if (customizer) {
        var result = customizer(objValue, srcValue, key, object, source, stack);
      }
      if (!(result === undefined
            ? baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG$4 | COMPARE_UNORDERED_FLAG$2, customizer, stack)
            : result
          )) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` if suitable for strict
 *  equality comparisons, else `false`.
 */
function isStrictComparable(value) {
  return value === value && !isObject(value);
}

/**
 * Gets the property names, values, and compare flags of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the match data of `object`.
 */
function getMatchData(object) {
  var result = keys(object),
      length = result.length;

  while (length--) {
    var key = result[length],
        value = object[key];

    result[length] = [key, value, isStrictComparable(value)];
  }
  return result;
}

/**
 * A specialized version of `matchesProperty` for source values suitable
 * for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function matchesStrictComparable(key, srcValue) {
  return function(object) {
    if (object == null) {
      return false;
    }
    return object[key] === srcValue &&
      (srcValue !== undefined || (key in Object(object)));
  };
}

/**
 * The base implementation of `_.matches` which doesn't clone `source`.
 *
 * @private
 * @param {Object} source The object of property values to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatches(source) {
  var matchData = getMatchData(source);
  if (matchData.length == 1 && matchData[0][2]) {
    return matchesStrictComparable(matchData[0][0], matchData[0][1]);
  }
  return function(object) {
    return object === source || baseIsMatch(object, source, matchData);
  };
}

/** `Object#toString` result references. */
var symbolTag$1 = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && baseGetTag(value) == symbolTag$1);
}

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (isArray(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize.Cache || MapCache);
  return memoized;
}

// Expose `MapCache`.
memoize.Cache = MapCache;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped(func) {
  var result = memoize(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });

  var cache = result.cache;
  return result;
}

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath = memoizeCapped(function(string) {
  var result = [];
  if (string.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto$1 = Symbol ? Symbol.prototype : undefined,
    symbolToString = symbolProto$1 ? symbolProto$1.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return arrayMap(value, baseToString) + '';
  }
  if (isSymbol(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
}

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : baseToString(value);
}

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath(value, object) {
  if (isArray(value)) {
    return value;
  }
  return isKey(value, object) ? [value] : stringToPath(toString(value));
}

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value == 'string' || isSymbol(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$1) ? '-0' : result;
}

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet(object, path) {
  path = castPath(path, object);

  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[toKey(path[index++])];
  }
  return (index && index == length) ? object : undefined;
}

/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get(object, path, defaultValue) {
  var result = object == null ? undefined : baseGet(object, path);
  return result === undefined ? defaultValue : result;
}

/**
 * The base implementation of `_.hasIn` without support for deep paths.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {Array|string} key The key to check.
 * @returns {boolean} Returns `true` if `key` exists, else `false`.
 */
function baseHasIn(object, key) {
  return object != null && key in Object(object);
}

/**
 * Checks if `path` exists on `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @param {Function} hasFunc The function to check properties.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 */
function hasPath(object, path, hasFunc) {
  path = castPath(path, object);

  var index = -1,
      length = path.length,
      result = false;

  while (++index < length) {
    var key = toKey(path[index]);
    if (!(result = object != null && hasFunc(object, key))) {
      break;
    }
    object = object[key];
  }
  if (result || ++index != length) {
    return result;
  }
  length = object == null ? 0 : object.length;
  return !!length && isLength(length) && isIndex(key, length) &&
    (isArray(object) || isArguments(object));
}

/**
 * Checks if `path` is a direct or inherited property of `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = _.create({ 'a': _.create({ 'b': 2 }) });
 *
 * _.hasIn(object, 'a');
 * // => true
 *
 * _.hasIn(object, 'a.b');
 * // => true
 *
 * _.hasIn(object, ['a', 'b']);
 * // => true
 *
 * _.hasIn(object, 'b');
 * // => false
 */
function hasIn(object, path) {
  return object != null && hasPath(object, path, baseHasIn);
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$5 = 1,
    COMPARE_UNORDERED_FLAG$3 = 2;

/**
 * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
 *
 * @private
 * @param {string} path The path of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatchesProperty(path, srcValue) {
  if (isKey(path) && isStrictComparable(srcValue)) {
    return matchesStrictComparable(toKey(path), srcValue);
  }
  return function(object) {
    var objValue = get(object, path);
    return (objValue === undefined && objValue === srcValue)
      ? hasIn(object, path)
      : baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG$5 | COMPARE_UNORDERED_FLAG$3);
  };
}

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */
function identity(value) {
  return value;
}

/**
 * The base implementation of `_.property` without support for deep paths.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function baseProperty(key) {
  return function(object) {
    return object == null ? undefined : object[key];
  };
}

/**
 * A specialized version of `baseProperty` which supports deep paths.
 *
 * @private
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyDeep(path) {
  return function(object) {
    return baseGet(object, path);
  };
}

/**
 * Creates a function that returns the value at `path` of a given object.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 * @example
 *
 * var objects = [
 *   { 'a': { 'b': 2 } },
 *   { 'a': { 'b': 1 } }
 * ];
 *
 * _.map(objects, _.property('a.b'));
 * // => [2, 1]
 *
 * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
 * // => [1, 2]
 */
function property(path) {
  return isKey(path) ? baseProperty(toKey(path)) : basePropertyDeep(path);
}

/**
 * The base implementation of `_.iteratee`.
 *
 * @private
 * @param {*} [value=_.identity] The value to convert to an iteratee.
 * @returns {Function} Returns the iteratee.
 */
function baseIteratee(value) {
  // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
  // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
  if (typeof value == 'function') {
    return value;
  }
  if (value == null) {
    return identity;
  }
  if (typeof value == 'object') {
    return isArray(value)
      ? baseMatchesProperty(value[0], value[1])
      : baseMatches(value);
  }
  return property(value);
}

/**
 * Creates a base function for methods like `_.forIn` and `_.forOwn`.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseFor(fromRight) {
  return function(object, iteratee, keysFunc) {
    var index = -1,
        iterable = Object(object),
        props = keysFunc(object),
        length = props.length;

    while (length--) {
      var key = props[fromRight ? length : ++index];
      if (iteratee(iterable[key], key, iterable) === false) {
        break;
      }
    }
    return object;
  };
}

/**
 * The base implementation of `baseForOwn` which iterates over `object`
 * properties returned by `keysFunc` and invokes `iteratee` for each property.
 * Iteratee functions may exit iteration early by explicitly returning `false`.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @returns {Object} Returns `object`.
 */
var baseFor = createBaseFor();

/**
 * The base implementation of `_.forOwn` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Object} Returns `object`.
 */
function baseForOwn(object, iteratee) {
  return object && baseFor(object, iteratee, keys);
}

/**
 * Creates a `baseEach` or `baseEachRight` function.
 *
 * @private
 * @param {Function} eachFunc The function to iterate over a collection.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseEach(eachFunc, fromRight) {
  return function(collection, iteratee) {
    if (collection == null) {
      return collection;
    }
    if (!isArrayLike(collection)) {
      return eachFunc(collection, iteratee);
    }
    var length = collection.length,
        index = fromRight ? length : -1,
        iterable = Object(collection);

    while ((fromRight ? index-- : ++index < length)) {
      if (iteratee(iterable[index], index, iterable) === false) {
        break;
      }
    }
    return collection;
  };
}

/**
 * The base implementation of `_.forEach` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array|Object} Returns `collection`.
 */
var baseEach = createBaseEach(baseForOwn);

/**
 * The base implementation of `_.map` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function baseMap(collection, iteratee) {
  var index = -1,
      result = isArrayLike(collection) ? Array(collection.length) : [];

  baseEach(collection, function(value, key, collection) {
    result[++index] = iteratee(value, key, collection);
  });
  return result;
}

/**
 * Creates an array of values by running each element in `collection` thru
 * `iteratee`. The iteratee is invoked with three arguments:
 * (value, index|key, collection).
 *
 * Many lodash methods are guarded to work as iteratees for methods like
 * `_.every`, `_.filter`, `_.map`, `_.mapValues`, `_.reject`, and `_.some`.
 *
 * The guarded methods are:
 * `ary`, `chunk`, `curry`, `curryRight`, `drop`, `dropRight`, `every`,
 * `fill`, `invert`, `parseInt`, `random`, `range`, `rangeRight`, `repeat`,
 * `sampleSize`, `slice`, `some`, `sortBy`, `split`, `take`, `takeRight`,
 * `template`, `trim`, `trimEnd`, `trimStart`, and `words`
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} [iteratee=_.identity] The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 * @example
 *
 * function square(n) {
 *   return n * n;
 * }
 *
 * _.map([4, 8], square);
 * // => [16, 64]
 *
 * _.map({ 'a': 4, 'b': 8 }, square);
 * // => [16, 64] (iteration order is not guaranteed)
 *
 * var users = [
 *   { 'user': 'barney' },
 *   { 'user': 'fred' }
 * ];
 *
 * // The `_.property` iteratee shorthand.
 * _.map(users, 'user');
 * // => ['barney', 'fred']
 */
function map(collection, iteratee) {
  var func = isArray(collection) ? arrayMap : baseMap;
  return func(collection, baseIteratee(iteratee, 3));
}

/**
 * A specialized version of `_.forEach` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns `array`.
 */
function arrayEach(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (iteratee(array[index], index, array) === false) {
      break;
    }
  }
  return array;
}

/**
 * Casts `value` to `identity` if it's not a function.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {Function} Returns cast function.
 */
function castFunction(value) {
  return typeof value == 'function' ? value : identity;
}

/**
 * Iterates over elements of `collection` and invokes `iteratee` for each element.
 * The iteratee is invoked with three arguments: (value, index|key, collection).
 * Iteratee functions may exit iteration early by explicitly returning `false`.
 *
 * **Note:** As with other "Collections" methods, objects with a "length"
 * property are iterated like arrays. To avoid this behavior use `_.forIn`
 * or `_.forOwn` for object iteration.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @alias each
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} [iteratee=_.identity] The function invoked per iteration.
 * @returns {Array|Object} Returns `collection`.
 * @see _.forEachRight
 * @example
 *
 * _.forEach([1, 2], function(value) {
 *   console.log(value);
 * });
 * // => Logs `1` then `2`.
 *
 * _.forEach({ 'a': 1, 'b': 2 }, function(value, key) {
 *   console.log(key);
 * });
 * // => Logs 'a' then 'b' (iteration order is not guaranteed).
 */
function forEach(collection, iteratee) {
  var func = isArray(collection) ? arrayEach : baseEach;
  return func(collection, castFunction(iteratee));
}

/**
 * Creates a `_.find` or `_.findLast` function.
 *
 * @private
 * @param {Function} findIndexFunc The function to find the collection index.
 * @returns {Function} Returns the new find function.
 */
function createFind(findIndexFunc) {
  return function(collection, predicate, fromIndex) {
    var iterable = Object(collection);
    if (!isArrayLike(collection)) {
      var iteratee = baseIteratee(predicate, 3);
      collection = keys(collection);
      predicate = function(key) { return iteratee(iterable[key], key, iterable); };
    }
    var index = findIndexFunc(collection, predicate, fromIndex);
    return index > -1 ? iterable[iteratee ? collection[index] : index] : undefined;
  };
}

/**
 * The base implementation of `_.findIndex` and `_.findLastIndex` without
 * support for iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} predicate The function invoked per iteration.
 * @param {number} fromIndex The index to search from.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseFindIndex(array, predicate, fromIndex, fromRight) {
  var length = array.length,
      index = fromIndex + (fromRight ? 1 : -1);

  while ((fromRight ? index-- : ++index < length)) {
    if (predicate(array[index], index, array)) {
      return index;
    }
  }
  return -1;
}

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to match leading and trailing whitespace. */
var reTrim = /^\s+|\s+$/g;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol(value)) {
    return NAN;
  }
  if (isObject(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = value.replace(reTrim, '');
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

/** Used as references for various `Number` constants. */
var INFINITY$2 = 1 / 0,
    MAX_INTEGER = 1.7976931348623157e+308;

/**
 * Converts `value` to a finite number.
 *
 * @static
 * @memberOf _
 * @since 4.12.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted number.
 * @example
 *
 * _.toFinite(3.2);
 * // => 3.2
 *
 * _.toFinite(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toFinite(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toFinite('3.2');
 * // => 3.2
 */
function toFinite(value) {
  if (!value) {
    return value === 0 ? value : 0;
  }
  value = toNumber(value);
  if (value === INFINITY$2 || value === -INFINITY$2) {
    var sign = (value < 0 ? -1 : 1);
    return sign * MAX_INTEGER;
  }
  return value === value ? value : 0;
}

/**
 * Converts `value` to an integer.
 *
 * **Note:** This method is loosely based on
 * [`ToInteger`](http://www.ecma-international.org/ecma-262/7.0/#sec-tointeger).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted integer.
 * @example
 *
 * _.toInteger(3.2);
 * // => 3
 *
 * _.toInteger(Number.MIN_VALUE);
 * // => 0
 *
 * _.toInteger(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toInteger('3.2');
 * // => 3
 */
function toInteger(value) {
  var result = toFinite(value),
      remainder = result % 1;

  return result === result ? (remainder ? result - remainder : result) : 0;
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max;

/**
 * This method is like `_.find` except that it returns the index of the first
 * element `predicate` returns truthy for instead of the element itself.
 *
 * @static
 * @memberOf _
 * @since 1.1.0
 * @category Array
 * @param {Array} array The array to inspect.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @param {number} [fromIndex=0] The index to search from.
 * @returns {number} Returns the index of the found element, else `-1`.
 * @example
 *
 * var users = [
 *   { 'user': 'barney',  'active': false },
 *   { 'user': 'fred',    'active': false },
 *   { 'user': 'pebbles', 'active': true }
 * ];
 *
 * _.findIndex(users, function(o) { return o.user == 'barney'; });
 * // => 0
 *
 * // The `_.matches` iteratee shorthand.
 * _.findIndex(users, { 'user': 'fred', 'active': false });
 * // => 1
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.findIndex(users, ['active', false]);
 * // => 0
 *
 * // The `_.property` iteratee shorthand.
 * _.findIndex(users, 'active');
 * // => 2
 */
function findIndex(array, predicate, fromIndex) {
  var length = array == null ? 0 : array.length;
  if (!length) {
    return -1;
  }
  var index = fromIndex == null ? 0 : toInteger(fromIndex);
  if (index < 0) {
    index = nativeMax(length + index, 0);
  }
  return baseFindIndex(array, baseIteratee(predicate, 3), index);
}

/**
 * Iterates over elements of `collection`, returning the first element
 * `predicate` returns truthy for. The predicate is invoked with three
 * arguments: (value, index|key, collection).
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to inspect.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @param {number} [fromIndex=0] The index to search from.
 * @returns {*} Returns the matched element, else `undefined`.
 * @example
 *
 * var users = [
 *   { 'user': 'barney',  'age': 36, 'active': true },
 *   { 'user': 'fred',    'age': 40, 'active': false },
 *   { 'user': 'pebbles', 'age': 1,  'active': true }
 * ];
 *
 * _.find(users, function(o) { return o.age < 40; });
 * // => object for 'barney'
 *
 * // The `_.matches` iteratee shorthand.
 * _.find(users, { 'age': 1, 'active': true });
 * // => object for 'pebbles'
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.find(users, ['active', false]);
 * // => object for 'fred'
 *
 * // The `_.property` iteratee shorthand.
 * _.find(users, 'active');
 * // => object for 'barney'
 */
var find = createFind(findIndex);

/**
 * The base implementation of `_.filter` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function baseFilter(collection, predicate) {
  var result = [];
  baseEach(collection, function(value, index, collection) {
    if (predicate(value, index, collection)) {
      result.push(value);
    }
  });
  return result;
}

/** Error message constants. */
var FUNC_ERROR_TEXT$1 = 'Expected a function';

/**
 * Creates a function that negates the result of the predicate `func`. The
 * `func` predicate is invoked with the `this` binding and arguments of the
 * created function.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Function
 * @param {Function} predicate The predicate to negate.
 * @returns {Function} Returns the new negated function.
 * @example
 *
 * function isEven(n) {
 *   return n % 2 == 0;
 * }
 *
 * _.filter([1, 2, 3, 4, 5, 6], _.negate(isEven));
 * // => [1, 3, 5]
 */
function negate(predicate) {
  if (typeof predicate != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT$1);
  }
  return function() {
    var args = arguments;
    switch (args.length) {
      case 0: return !predicate.call(this);
      case 1: return !predicate.call(this, args[0]);
      case 2: return !predicate.call(this, args[0], args[1]);
      case 3: return !predicate.call(this, args[0], args[1], args[2]);
    }
    return !predicate.apply(this, args);
  };
}

/**
 * The opposite of `_.filter`; this method returns the elements of `collection`
 * that `predicate` does **not** return truthy for.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 * @see _.filter
 * @example
 *
 * var users = [
 *   { 'user': 'barney', 'age': 36, 'active': false },
 *   { 'user': 'fred',   'age': 40, 'active': true }
 * ];
 *
 * _.reject(users, function(o) { return !o.active; });
 * // => objects for ['fred']
 *
 * // The `_.matches` iteratee shorthand.
 * _.reject(users, { 'age': 40, 'active': true });
 * // => objects for ['barney']
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.reject(users, ['active', false]);
 * // => objects for ['fred']
 *
 * // The `_.property` iteratee shorthand.
 * _.reject(users, 'active');
 * // => objects for ['barney']
 */
function reject(collection, predicate) {
  var func = isArray(collection) ? arrayFilter : baseFilter;
  return func(collection, negate(baseIteratee(predicate, 3)));
}

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */
function apply(func, thisArg, args) {
  switch (args.length) {
    case 0: return func.call(thisArg);
    case 1: return func.call(thisArg, args[0]);
    case 2: return func.call(thisArg, args[0], args[1]);
    case 3: return func.call(thisArg, args[0], args[1], args[2]);
  }
  return func.apply(thisArg, args);
}

/**
 * Gets the last element of `array`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to query.
 * @returns {*} Returns the last element of `array`.
 * @example
 *
 * _.last([1, 2, 3]);
 * // => 3
 */
function last(array) {
  var length = array == null ? 0 : array.length;
  return length ? array[length - 1] : undefined;
}

/**
 * The base implementation of `_.slice` without an iteratee call guard.
 *
 * @private
 * @param {Array} array The array to slice.
 * @param {number} [start=0] The start position.
 * @param {number} [end=array.length] The end position.
 * @returns {Array} Returns the slice of `array`.
 */
function baseSlice(array, start, end) {
  var index = -1,
      length = array.length;

  if (start < 0) {
    start = -start > length ? 0 : (length + start);
  }
  end = end > length ? length : end;
  if (end < 0) {
    end += length;
  }
  length = start > end ? 0 : ((end - start) >>> 0);
  start >>>= 0;

  var result = Array(length);
  while (++index < length) {
    result[index] = array[index + start];
  }
  return result;
}

/**
 * Gets the parent value at `path` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array} path The path to get the parent value of.
 * @returns {*} Returns the parent value.
 */
function parent(object, path) {
  return path.length < 2 ? object : baseGet(object, baseSlice(path, 0, -1));
}

/**
 * The base implementation of `_.invoke` without support for individual
 * method arguments.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the method to invoke.
 * @param {Array} args The arguments to invoke the method with.
 * @returns {*} Returns the result of the invoked method.
 */
function baseInvoke(object, path, args) {
  path = castPath(path, object);
  object = parent(object, path);
  var func = object == null ? object : object[toKey(last(path))];
  return func == null ? undefined : apply(func, object, args);
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$1 = Math.max;

/**
 * A specialized version of `baseRest` which transforms the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @param {Function} transform The rest array transform.
 * @returns {Function} Returns the new function.
 */
function overRest(func, start, transform) {
  start = nativeMax$1(start === undefined ? (func.length - 1) : start, 0);
  return function() {
    var args = arguments,
        index = -1,
        length = nativeMax$1(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }
    index = -1;
    var otherArgs = Array(start + 1);
    while (++index < start) {
      otherArgs[index] = args[index];
    }
    otherArgs[start] = transform(array);
    return apply(func, this, otherArgs);
  };
}

/**
 * Creates a function that returns `value`.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {*} value The value to return from the new function.
 * @returns {Function} Returns the new constant function.
 * @example
 *
 * var objects = _.times(2, _.constant({ 'a': 1 }));
 *
 * console.log(objects);
 * // => [{ 'a': 1 }, { 'a': 1 }]
 *
 * console.log(objects[0] === objects[1]);
 * // => true
 */
function constant(value) {
  return function() {
    return value;
  };
}

var defineProperty = (function() {
  try {
    var func = getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

/**
 * The base implementation of `setToString` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var baseSetToString = !defineProperty ? identity : function(func, string) {
  return defineProperty(func, 'toString', {
    'configurable': true,
    'enumerable': false,
    'value': constant(string),
    'writable': true
  });
};

/** Used to detect hot functions by number of calls within a span of milliseconds. */
var HOT_COUNT = 800,
    HOT_SPAN = 16;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeNow = Date.now;

/**
 * Creates a function that'll short out and invoke `identity` instead
 * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
 * milliseconds.
 *
 * @private
 * @param {Function} func The function to restrict.
 * @returns {Function} Returns the new shortable function.
 */
function shortOut(func) {
  var count = 0,
      lastCalled = 0;

  return function() {
    var stamp = nativeNow(),
        remaining = HOT_SPAN - (stamp - lastCalled);

    lastCalled = stamp;
    if (remaining > 0) {
      if (++count >= HOT_COUNT) {
        return arguments[0];
      }
    } else {
      count = 0;
    }
    return func.apply(undefined, arguments);
  };
}

/**
 * Sets the `toString` method of `func` to return `string`.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var setToString = shortOut(baseSetToString);

/**
 * The base implementation of `_.rest` which doesn't validate or coerce arguments.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @returns {Function} Returns the new function.
 */
function baseRest(func, start) {
  return setToString(overRest(func, start, identity), func + '');
}

/**
 * Invokes the method at `path` of each element in `collection`, returning
 * an array of the results of each invoked method. Any additional arguments
 * are provided to each invoked method. If `path` is a function, it's invoked
 * for, and `this` bound to, each element in `collection`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Array|Function|string} path The path of the method to invoke or
 *  the function invoked per iteration.
 * @param {...*} [args] The arguments to invoke each method with.
 * @returns {Array} Returns the array of results.
 * @example
 *
 * _.invokeMap([[5, 1, 7], [3, 2, 1]], 'sort');
 * // => [[1, 5, 7], [1, 2, 3]]
 *
 * _.invokeMap([123, 456], String.prototype.split, '');
 * // => [['1', '2', '3'], ['4', '5', '6']]
 */
var invokeMap = baseRest(function(collection, path, args) {
  var index = -1,
      isFunc = typeof path == 'function',
      result = isArrayLike(collection) ? Array(collection.length) : [];

  baseEach(collection, function(value) {
    result[++index] = isFunc ? apply(path, value, args) : baseInvoke(value, path, args);
  });
  return result;
});

/**
 * The base implementation of `_.unset`.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {Array|string} path The property path to unset.
 * @returns {boolean} Returns `true` if the property is deleted, else `false`.
 */
function baseUnset(object, path) {
  path = castPath(path, object);
  object = parent(object, path);
  return object == null || delete object[toKey(last(path))];
}

/** Used for built-in method references. */
var arrayProto$1 = Array.prototype;

/** Built-in value references. */
var splice$1 = arrayProto$1.splice;

/**
 * The base implementation of `_.pullAt` without support for individual
 * indexes or capturing the removed elements.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {number[]} indexes The indexes of elements to remove.
 * @returns {Array} Returns `array`.
 */
function basePullAt(array, indexes) {
  var length = array ? indexes.length : 0,
      lastIndex = length - 1;

  while (length--) {
    var index = indexes[length];
    if (length == lastIndex || index !== previous) {
      var previous = index;
      if (isIndex(index)) {
        splice$1.call(array, index, 1);
      } else {
        baseUnset(array, index);
      }
    }
  }
  return array;
}

/**
 * Removes all elements from `array` that `predicate` returns truthy for
 * and returns an array of the removed elements. The predicate is invoked
 * with three arguments: (value, index, array).
 *
 * **Note:** Unlike `_.filter`, this method mutates `array`. Use `_.pull`
 * to pull elements from an array by value.
 *
 * @static
 * @memberOf _
 * @since 2.0.0
 * @category Array
 * @param {Array} array The array to modify.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @returns {Array} Returns the new array of removed elements.
 * @example
 *
 * var array = [1, 2, 3, 4];
 * var evens = _.remove(array, function(n) {
 *   return n % 2 == 0;
 * });
 *
 * console.log(array);
 * // => [1, 3]
 *
 * console.log(evens);
 * // => [2, 4]
 */
function remove(array, predicate) {
  var result = [];
  if (!(array && array.length)) {
    return result;
  }
  var index = -1,
      indexes = [],
      length = array.length;

  predicate = baseIteratee(predicate, 3);
  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result.push(value);
      indexes.push(index);
    }
  }
  basePullAt(array, indexes);
  return result;
}

var wellKnownNamespaces = {
   x: 'adobe:ns:meta/',
   xml: 'http://www.w3.org/1999/xhtml',
   rdf: 'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
   dc: 'http://purl.org/dc/elements/1.1/',
   exif: 'http://ns.adobe.com/exif/1.0',
   stDim: 'http://ns.adobe.com/xap/1.0/sType/Dimensions#',
   stFnt: 'http://ns.adobe.com/xap/1.0/sType/Font#',
   tsc: 'http://www.techsmith.com/xmp/tsc/',
   tscDM: 'http://www.techsmith.com/xmp/tscDM/',
   tscHS: 'http://www.techsmith.com/xmp/tscHS/',
   tscIQ: 'http://www.techsmith.com/xmp/tscIQ/',
   xmp: 'http://ns.adobe.com/xap/1.0/',
   xmpDM: 'http://ns.adobe.com/xmp/1.0/DynamicMedia/',
   xmpG: 'http://ns.adobe.com/xap/1.0/g/',
   xmpMM: 'http://ns.adobe.com/xap/1.0/mm/'
};

/**
 *  Namespace resolver for xmp namespaces.
 *  @function namespaceResolver
 *  @param {String} prefix - XML namespace prefix
 *  @static
 *  @return {String} The namespace corresponding to the prefix or null if not found
 */
var namespaceResolver = function(prefix) {
   return wellKnownNamespaces[prefix] || null;
};

/** `Object#toString` result references. */
var stringTag$2 = '[object String]';

/**
 * Checks if `value` is classified as a `String` primitive or object.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a string, else `false`.
 * @example
 *
 * _.isString('abc');
 * // => true
 *
 * _.isString(1);
 * // => false
 */
function isString(value) {
  return typeof value == 'string' ||
    (!isArray(value) && isObjectLike(value) && baseGetTag(value) == stringTag$2);
}

/**
 * Checks if `value` is `null` or `undefined`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is nullish, else `false`.
 * @example
 *
 * _.isNil(null);
 * // => true
 *
 * _.isNil(void 0);
 * // => true
 *
 * _.isNil(NaN);
 * // => false
 */
function isNil(value) {
  return value == null;
}

var parseXml;

// Taken from http://stackoverflow.com/a/17604251
if (typeof window.DOMParser !== 'undefined') {
   var DOMParserProto = DOMParser.prototype;
   var realParseFromString = DOMParserProto.parseFromString;

   // Firefox/Opera/IE throw errors on unsupported types
   var canParseHtmlNatively;
   try {
      // WebKit returns null on unsupported types
      if ((new DOMParser()).parseFromString('', 'text/html')) {
         // text/html parsing is natively supported
         canParseHtmlNatively = true;
      }
   } catch (ex) {
      // nothing to do
   }

   if (!canParseHtmlNatively) {
      DOMParserProto.parseFromString = function(markup, type) {
         if (/^\s*text\/html\s*(?:;|$)/i.test(type)) {
            var doc = document.implementation.createHTMLDocument('');
            var docElement = doc.documentElement;
            var firstElement;

            docElement.innerHTML = markup;
            firstElement = docElement.firstElementChild;

            if (docElement.childElementCount === 1 &&
               firstElement.localName.toLowerCase() === 'html') {
               doc.replaceChild(firstElement, docElement);
            }

            return doc;
         }
         return realParseFromString.apply(this, arguments);
      };
   }

   parseXml = function(xmlStr, contentType) {
      contentType = contentType || 'text/xml';
      return (new window.DOMParser()).parseFromString(xmlStr, contentType);
   };
} else if (typeof window.ActiveXObject !== 'undefined' &&
   new window.ActiveXObject('Microsoft.XMLDOM')) {
   parseXml = function(xmlStr) {
      var xmlDoc = new window.ActiveXObject('Microsoft.XMLDOM');
      xmlDoc.async = 'false';
      xmlDoc.loadXML(xmlStr);
      return xmlDoc;
   };
} else {
   throw new Error('No XML parser found');
}

var xmlDocFragmentToString = function(xmlDoc) {
   var xmlSerializer = new XMLSerializer();
   return xmlSerializer.serializeToString(xmlDoc);
};

var xmlDocToString = function(xmlDoc) {
   return '<?xml version="1.0" encoding="UTF-8"?>\n' + this.xmlDocFragmentToString(xmlDoc);
};

var getElementText = function(element) {
   return element.textContent;
};

var hasAttribute = function(element, attributeName) {
   var attrValue = element.getAttribute(attributeName);
   return !isNil(attrValue);
};

var hasAttributeWithValue = function(element, attributeName, value) {
   var attrValue = element.getAttribute(attributeName);
   return attrValue === value;
};

var createDocument = function() {
   var xmlDocument = document.implementation.createDocument('', '', null);
   return xmlDocument;
};

var createElement = function(doc, name) {
   var element = doc.createElement(name);
   return element;
};

var createElementWithValue = function(doc, name, value) {
   var element = doc.createElement(name);
   element.appendChild(doc.createTextNode(value));
   return element;
};

var getElementsByTagName = function(element, name) {
   var ns = '*';
   var childElements = element.getElementsByTagNameNS(ns, name);
   return childElements;
};

var getFirstElementByTagName = function(element, name) {
   var childElements = getElementsByTagName(element, name);
   return childElements && childElements[0] ? childElements[0] : null;
};

var getFirstChildElementTextByTagName = function(element, name) {
   var childElement = getFirstElementByTagName(element, name);
   return childElement ? getElementText(childElement) : null;
};

var XmlUtility = function() {}; // eslint-disable-line no-empty-function
XmlUtility.prototype.parseXml = parseXml;
XmlUtility.prototype.xmlDocFragmentToString = xmlDocFragmentToString;
XmlUtility.prototype.xmlDocToString = xmlDocToString;
XmlUtility.prototype.getElementText = getElementText;
XmlUtility.prototype.hasAttribute = hasAttribute;
XmlUtility.prototype.hasAttributeWithValue = hasAttributeWithValue;
XmlUtility.prototype.createDocument = createDocument;
XmlUtility.prototype.createElement = createElement;
XmlUtility.prototype.createElementWithValue = createElementWithValue;
XmlUtility.prototype.getElementsByTagName = getElementsByTagName;
XmlUtility.prototype.getFirstElementByTagName = getFirstElementByTagName;
XmlUtility.prototype.getFirstChildElementTextByTagName = getFirstChildElementTextByTagName;

var isDocument = function(docOrElement) {
   return isFunction(docOrElement.evaluate);
};

var getFirstElementByTagNameNSPrefix = function(docOrElement, name) {
   if (isDocument(docOrElement)) {
      return docOrElement.evaluate('//' + name, docOrElement, this.namespaceResolver, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
   }
   var childElements = this.getElementsByTagNameNSPrefix(docOrElement, name);
   return childElements && childElements[0] ? childElements[0] : undefined;
};

var getElementsByTagNameNSPrefix = function(element, name) {
   if (!element) {
      return null;
   }

   if (name.indexOf(':') === -1) {
      throw new Error('getElementsByTagNameNSPrefix requires the name passed in to have a namespace prefix. getElementsByTagName can be used if you do not know the prefix.');
   }

   var tokens = name.split(':');
   var ns = this.namespaceResolver(tokens[0]);
   var tagName = name.split(':')[1];
   var childElements = element.getElementsByTagNameNS(ns, tagName);
   return childElements;
};

var getFirstChildElementTextByTagNameNSPrefix = function(element, name) {
   var childElement = this.getFirstElementByTagNameNSPrefix(element, name);
   return childElement ? this.getElementText(childElement) : undefined;
};

var createDocumentNS = function(ns, prefix, name) {
   var xmlDocument = document.implementation.createDocument(ns, prefix + ':' + name, null);
   return xmlDocument;
};

var createElementNS = function(doc, ns, prefix, name) {
   var element = doc.createElementNS(ns, prefix + ':' + name);
   return element;
};

var createElementNSWithValue = function(doc, ns, prefix, name, value) {
   var element = createElementNS(doc, ns, prefix, name);
   element.appendChild(doc.createTextNode(value));
   return element;
};

var getNSInfo = function(tagName, namespaceResolver) {
   var nameParts = tagName.split(':', 2);
   if (nameParts.length !== 2) {
      throw new Error('invalid tag name [' + tagName + '] should contain \':\' to define namespace');
   } else if (!namespaceResolver(nameParts[0])) {
      throw new Error('invalid namespace prefix [' + nameParts[0] + '] was not able to be mapped to known namespace');
   }

   return {
      nsUrl: namespaceResolver(nameParts[0]),
      nsPrefix: nameParts[0],
      tagShortName: nameParts[1]
   };
};

var createElementFromObject = function(tagInfo, doc) {
   doc = doc || this.createDocument();
   var that = this;

   if (isString(tagInfo)) {
      return createElement(doc, tagInfo);
   } else if (typeof tagInfo === 'object' && typeof tagInfo.toXmp === 'function') {
      return tagInfo.toXmp();
   } else if (typeof tagInfo === 'object' && tagInfo.name) {
      var nsInfo = getNSInfo(tagInfo.name, this.namespaceResolver);
      var newElement = createElementNS(doc, nsInfo.nsUrl, nsInfo.nsPrefix, nsInfo.tagShortName);
      if (tagInfo.atts && typeof tagInfo.atts === 'object') {
         forEach(tagInfo.atts, function(attrVal, attrName) {
            if (!isNil(attrVal)) {
               var attNsInfo = getNSInfo(attrName, that.namespaceResolver);
               if (attNsInfo.nsPrefix !== 'xml') {
                  newElement.setAttributeNS(attNsInfo.nsUrl, attrName, attrVal);
               } else {
                  newElement.setAttribute(attrName, attrVal);
               }
            }
         });
      }
      if (tagInfo.children && Array.isArray(tagInfo.children)) {
         forEach(tagInfo.children, function(child) {
            if (child) {
               newElement.appendChild(that.createElementFromObject(child, doc));
            }
         });
      }
      if (tagInfo.text !== undefined && tagInfo.text !== null) {
         newElement.textContent = tagInfo.text;
      }
      return newElement;
   }
   throw new Error('invalid tag info');
};

var NamespaceXmlUtility = function(namespaceResolver) {
   if (!namespaceResolver) {
      throw new Error('namespaceResolver is required');
   }
   this.namespaceResolver = namespaceResolver;
};

NamespaceXmlUtility.prototype = Object.create(XmlUtility.prototype);
NamespaceXmlUtility.prototype.getFirstElementByTagNameNSPrefix = getFirstElementByTagNameNSPrefix;
NamespaceXmlUtility.prototype.getElementsByTagNameNSPrefix = getElementsByTagNameNSPrefix;
NamespaceXmlUtility.prototype.getFirstChildElementTextByTagNameNSPrefix = getFirstChildElementTextByTagNameNSPrefix;
NamespaceXmlUtility.prototype.createDocumentNS = createDocumentNS;
NamespaceXmlUtility.prototype.createElementFromObject = createElementFromObject;
NamespaceXmlUtility.prototype.createElementNS = createElementNS;
NamespaceXmlUtility.prototype.createElementNSWithValue = createElementNSWithValue;

var LanguageIdentifier = function(languageIdentifier) {
   if (typeof languageIdentifier !== 'string') {
      throw new Error('Language Identifier is expecting a string, but got (' + languageIdentifier + ')');
   }

   var _identifier = languageIdentifier.toLowerCase();
   var _language = '';
   var _region = '';

   var parseLanguageIdentifier = function() {
      var languageIdentifierParts = _identifier.split('-');
      if (languageIdentifierParts.length > 1) {
         _region = languageIdentifierParts.pop();
      }
      _language = languageIdentifierParts.join('-');
   };

   var toPOJO = function() {
      return {
         identifier: _identifier,
         language: _language,
         region: _region
      };
   };

   parseLanguageIdentifier();

   return Object.defineProperties(
      {
         toPOJO: toPOJO
      },
      {
         identifier: {
            get: function() {
               return _identifier;
            }
         },
         language: {
            get: function() {
               return _language;
            }
         },
         region: {
            get: function() {
               return _region;
            }
         }
      });
};

/** @module LanguageIdentifier */
var LanguageIdentifier$1 = {
   /**
    *  Factory method that returns a new LanguageIdentifier object.
    *  @function create
    *  @param {string} languageIdentifier - RFC 3066 notation language string
    *  @static
    *  @return {module:LanguageIdentifier} new LanguageIdentifier instance
    */
   create: LanguageIdentifier
};

var _identifiers = Object.freeze({
   default: 'default',
   af: {
      name: 'Afrikaans',
      displayName: 'Afrikaans',
      regions: {
         za: 'South Africa'
      }
   },
   sq: {
      name: 'Albanian',
      displayName: 'Shqip',
      regions: {
         al: 'Albania'
      }
   },
   ar: {
      name: 'Arabic',
      displayName: 'العربية',
      regions: {
         dz: 'Algeria',
         bh: 'Bahrain',
         eg: 'Egypt',
         iq: 'Iraq',
         jo: 'Jordan',
         kw: 'Kuwait',
         lb: 'Lebanon',
         ly: 'Libya',
         ma: 'Morocco',
         om: 'Oman',
         qa: 'Qatar',
         sa: 'Saudi Arabia',
         sy: 'Syria',
         tn: 'Tunisia',
         ae: 'United Arab Emirates',
         ye: 'Yemen'
      }
   },
   hy: {
      name: 'Armenian',
      displayName: 'Արևելահայերեն',
      regions: {
         am: 'Armenia'
      }
   },
   'cy-az': {
      name: 'Azeri (Cyrillic)',
      displayName: 'Азәрбајҹан дили',
      regions: {
         az: 'Azerbaijan'
      }
   },
   'lt-az': {
      name: 'Azeri (Latin)',
      displayName: 'Azərbaycanca',
      regions: {
         az: 'Azerbaijan'
      }
   },
   eu: {
      name: 'Basque',
      displayName: 'Euskara',
      regions: {
         es: 'Basque'
      }
   },
   be: {
      name: 'Belarusian',
      displayName: 'Беларуская',
      regions: {
         by: 'Belarus'
      }
   },
   bg: {
      name: 'Bulgarian',
      displayName: 'Български',
      regions: {
         bg: 'Bulgaria'
      }
   },
   ca: {
      name: 'Catalan',
      displayName: 'Català',
      regions: {
         es: 'Catalan'
      }
   },
   zh: {
      name: 'Chinese',
      displayName: '简体中文',
      regions: {
         cn: 'China',
         hk: 'Hong Kong SAR',
         mo: 'Macau SAR',
         sg: 'Singapore',
         tw: 'Taiwan',
         chs: '(Simplified)',
         cht: '(Traditional)'
      }
   },
   hr: {
      name: 'Croatian',
      displayName: 'hrvatski',
      regions: {
         hr: 'Croatia'
      }
   },
   cs: {
      name: 'Czech',
      displayName: 'Čeština',
      regions: {
         cz: 'Czech Republic'
      }
   },
   da: {
      name: 'Danish',
      displayName: 'Dansk',
      regions: {
         dk: 'Denmark'
      }
   },
   div: {
      name: 'Dhivehi',
      displayName: 'ދިވެހި',
      regions: {
         mv: 'Maldives'
      }
   },
   nl: {
      name: 'Dutch',
      displayName: 'Nederlands',
      regions: {
         be: 'Belgium',
         nl: 'The Netherlands'
      }
   },
   en: {
      name: 'English',
      displayName: 'English',
      regions: {
         au: 'Australia',
         bz: 'Belize',
         ca: 'Canada',
         cb: 'Caribbean',
         ie: 'Ireland',
         jm: 'Jamaica',
         nz: 'New Zealand',
         ph: 'Philippines',
         za: 'South Africa',
         tt: 'Trinidad and Tobago',
         gb: 'United Kingdom',
         us: 'United States',
         zw: 'Zimbabwe'
      }
   },
   et: {
      name: 'Estonian',
      displayName: 'Eesti',
      regions: {
         ee: 'Estonia'
      }
   },
   fo: {
      name: 'Faroese',
      displayName: 'Føroyskt',
      regions: {
         fo: 'Faroe Islands'
      }
   },
   fa: {
      name: 'Farsi',
      displayName: 'فارسی',
      regions: {
         ir: 'Iran'
      }
   },
   fi: {
      name: 'Finnish',
      displayName: 'Suomi',
      regions: {
         fi: 'Finland'
      }
   },
   fr: {
      name: 'French',
      displayName: 'Français',
      regions: {
         be: 'Belgium',
         ca: 'Canada',
         fr: 'France',
         lu: 'Luxembourg',
         mc: 'Monaco',
         ch: 'Switzerland'
      }
   },
   gl: {
      name: 'Galician',
      displayName: 'galego',
      regions: {
         es: 'Galician'
      }
   },
   ka: {
      name: 'Georgian',
      displayName: 'ქართული',
      regions: {
         ge: 'Georgia'
      }
   },
   de: {
      name: 'German',
      displayName: 'Deutsch',
      regions: {
         at: 'Austria',
         de: 'Germany',
         li: 'Liechtenstein',
         lu: 'Luxembourg',
         ch: 'Switzerland'
      }
   },
   el: {
      name: 'Greek',
      displayName: 'Ελληνικά',
      regions: {
         gr: 'Greece'
      }
   },
   gu: {
      name: 'Gujarati',
      displayName: 'ગુજરાતી',
      regions: {
         in: 'India'
      }
   },
   he: {
      name: 'Hebrew',
      displayName: 'עברית',
      regions: {
         il: 'Israel'
      }
   },
   hi: {
      name: 'Hindi',
      displayName: 'हिन्दी',
      regions: {
         in: 'India'
      }
   },
   hu: {
      name: 'Hungarian',
      displayName: 'Magyar',
      regions: {
         hu: 'Hungary'
      }
   },
   is: {
      name: 'Icelandic',
      displayName: 'Íslenska',
      regions: {
         is: 'Iceland'
      }
   },
   id: {
      name: 'Indonesian',
      displayName: 'Bahasa Indonesia',
      regions: {
         id: 'Indonesia'
      }
   },
   it: {
      name: 'Italian',
      displayName: 'Italiano',
      regions: {
         it: 'Italy',
         ch: 'Switzerland'
      }
   },
   ja: {
      name: 'Japanese',
      displayName: '日本語',
      regions: {
         jp: 'Japan'
      }
   },
   kn: {
      name: 'Kannada',
      displayName: 'ಕನ್ನಡ',
      regions: {
         in: 'India'
      }
   },
   kk: {
      name: 'Kazakh',
      displayName: 'Қазақ Tілі',
      regions: {
         kz: 'Kazakhstan'
      }
   },
   kok: {
      name: 'Konkani',
      displayName: 'कोंकणी',
      regions: {
         in: 'India'
      }
   },
   ko: {
      name: 'Korean',
      displayName: '한국어',
      regions: {
         kr: 'Korea'
      }
   },
   ky: {
      name: 'Kyrgyz',
      displayName: 'кыргызча',
      regions: {
         kz: 'Kazakhstan'
      }
   },
   lv: {
      name: 'Latvian',
      displayName: 'Latviešu',
      regions: {
         lv: 'Latvia'
      }
   },
   lt: {
      name: 'Lithuanian',
      displayName: 'Lietuvių',
      regions: {
         lt: 'Lithuania'
      }
   },
   mk: {
      name: 'Macedonian',
      displayName: 'Mакедонски',
      regions: {
         mk: '(FYROM)'
      }
   },
   ms: {
      name: 'Malay',
      displayName: 'Bahasa Melayu',
      regions: {
         bn: 'Brunei',
         my: 'Malaysia'
      }
   },
   mr: {
      name: 'Marathi',
      displayName: 'मराठी',
      regions: {
         in: 'India'
      }
   },
   mn: {
      name: 'Mongolian',
      displayName: 'Монгол Хэл',
      regions: {
         mn: 'Mongolia'
      }
   },
   nb: {
      name: 'Norwegian (Bokmål)',
      displayName: 'Norsk (Bokmål)',
      regions: {
         no: 'Norway'
      }
   },
   nn: {
      name: 'Norwegian (Nynorsk)',
      displayName: 'Norsk (Nynorsk)',
      regions: {
         no: 'Norway'
      }
   },
   pl: {
      name: 'Polish',
      displayName: 'Polsk',
      regions: {
         pl: 'Poland'
      }
   },
   pt: {
      name: 'Portuguese',
      displayName: 'Português',
      regions: {
         br: 'Brazil',
         pt: 'Portugal'
      }
   },
   pa: {
      name: 'Punjabi',
      displayName: 'ਪੰਜਾਬੀ',
      regions: {
         in: 'India'
      }
   },
   ro: {
      name: 'Romanian',
      displayName: 'Română',
      regions: {
         ro: 'Romania'
      }
   },
   ru: {
      name: 'Russian',
      displayName: 'Русский',
      regions: {
         ru: 'Russia'
      }
   },
   sa: {
      name: 'Sanskrit',
      displayName: 'संस्कृतम्',
      regions: {
         in: 'India'
      }
   },
   'cy-sr': {
      name: 'Serbian (Cyrillic)',
      displayName: 'Српски',
      regions: {
         sp: 'Serbia'
      }
   },
   'lt-sr': {
      name: 'Serbian (Latin)',
      displayName: 'Srpski',
      regions: {
         sp: 'Serbia'
      }
   },
   sk: {
      name: 'Slovak',
      displayName: 'Slovenčina',
      regions: {
         sk: 'Slovakia'
      }
   },
   sl: {
      name: 'Slovenian',
      displayName: 'Slovenščina',
      regions: {
         si: 'Slovenia'
      }
   },
   es: {
      name: 'Spanish',
      displayName: 'Español',
      regions: {
         ar: 'Argentina',
         bo: 'Bolivia',
         cl: 'Chile',
         co: 'Colombia',
         cr: 'Costa Rica',
         do: 'Dominican Republic',
         ec: 'Ecuador',
         sv: 'El Salvador',
         gt: 'Guatemala',
         hn: 'Honduras',
         mx: 'Mexico',
         ni: 'Nicaragua',
         pa: 'Panama',
         py: 'Paraguay',
         pe: 'Peru',
         pr: 'Puerto Rico',
         es: 'Spain',
         uy: 'Uruguay',
         ve: 'Venezuela'
      }
   },
   sw: {
      name: 'Swahili',
      displayName: 'Kiswahili',
      regions: {
         ke: 'Kenya'
      }
   },
   sv: {
      name: 'Swedish',
      displayName: 'Svenska',
      regions: {
         fi: 'Finland',
         se: 'Sweden'
      }
   },
   syr: {
      name: 'Syriac',
      displayName: 'ܠܫܢܐ ܣܘܪܝܝܐ‎',
      regions: {
         sy: 'Syria'
      }
   },
   ta: {
      name: 'Tamil',
      displayName: 'தமிழ்‎',
      regions: {
         in: 'India'
      }
   },
   tt: {
      name: 'Tatar',
      displayName: 'татар теле',
      regions: {
         ru: 'Russia'
      }
   },
   te: {
      name: 'Telugu',
      displayName: 'తెలుగు',
      regions: {
         in: 'India'
      }
   },
   th: {
      name: 'Thai',
      displayName: 'ไทย',
      regions: {
         th: 'Thailand'
      }
   },
   tr: {
      name: 'Turkish',
      displayName: 'Türkçe',
      regions: {
         tr: 'Turkey'
      }
   },
   uk: {
      name: 'Ukrainian',
      displayName: 'Українська',
      regions: {
         ua: 'Ukraine'
      }
   },
   ur: {
      name: 'Urdu',
      displayName: 'اُردُو',
      regions: {
         pk: 'Pakistan'
      }
   },
   'cy-uz': {
      name: 'Uzbek (Cyrillic)',
      displayName: 'Ўзбек',
      regions: {
         uz: 'Uzbekistan'
      }
   },
   'lt-uz': {
      name: 'Uzbek (Latin)',
      displayName: 'oʻzbek tili',
      regions: {
         uz: 'Uzbekistan'
      }
   },
   vi: {
      name: 'Vietnamese',
      displayName: 'Tiếng Việt',
      regions: {
         vn: 'Vietnam'
      }
   }
});

/** @module LanguageIdentifiers */

/**
 *  Check if a language is supported.
 *  @function isSupportedLanguage
 *  @param {string} languageIdentifierString - RFC 3066 notation language string
 *  @static
 *  @return {boolean} is the language found in the languageIdentifier is found.
 */
var isSupportedLanguage = function(languageIdentifierString) {
   var identifier = LanguageIdentifier$1.create(languageIdentifierString);
   return _identifiers[identifier.language] !== undefined;
};

var languageIdentifiers = Object.defineProperties({
   isSupportedLanguage: isSupportedLanguage
}, {
   identifiers: {
      get: function() {
         return _identifiers;
      }
   }
});

/**
 * @this {Promise}
 */
function finallyConstructor(callback) {
  var constructor = this.constructor;
  return this.then(
    function(value) {
      return constructor.resolve(callback()).then(function() {
        return value;
      });
    },
    function(reason) {
      return constructor.resolve(callback()).then(function() {
        return constructor.reject(reason);
      });
    }
  );
}

// Store setTimeout reference so promise-polyfill will be unaffected by
// other code modifying setTimeout (like sinon.useFakeTimers())
var setTimeoutFunc = setTimeout;

function noop() {}

// Polyfill for Function.prototype.bind
function bind(fn, thisArg) {
  return function() {
    fn.apply(thisArg, arguments);
  };
}

/**
 * @constructor
 * @param {Function} fn
 */
function Promise$1(fn) {
  if (!(this instanceof Promise$1))
    throw new TypeError('Promises must be constructed via new');
  if (typeof fn !== 'function') throw new TypeError('not a function');
  /** @type {!number} */
  this._state = 0;
  /** @type {!boolean} */
  this._handled = false;
  /** @type {Promise|undefined} */
  this._value = undefined;
  /** @type {!Array<!Function>} */
  this._deferreds = [];

  doResolve(fn, this);
}

function handle(self, deferred) {
  while (self._state === 3) {
    self = self._value;
  }
  if (self._state === 0) {
    self._deferreds.push(deferred);
    return;
  }
  self._handled = true;
  Promise$1._immediateFn(function() {
    var cb = self._state === 1 ? deferred.onFulfilled : deferred.onRejected;
    if (cb === null) {
      (self._state === 1 ? resolve : reject$1)(deferred.promise, self._value);
      return;
    }
    var ret;
    try {
      ret = cb(self._value);
    } catch (e) {
      reject$1(deferred.promise, e);
      return;
    }
    resolve(deferred.promise, ret);
  });
}

function resolve(self, newValue) {
  try {
    // Promise Resolution Procedure: https://github.com/promises-aplus/promises-spec#the-promise-resolution-procedure
    if (newValue === self)
      throw new TypeError('A promise cannot be resolved with itself.');
    if (
      newValue &&
      (typeof newValue === 'object' || typeof newValue === 'function')
    ) {
      var then = newValue.then;
      if (newValue instanceof Promise$1) {
        self._state = 3;
        self._value = newValue;
        finale(self);
        return;
      } else if (typeof then === 'function') {
        doResolve(bind(then, newValue), self);
        return;
      }
    }
    self._state = 1;
    self._value = newValue;
    finale(self);
  } catch (e) {
    reject$1(self, e);
  }
}

function reject$1(self, newValue) {
  self._state = 2;
  self._value = newValue;
  finale(self);
}

function finale(self) {
  if (self._state === 2 && self._deferreds.length === 0) {
    Promise$1._immediateFn(function() {
      if (!self._handled) {
        Promise$1._unhandledRejectionFn(self._value);
      }
    });
  }

  for (var i = 0, len = self._deferreds.length; i < len; i++) {
    handle(self, self._deferreds[i]);
  }
  self._deferreds = null;
}

/**
 * @constructor
 */
function Handler(onFulfilled, onRejected, promise) {
  this.onFulfilled = typeof onFulfilled === 'function' ? onFulfilled : null;
  this.onRejected = typeof onRejected === 'function' ? onRejected : null;
  this.promise = promise;
}

/**
 * Take a potentially misbehaving resolver function and make sure
 * onFulfilled and onRejected are only called once.
 *
 * Makes no guarantees about asynchrony.
 */
function doResolve(fn, self) {
  var done = false;
  try {
    fn(
      function(value) {
        if (done) return;
        done = true;
        resolve(self, value);
      },
      function(reason) {
        if (done) return;
        done = true;
        reject$1(self, reason);
      }
    );
  } catch (ex) {
    if (done) return;
    done = true;
    reject$1(self, ex);
  }
}

Promise$1.prototype['catch'] = function(onRejected) {
  return this.then(null, onRejected);
};

Promise$1.prototype.then = function(onFulfilled, onRejected) {
  // @ts-ignore
  var prom = new this.constructor(noop);

  handle(this, new Handler(onFulfilled, onRejected, prom));
  return prom;
};

Promise$1.prototype['finally'] = finallyConstructor;

Promise$1.all = function(arr) {
  return new Promise$1(function(resolve, reject) {
    if (!arr || typeof arr.length === 'undefined')
      throw new TypeError('Promise.all accepts an array');
    var args = Array.prototype.slice.call(arr);
    if (args.length === 0) return resolve([]);
    var remaining = args.length;

    function res(i, val) {
      try {
        if (val && (typeof val === 'object' || typeof val === 'function')) {
          var then = val.then;
          if (typeof then === 'function') {
            then.call(
              val,
              function(val) {
                res(i, val);
              },
              reject
            );
            return;
          }
        }
        args[i] = val;
        if (--remaining === 0) {
          resolve(args);
        }
      } catch (ex) {
        reject(ex);
      }
    }

    for (var i = 0; i < args.length; i++) {
      res(i, args[i]);
    }
  });
};

Promise$1.resolve = function(value) {
  if (value && typeof value === 'object' && value.constructor === Promise$1) {
    return value;
  }

  return new Promise$1(function(resolve) {
    resolve(value);
  });
};

Promise$1.reject = function(value) {
  return new Promise$1(function(resolve, reject) {
    reject(value);
  });
};

Promise$1.race = function(values) {
  return new Promise$1(function(resolve, reject) {
    for (var i = 0, len = values.length; i < len; i++) {
      values[i].then(resolve, reject);
    }
  });
};

// Use polyfill for setImmediate for performance gains
Promise$1._immediateFn =
  (typeof setImmediate === 'function' &&
    function(fn) {
      setImmediate(fn);
    }) ||
  function(fn) {
    setTimeoutFunc(fn, 0);
  };

Promise$1._unhandledRejectionFn = function _unhandledRejectionFn(err) {
  if (typeof console !== 'undefined' && console) {
    console.warn('Possible Unhandled Promise Rejection:', err); // eslint-disable-line no-console
  }
};

var UndoAction = function(undoFunc, undoParams, redoFunc, redoParams) {
   var execute = function() {
      return new Promise$1(function(resolve, reject) {
         try {
            undoFunc(undoParams);
         } catch (error) {
            reject(error);
         }

         var redo = UndoAction(redoFunc, redoParams, undoFunc, undoParams);
         resolve(redo);
      });
   };

   return {
      execute: execute
   };
};

var NoOpAction = function() {
   return {
      execute: function() {
         return Promise$1.resolve();
      }
   };
};

/** @module UndoAction */
var UndoAction$1 = {
   /**
    *  Factory method that returns a new UndoAction object.
    *  @function create
    *  @param {Function} undoFunc - The function that will be used to undo the action.
    *  @param {Object} undoParams - Parameters that will be passed to undo function.
    *  @param {Function} redoFunc - The function that will be use to redo the action.
    *  @param {Object} redoParams - Parameters that will be passed to redo function.
    *  @static
    *  @return {module:UndoAction} new Command instance
    */
   create: function(undoFunc, undoParams, redoFunc, redoParams) {
      return UndoAction(undoFunc, undoParams, redoFunc, redoParams);
   },
   noOp: function() {
      return NoOpAction();
   }
};

var ImageRectangle = function(propertyInfo) {
   var _x;
   var _y;
   var _width;
   var _height;

   if (isString(propertyInfo)) {
      var tokens = propertyInfo.split(',');
      _x = Number(tokens[0]);
      _y = Number(tokens[1]);
      _width = Number(tokens[2]);
      _height = Number(tokens[3]);
   } else if (isObject(propertyInfo)) {
      _x = propertyInfo.x;
      _y = propertyInfo.y;
      _width = propertyInfo.width;
      _height = propertyInfo.height;
   } else {
      throw new Error('ImageRectangle is expecting a rectangle property string format: "x, y, width, height"');
   }

   var _public = Object.defineProperties({},
      {
         x: {
            get: function() {
               return _x;
            }
         },
         y: {
            get: function() {
               return _y;
            }
         },
         width: {
            get: function() {
               return _width;
            }
         },
         height: {
            get: function() {
               return _height;
            }
         }
      });

   _public.toPOJO = function() {
      return {
         x: _x,
         y: _y,
         width: _width,
         height: _height
      };
   };

   _public.toXmpAtt = function() {
      return [_x, _y, _width, _height].join(', ');
   };

   return _public;
};

/** @module ImageRectangle */
var ImageRectangle$1 = {
   /**
    *  Factory method that returns a new ImageRectangle object.
    *  @function create
    *  @param {String|Object} propertyInfo - Required property string of the rectangle. A Rectangle object
    *  is an area defined by its position, as indicated by its top-left corner point (x, y) and by its width
    *  and its height. The properties should be comma separated in the following format "x, y, width, height".  Alternatively,
    *  this can be an object containing x,y,width,height properties (i.e. the object that comes out from .toPOJO())
    *  @static
    *  @return {module:ImageRectangle} new ImageRectangle instance
    */
   create: function(propertyInfo) {
      return ImageRectangle(propertyInfo);
   }
};

var TAG_BEGIN_NOTATION = '\\';
var TAG_CLOSE_NOTATION = '0';
var TAG_END_NOTATION = ' ';
var TAG_SPACE = ' ';
var LOWER_CASE_A_CHAR_CODE = 97;
var LOWER_CASE_Z_CHAR_CODE = 122;

var isBlacklistedTag = function(strTag) {
   var LINE_BREAK = 'par ';
   return strTag.toLowerCase() === LINE_BREAK;
};

var findNextTag = function(strTextToSearch, nStartPoint) {
   if (nStartPoint === undefined) {
      nStartPoint = 0;
   }

   var nPosition = -1;
   // Make sure we're not null
   if (strTextToSearch) {
      // Get the first tag-looking sequence
      nPosition = strTextToSearch.indexOf(TAG_BEGIN_NOTATION, nStartPoint);
      // While we've got a tag-looking sequence....
      while (nPosition !== -1) {
         // As long as this point is not the last character in the string
         if (nPosition + 1 < strTextToSearch.length) {
            var nCode = strTextToSearch.toLowerCase().charCodeAt(nPosition + 1);
            // If the lower-case character is a lower-case alpha character
            if (LOWER_CASE_A_CHAR_CODE <= nCode && nCode <= LOWER_CASE_Z_CHAR_CODE) {
               break;
            }
         }
         // Get the next location of a tag-looking sequence
         nPosition = strTextToSearch.indexOf(TAG_BEGIN_NOTATION, nPosition + 1);
      }
   }

   // Return -1 of the position of the first valid tag
   return nPosition;
};

var getTagAt = function(strText, nTagPosition) {
   var strTag;
   if (nTagPosition !== -1) {
      var nEnd = strText.indexOf(TAG_END_NOTATION, nTagPosition + 1);
      if (nEnd !== -1) {
         strTag = strText.substring(nTagPosition + 1, nEnd + 1);
      } else {
         strTag = strText.substring(nTagPosition + 1);
      }
   }
   return strTag;
};

var findNextTagNotBlacklisted = function(strTextToSearch, nStartPoint) {
   if (nStartPoint === undefined) {
      nStartPoint = 0;
   }

   var nPosition = findNextTag(strTextToSearch, nStartPoint);
   while (nPosition !== -1) {
      var strTag = getTagAt(strTextToSearch, nPosition);

      if (strTag && !isBlacklistedTag(strTag)) {
         break;
      }

      nPosition = findNextTag(strTextToSearch, nPosition + 1);
   }

   return nPosition;
};

var getTextToCloseTag = function(strTag, arrStack) {
   var strCloseTags = '';
   var arrOpenTags = [];
   var strCloseTagNeeded = arrStack.pop();

   while (strCloseTagNeeded && strCloseTagNeeded !== strTag && arrStack.length) {
      strCloseTags += TAG_BEGIN_NOTATION + strCloseTagNeeded;

      arrOpenTags.push(matchingTag(strCloseTagNeeded));

      if (arrStack.length) {
         strCloseTagNeeded = arrStack.pop();
      }
   }

   strCloseTags += TAG_BEGIN_NOTATION + strTag;

   while (arrOpenTags.length) {
      var strOpenTagNeeded = arrOpenTags.pop();
      if (strOpenTagNeeded) {
         strCloseTags += TAG_BEGIN_NOTATION + strOpenTagNeeded;
         arrStack.push(matchingTag(strOpenTagNeeded));
      }
   }

   return strCloseTags;
};

var isClosingTag = function(strTag) {
   return strTag.indexOf(TAG_CLOSE_NOTATION) !== -1;
};

var matchingTag = function(strToMatch) {
   var strMatch = '';

   if (strToMatch !== '') {
      // An end tag
      if (isClosingTag(strToMatch)) {
         strMatch = strToMatch.substring(0, strToMatch.indexOf(TAG_CLOSE_NOTATION));
      } else {
         // Not an end tag
         // If we need to remove space (and we should)
         if (strToMatch.indexOf(TAG_SPACE) !== -1) {
            strMatch = strToMatch.substring(0, strToMatch.indexOf(TAG_SPACE));
         }
         strMatch += TAG_CLOSE_NOTATION;
      }
      strMatch += TAG_SPACE;
   }

   return strMatch;
};

var removeTag = function(strToFix, nTagPosition) {
   if (nTagPosition !== -1 && strToFix && nTagPosition < strToFix.length) {
      // Get the end of the tag
      var nTagEnd = strToFix.indexOf(TAG_END_NOTATION, nTagPosition);
      // If end exists
      if (nTagEnd !== -1) {
         // Remove it
         strToFix = strToFix.substring(0, nTagPosition) + strToFix.substring(nTagEnd + 1);
      } else {
         strToFix = strToFix.substring(0, nTagPosition);
      }
   }
   return strToFix;
};

var trimString = function(strToFix) {
   while (strToFix.length > 1 && strToFix.charAt(0) === TAG_SPACE) {
      strToFix = strToFix.substring(1);
   }

   while (strToFix.length > 1 && strToFix.charAt(strToFix.length - 1) === TAG_SPACE) {
      strToFix = strToFix.substring(0, strToFix.length - 1);
   }

   if (strToFix === TAG_SPACE) {
      strToFix = '';
   }

   return strToFix;
};

var nestTags = function(strToFix) {
   if (strToFix) {
      var arrTagStack = [];
      var nPosition = findNextTagNotBlacklisted(strToFix);
      while (nPosition !== -1) {
         // Please note: the "tag" is considered to be what follows the backslash that signals it's beginning.
         var strTag = getTagAt(strToFix, nPosition);
         if (strTag && isClosingTag(strTag)) {
            // Get the new tag set required to add it in to the string
            var strCloseTags = getTextToCloseTag(strTag, arrTagStack);

            // Insert the tags and don't forget the rest of the original, if there is any
            var nTagEnd = strToFix.indexOf(TAG_END_NOTATION, nPosition);
            if (nTagEnd !== -1) {
               strToFix = strToFix.substring(0, nPosition) + strCloseTags + strToFix.substring(nTagEnd + 1);
            } else {
               strToFix = strToFix.substring(0, nPosition) + strCloseTags;
            }

            // Increment this so that we're not going through the stuff we just inserted
            nPosition += strCloseTags.length;
         } else {
            if (strTag && !isBlacklistedTag(strTag)) {
               arrTagStack.push(matchingTag(strTag));
            }

            nPosition = Number(nPosition) + 1;
         }
         nPosition = findNextTag(strToFix, nPosition);
      }
   }

   return strToFix;
};

var removeEmptyTags = function(strToFix) {
   var strFixed = strToFix;

   if (strFixed) {
      // Loop through the entire string as long as there were corrections made last time
      // (in case more can be made based on the previous ones)
      var nCountCorrectedThisLoop = 1;
      while (nCountCorrectedThisLoop > 0) {
         nCountCorrectedThisLoop = 0;
         // Get the first tag that isn't blacklisted
         var nPosition = findNextTagNotBlacklisted(strFixed);
         while (nPosition !== -1) {
            // Get the next tag that's not blacklisted
            var nNextPosition = findNextTagNotBlacklisted(strFixed, nPosition + 1);

            // Get the actual tag
            var strTag = getTagAt(strFixed, nPosition);
            // If the actual tag exists
            if (strTag) {
               // As long as that next tag is actually found
               if (nNextPosition !== -1) {
                  // Set up the alternate tag that we're trying to match with
                  var strAlternateTag = matchingTag(strTag);

                  // Get the actual next tag
                  var strNextTag = getTagAt(strFixed, nNextPosition);
                  // If the actual next tag exists and is the same as what we're looking for
                  if (strNextTag && strNextTag === strAlternateTag) {
                     // Get the string between the two tags
                     var nStringBetweenBegin = nPosition + 1 + strTag.length;
                     var strBetween = strFixed.substring(nStringBetweenBegin, nNextPosition);
                     // If the trimmed string is empty
                     if (!trimString(strBetween).length) {
                        // Remove the ending tag
                        strFixed = removeTag(strFixed, nNextPosition);
                        // Then remove the beginning tag that we're actually at
                        strFixed = removeTag(strFixed, nPosition);

                        // Adjust the position of that next tag by the amount of space we've reduced
                        nNextPosition -= strTag.length + 1;
                        // Note that we've made a correction
                        nCountCorrectedThisLoop++;
                     }
                  }
               }
            }

            // Set the actual next position for the inner loop
            nPosition = nNextPosition;
         }
      }
   }

   return strFixed;
};

var toText = function(str) {
   if (str.match(/{\\rtf1 /)) {
      str = str.replace(/{\\rtf1 /, '');

      if (str.charAt(str.length - 1) === '}') {
         str = str.substring(0, str.length - 1);
      }
   }

   str = str.replace(/\\i /g, '');
   str = str.replace(/\\i0 /g, '');
   str = str.replace(/\\b /g, '');
   str = str.replace(/\\b0 /g, '');
   str = str.replace(/\\ul /g, '');
   str = str.replace(/\\ul0 /g, '');
   str = str.replace(/\\par/g, '');

   str = str.replace(/\\{/g, '{');
   str = str.replace(/\\}/g, '}');
   str = str.replace(/\\\\/g, '\\');

   return str;
};

var toHtml = function(str) {
   str = nestTags(str);
   str = removeEmptyTags(str);

   str = str.split('<').join('&lt;');
   str = str.split('>').join('&gt;');

   var removeLastBracket = false;

   //  remove the RTF and Replace with HTML
   if (str.indexOf('{\\rtf1') !== -1) {
      str = str.split('{\\rtf1 ').join('');
      removeLastBracket = true;
   }

   str = str.split('\\i0 ').join('</i>');
   str = str.split('\\b0 ').join('</b>');
   str = str.split('\\ul0 ').join('</u>');

   str = str.split('\\i ').join('<i>');
   str = str.split('\\b ').join('<b>');
   str = str.split('\\ul ').join('<u>');
   str = str.split('\\par ').join('<br/>');

   str = str.split('\\{').join('{');
   str = str.split('\\}').join('}');
   str = str.split('\\\\').join('\\');

   if (removeLastBracket && str.charAt(str.length - 1) === '}') {
      str = str.substring(0, str.length - 1);
   }

   // convert these so they are displayed and not used as HTML Tags ( exception <br> )
   str = str.split('&lt;br&gt;').join('<br/>');
   str = str.split('&lt;BR&gt;').join('<br/>');

   return str;
};

/** @module Rtf */
var Rtf = {
   /**
    *  Take a string with rtf markup and translate it to text
    *  @function toText
    *  @param {String} str - The rtf formatted input string to parse.
    *  @static
    *  @return {String} The string as text-only
    */
   toText: toText,
   /**
    *  Take a string with rtf markup and translate it to html
    *  @function toHtml
    *  @param {String} str - The rtf formatted input string to parse.
    *  @static
    *  @return {String} The string with html markup aligning to the rtf markup
    */
   toHtml: toHtml
};

var validateParams = function(params) {
   return params && typeof params.text === 'string' && (!params.languageIdentifierString || typeof params.languageIdentifierString === 'string');
};

var CaptionText = function(params) {
   var _languageIdentifier = LanguageIdentifier$1.create(languageIdentifiers.identifiers.default);
   var _text;
   var _plainText;
   var _markupText;
   var _additionalParamProperties = {};
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var setLanguageIdentifier = function(languageIdentifierString) {
      if (!languageIdentifiers.isSupportedLanguage(languageIdentifierString)) {
         languageIdentifierString = languageIdentifiers.identifiers.default;
      }

      var previousLanguageIdentifierString = _languageIdentifier.identifier;
      _languageIdentifier = LanguageIdentifier$1.create(languageIdentifierString);
      return UndoAction$1.create(setLanguageIdentifier, previousLanguageIdentifierString, setLanguageIdentifier, languageIdentifierString);
   };

   var setText = function(text) {
      if (typeof text !== 'string') {
         throw new Error('Caption text is expecting a string, but got (' + text + ')');
      }
      var previous = _text;
      _text = text;
      _plainText = Rtf.toText(_text);
      _markupText = Rtf.toHtml(_text);
      return UndoAction$1.create(setText, previous, setText, _text);
   };

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         atts: {
            'xml:lang': _languageIdentifier.identifier
         },
         text: _text
      };
      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         languageIdentifier: {
            get: function() {
               return _languageIdentifier;
            }
         },
         language: {
            get: function() {
               return _languageIdentifier.language;
            }
         },
         text: {
            get: function() {
               return _text;
            }
         },
         plainText: {
            get: function() {
               return _plainText;
            }
         },
         markupText: {
            get: function() {
               return _markupText;
            }
         }
      });

   _public.setLanguageIdentifier = setLanguageIdentifier;
   _public.setText = setText;
   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         languageIdentifier: _languageIdentifier.toPOJO(),
         language: _languageIdentifier.language,
         text: _text,
         plainText: _plainText,
         markupText: _markupText
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      forEach(keys(_additionalParamProperties), function(key) {
         pojo[key] = _additionalParamProperties[key];
      });

      return pojo;
   };

   if (validateParams(params)) {
      forEach(keys(params), function(key) {
         if (key === 'languageIdentifierString') {
            setLanguageIdentifier(params.languageIdentifierString);
         } else if (key === 'text') {
            setText(params.text);
         } else if (!isFunction(_additionalParamProperties[key])) {
            _additionalParamProperties[key] = params[key];
         }
      });
   } else {
      throw new Error('CaptionText expects text and language parameters');
   }

   return _public;
};

var createFromXmpElement = function(textElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);
   var rtfText = _xmlUtility.getElementText(textElement);
   var params = {
      languageIdentifierString: textElement.getAttribute('xml:lang'),
      text: rtfText
   };

   return CaptionText(params);
};

/** @module CaptionText */
var CaptionText$1 = {
   /**
    *  Factory method that returns a new CaptionText object.
    *  @function create
    *  @param {Object} params - Optional params for caption
    *  @param {String} [params.languageIdentifierString] - RFC 3066 notation language string
    *  @param {String} [params.text] - Text of caption item.
    *  @static
    *  @return {module:CaptionText} new CaptionText instance
    */
   create: function(params) {
      return CaptionText(params);
   },

   /**
    *  Factory method that returns a new CaptionText object from an XMP caption element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP caption item element.
    *  @static
    *  @return {module:CaptionText} new CaptionText instance
    */
   createFromXmpElement: createFromXmpElement
};

var decoratorFunctions = [];
var nullCaptionText = {
   text: undefined,
   plainText: undefined,
   markupText: undefined
};

var Caption = function(params) {
   var _startTime;
   var _duration;
   var _captionTextList = [];
   var _horizontalAlignment;
   var _verticalAlignment;
   var _file;
   var _image;
   var _imageIndex;
   var _imageOffset;
   var _imageRectangle;
   var _activeLanguageIdentifier = LanguageIdentifier$1.create(languageIdentifiers.identifiers.default);
   var _additionalParamProperties = {};
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var getAvailableLanguages = function() {
      return map(_captionTextList, function(captionText) {
         return captionText.languageIdentifier.identifier;
      });
   };

   var setStartTime = function(startTime) {
      if (typeof startTime !== 'number') {
         throw new Error('Caption startTime is expecting a number, but got (' + startTime + ')');
      }
      var previous = _startTime;
      _startTime = Math.floor(startTime);
      return UndoAction$1.create(setStartTime, previous, setStartTime, _startTime);
   };

   var setDuration = function(duration) {
      if (typeof duration !== 'number') {
         throw new Error('Caption duration is expecting a number, but got (' + duration + ')');
      }
      var previous = _duration;
      _duration = Math.floor(duration);
      return UndoAction$1.create(setDuration, previous, setDuration, _duration);
   };

   var addOrReplaceCaptionText = function(newCaptionText) {
      var existingCaptionItemIndex = findIndex(_captionTextList, function(captionItem) {
         return captionItem.languageIdentifier.identifier === newCaptionText.languageIdentifier.identifier;
      });
      if (existingCaptionItemIndex !== -1) {
         var oldCaptionItem = _captionTextList[existingCaptionItemIndex];
         _captionTextList.splice(existingCaptionItemIndex, 1, newCaptionText);
         return UndoAction$1.create(addOrReplaceCaptionText, oldCaptionItem, addOrReplaceCaptionText, newCaptionText);
      }

      if (newCaptionText.toPOJO) {
         _captionTextList.push(newCaptionText);
      } else {
         _captionTextList.push(CaptionText$1.create(newCaptionText));
      }
      return UndoAction$1.create(removeCaptionText, newCaptionText, addOrReplaceCaptionText, newCaptionText);
   };

   var removeCaptionText = function(captionText) {
      var removedCaptionItems = remove(_captionTextList, function(captionTextItem) {
         return captionTextItem.languageIdentifier.identifier === captionText.languageIdentifier.identifier && captionTextItem.text === captionText.text;
      });

      var removedCaptionItem = removedCaptionItems.length > 0 ? removedCaptionItems[0] : undefined;
      return UndoAction$1.create(addOrReplaceCaptionText, removedCaptionItem, removeCaptionText, captionText);
   };

   var setHorizontalAlignment = function(horizontalAlignment) {
      if (typeof horizontalAlignment !== 'string') {
         throw new Error('Caption horizontalAlignment is expecting a string, but got (' + horizontalAlignment + ')');
      }
      var previous = _horizontalAlignment;
      _horizontalAlignment = horizontalAlignment;
      return UndoAction$1.create(setHorizontalAlignment, previous, setHorizontalAlignment, _horizontalAlignment);
   };

   var setVerticalAlignment = function(verticalAlignment) {
      if (typeof verticalAlignment !== 'string') {
         throw new Error('Caption verticalAlignment is expecting a string, but got (' + verticalAlignment + ')');
      }
      var previous = _verticalAlignment;
      _verticalAlignment = verticalAlignment;
      return UndoAction$1.create(setVerticalAlignment, previous, setVerticalAlignment, _verticalAlignment);
   };

   var setFile = function(file) {
      if (typeof file !== 'string') {
         throw new Error('Caption file is expecting a string, but got (' + file + ')');
      }
      var previous = _file;
      _file = file;
      return UndoAction$1.create(setFile, previous, setFile, _file);
   };

   var setImage = function(image) {
      if (typeof image !== 'string') {
         throw new Error('Caption image is expecting a string, but got (' + image + ')');
      }
      var previous = _image;
      _image = image;
      return UndoAction$1.create(setImage, previous, setImage, _image);
   };

   var setImageIndex = function(imageIndex) {
      if (typeof imageIndex !== 'number') {
         throw new Error('Caption imageIndex is expecting a number, but got (' + imageIndex + ')');
      }
      var previous = _imageIndex;
      _imageIndex = imageIndex;
      return UndoAction$1.create(setImageIndex, previous, setImageIndex, _imageIndex);
   };

   var setImageOffset = function(imageOffset) {
      if (typeof imageOffset !== 'number') {
         throw new Error('Caption imageOffset is expecting a number, but got (' + imageOffset + ')');
      }
      var previous = _imageOffset;
      _imageOffset = imageOffset;
      return UndoAction$1.create(setImageOffset, previous, setImageOffset, _imageOffset);
   };

   var setImageRectangle = function(imageRectangle) {
      if (typeof imageRectangle !== 'object') {
         throw new Error('Caption imageRectangle is expecting an object, but got (' + imageRectangle + ')');
      }
      var previous = _imageRectangle;
      _imageRectangle = imageRectangle;
      return UndoAction$1.create(setImageRectangle, previous, setImageRectangle, _imageRectangle);
   };

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'xmpDM:startTime': _startTime,
                  'xmpDM:duration': _duration,
                  'tscDM:halign': _horizontalAlignment,
                  'tscDM:valign': _verticalAlignment,
                  'tscDM:file': _file,
                  'tscDM:image': _image,
                  'tscDM:imageindex': _imageIndex,
                  'tscDM:imageoffset': _imageOffset,
                  'tscDM:imagerect': _imageRectangle ? [_imageRectangle.x, _imageRectangle.y, _imageRectangle.width, _imageRectangle.height].join(', ') : undefined
               },
               children: [
                  {
                     name: 'xmpDM:name',
                     children: [
                        {
                           name: 'rdf:Alt',
                           children: _captionTextList
                        }
                     ]
                  }
               ]
            }
         ]
      };

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var getCaptionText = function() {
      if (_captionTextList.length === 0) {
         return nullCaptionText;
      }

      if (_activeLanguageIdentifier.identifier === languageIdentifiers.identifiers.default) {
         return _captionTextList[0];
      }
      var preferredCaptionText = find(_captionTextList, function(captionText) {
         return captionText.languageIdentifier.identifier === _activeLanguageIdentifier.identifier;
      });

      if (!preferredCaptionText) {
         preferredCaptionText = find(_captionTextList, function(captionText) {
            return captionText.language === _activeLanguageIdentifier.language;
         });
      }

      return preferredCaptionText || nullCaptionText;
   };

   var _public = Object.defineProperties({},
      {
         startTime: {
            get: function() {
               return _startTime;
            }
         },
         duration: {
            get: function() {
               return _duration;
            }
         },
         endTime: {
            get: function() {
               return _startTime + _duration;
            }
         },
         captionTextList: {
            get: function() {
               return _captionTextList;
            }
         },
         text: {
            get: function() {
               return getCaptionText().text;
            }
         },
         plainText: {
            get: function() {
               return getCaptionText().plainText;
            }
         },
         markupText: {
            get: function() {
               return getCaptionText().markupText;
            }
         },
         horizontalAlignment: {
            get: function() {
               return _horizontalAlignment;
            }
         },
         verticalAlignment: {
            get: function() {
               return _verticalAlignment;
            }
         },
         file: {
            get: function() {
               return _file;
            }
         },
         image: {
            get: function() {
               return _image;
            }
         },
         imageIndex: {
            get: function() {
               return _imageIndex;
            }
         },
         imageOffset: {
            get: function() {
               return _imageOffset;
            }
         },
         imageRectangle: {
            get: function() {
               return _imageRectangle;
            }
         },
         activeLanguageIdentifier: {
            get: function() {
               return _activeLanguageIdentifier;
            },
            set: function(languageIdentifier) {
               _activeLanguageIdentifier = languageIdentifier;
            }
         }
      });

   _public.getAvailableLanguages = getAvailableLanguages;
   _public.setStartTime = setStartTime;
   _public.setDuration = setDuration;
   _public.addOrReplaceCaptionText = addOrReplaceCaptionText;
   _public.removeCaptionText = removeCaptionText;
   _public.setHorizontalAlignment = setHorizontalAlignment;
   _public.setVerticalAlignment = setVerticalAlignment;
   _public.setFile = setFile;
   _public.setImage = setImage;
   _public.setImageIndex = setImageIndex;
   _public.setImageOffset = setImageOffset;
   _public.setImageRectangle = setImageRectangle;

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         startTime: _startTime,
         duration: _duration,
         captionTextList: invokeMap(_captionTextList, 'toPOJO'),
         horizontalAlignment: _horizontalAlignment,
         verticalAlignment: _verticalAlignment,
         file: _file,
         image: _image,
         imageIndex: _imageIndex,
         imageOffset: _imageOffset,
         imageRectangle: _imageRectangle ? _imageRectangle.toPOJO() : _imageRectangle
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      forEach(keys(_additionalParamProperties), function(key) {
         pojo[key] = _additionalParamProperties[key];
      });
      return pojo;
   };

   if (params) {
      forEach(keys(params), function(key) {
         if (key === 'startTime' && typeof(params.startTime) === 'number') {
            setStartTime(params.startTime);
         } else if (key === 'duration' && typeof(params.duration) === 'number') {
            setDuration(params.duration);
         } else if (key === 'captionTextList' && params.captionTextList) {
            map(params.captionTextList, addOrReplaceCaptionText);
         } else if (key === 'horizontalAlignment' && params.horizontalAlignment) {
            setHorizontalAlignment(params.horizontalAlignment);
         } else if (key === 'verticalAlignment' && params.verticalAlignment) {
            setVerticalAlignment(params.verticalAlignment);
         } else if (key === 'file' && params.file) {
            setFile(params.file);
         } else if (key === 'image' && params.image) {
            setImage(params.image);
         } else if (key === 'imageIndex' && typeof(params.imageIndex) === 'number') {
            setImageIndex(params.imageIndex);
         } else if (key === 'imageOffset' && typeof(params.imageOffset) === 'number') {
            setImageOffset(params.imageOffset);
         } else if (key === 'imageRectangle' && params.imageRectangle) {
            if (!params.imageRectangle.toPOJO) {
               params.imageRectangle = ImageRectangle$1.create(params.imageRectangle);
            }
            setImageRectangle(params.imageRectangle);
         } else if (!isFunction(_additionalParamProperties[key])) {
            _additionalParamProperties[key] = params[key];
         }
      });
   }

   forEach(decoratorFunctions, function(func) {
      func(_public);
   });

   return _public;
};

var createFromXmpElement$1 = function(captionElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var captionTextElements = _xmlUtility.getElementsByTagNameNSPrefix(captionElement, 'rdf:li');
   var captionTextList = map(captionTextElements, CaptionText$1.createFromXmpElement);

   var params = {
      startTime: Number(captionElement.getAttribute('xmpDM:startTime')),
      duration: Number(captionElement.getAttribute('xmpDM:duration')),
      captionTextList: captionTextList,
      horizontalAlignment: captionElement.getAttribute('tscDM:halign'),
      verticalAlignment: captionElement.getAttribute('tscDM:valign')
   };

   if (captionElement.getAttribute('tscDM:file')) {
      params.file = captionElement.getAttribute('tscDM:file');
   }

   if (captionElement.getAttribute('tscDM:image')) {
      params.image = captionElement.getAttribute('tscDM:image');
   }

   if (captionElement.getAttribute('tscDM:imageindex')) {
      params.imageIndex = Number(captionElement.getAttribute('tscDM:imageindex'));
   }

   if (captionElement.getAttribute('tscDM:imageoffset')) {
      params.imageOffset = Number(captionElement.getAttribute('tscDM:imageoffset'));
   }

   if (captionElement.getAttribute('tscDM:imagerect')) {
      params.imageRectangle = ImageRectangle$1.create(captionElement.getAttribute('tscDM:imagerect'));
   }

   return Caption(params);
};

var registerDecorator = function(decorator) {
   if (decorator && typeof decorator === 'function') {
      decoratorFunctions.push(decorator);
   } else {
      throw new Error('decorator must be a function');
   }
};

var clearDecorators = function() {
   decoratorFunctions = [];
};

/** @module Caption */
var Caption$1 = {
   /**
    *  Factory method that returns a new Caption object.
    *  @function create
    *  @param {Object} params - Optional params for caption
    *  @param {Number} [params.startTime] - Start time of caption.
    *  @param {Number} [params.duration] - Duration of caption.
    *  @param {module:CaptionText[]} [params.captionTextList] - List of CaptionText objects.
    *  @param {String} [params.horizontalAlignment] - Horizontal alignment of caption text.
    *  @param {String} [params.verticalAlignment] - Vertical alignment of caption text.
    *  @param {String} [params.file] - Reference to a specific file as String.
    *  @param {String} [params.image] - Url for caption thumbnail.
    *  @param {Number} [params.imageIndex] - Index of image within file consisting of one or more images.
    *  @param {Number} [params.imageOffset] - Number of bytes this image begins at, relative to the beginning of the entire file
    *  @param {module:ImageRectangle} [params.imageRectangle] - Image Rectangle for image sprite map
    *  @static
    *  @return {module:Caption} new Caption instance
    */
   create: function(params) {
      return Caption(params);
   },

   /**
    *  Factory method that returns a new Caption object from an XMP caption element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP caption element.
    *  @static
    *  @return {module:Caption} new Caption instance
    */
   createFromXmpElement: createFromXmpElement$1,

   /**
    *  Method to allow registration of decorator function that can extend the caption
    *  object before it is returned in the create functions.
    *  @function registerDecorator
    *  @param {Function} decorator - Function that will be used to decorate the caption.
    *  The function should take a caption parameter.
    *  @static
    */
   registerDecorator: registerDecorator,
   /**
    *  Method to allow clearing decorator functions
    *  @function clearDecorators
    *  @static
    */
   clearDecorators: clearDecorators
};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var tinycolor = createCommonjsModule(function (module) {
// TinyColor v1.4.1
// https://github.com/bgrins/TinyColor
// Brian Grinstead, MIT License

(function(Math) {

var trimLeft = /^\s+/,
    trimRight = /\s+$/,
    tinyCounter = 0,
    mathRound = Math.round,
    mathMin = Math.min,
    mathMax = Math.max,
    mathRandom = Math.random;

function tinycolor (color, opts) {

    color = (color) ? color : '';
    opts = opts || { };

    // If input is already a tinycolor, return itself
    if (color instanceof tinycolor) {
       return color;
    }
    // If we are called as a function, call using new instead
    if (!(this instanceof tinycolor)) {
        return new tinycolor(color, opts);
    }

    var rgb = inputToRGB(color);
    this._originalInput = color,
    this._r = rgb.r,
    this._g = rgb.g,
    this._b = rgb.b,
    this._a = rgb.a,
    this._roundA = mathRound(100*this._a) / 100,
    this._format = opts.format || rgb.format;
    this._gradientType = opts.gradientType;

    // Don't let the range of [0,255] come back in [0,1].
    // Potentially lose a little bit of precision here, but will fix issues where
    // .5 gets interpreted as half of the total, instead of half of 1
    // If it was supposed to be 128, this was already taken care of by `inputToRgb`
    if (this._r < 1) { this._r = mathRound(this._r); }
    if (this._g < 1) { this._g = mathRound(this._g); }
    if (this._b < 1) { this._b = mathRound(this._b); }

    this._ok = rgb.ok;
    this._tc_id = tinyCounter++;
}

tinycolor.prototype = {
    isDark: function() {
        return this.getBrightness() < 128;
    },
    isLight: function() {
        return !this.isDark();
    },
    isValid: function() {
        return this._ok;
    },
    getOriginalInput: function() {
      return this._originalInput;
    },
    getFormat: function() {
        return this._format;
    },
    getAlpha: function() {
        return this._a;
    },
    getBrightness: function() {
        //http://www.w3.org/TR/AERT#color-contrast
        var rgb = this.toRgb();
        return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    },
    getLuminance: function() {
        //http://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
        var rgb = this.toRgb();
        var RsRGB, GsRGB, BsRGB, R, G, B;
        RsRGB = rgb.r/255;
        GsRGB = rgb.g/255;
        BsRGB = rgb.b/255;

        if (RsRGB <= 0.03928) {R = RsRGB / 12.92;} else {R = Math.pow(((RsRGB + 0.055) / 1.055), 2.4);}
        if (GsRGB <= 0.03928) {G = GsRGB / 12.92;} else {G = Math.pow(((GsRGB + 0.055) / 1.055), 2.4);}
        if (BsRGB <= 0.03928) {B = BsRGB / 12.92;} else {B = Math.pow(((BsRGB + 0.055) / 1.055), 2.4);}
        return (0.2126 * R) + (0.7152 * G) + (0.0722 * B);
    },
    setAlpha: function(value) {
        this._a = boundAlpha(value);
        this._roundA = mathRound(100*this._a) / 100;
        return this;
    },
    toHsv: function() {
        var hsv = rgbToHsv(this._r, this._g, this._b);
        return { h: hsv.h * 360, s: hsv.s, v: hsv.v, a: this._a };
    },
    toHsvString: function() {
        var hsv = rgbToHsv(this._r, this._g, this._b);
        var h = mathRound(hsv.h * 360), s = mathRound(hsv.s * 100), v = mathRound(hsv.v * 100);
        return (this._a == 1) ?
          "hsv("  + h + ", " + s + "%, " + v + "%)" :
          "hsva(" + h + ", " + s + "%, " + v + "%, "+ this._roundA + ")";
    },
    toHsl: function() {
        var hsl = rgbToHsl(this._r, this._g, this._b);
        return { h: hsl.h * 360, s: hsl.s, l: hsl.l, a: this._a };
    },
    toHslString: function() {
        var hsl = rgbToHsl(this._r, this._g, this._b);
        var h = mathRound(hsl.h * 360), s = mathRound(hsl.s * 100), l = mathRound(hsl.l * 100);
        return (this._a == 1) ?
          "hsl("  + h + ", " + s + "%, " + l + "%)" :
          "hsla(" + h + ", " + s + "%, " + l + "%, "+ this._roundA + ")";
    },
    toHex: function(allow3Char) {
        return rgbToHex(this._r, this._g, this._b, allow3Char);
    },
    toHexString: function(allow3Char) {
        return '#' + this.toHex(allow3Char);
    },
    toHex8: function(allow4Char) {
        return rgbaToHex(this._r, this._g, this._b, this._a, allow4Char);
    },
    toHex8String: function(allow4Char) {
        return '#' + this.toHex8(allow4Char);
    },
    toRgb: function() {
        return { r: mathRound(this._r), g: mathRound(this._g), b: mathRound(this._b), a: this._a };
    },
    toRgbString: function() {
        return (this._a == 1) ?
          "rgb("  + mathRound(this._r) + ", " + mathRound(this._g) + ", " + mathRound(this._b) + ")" :
          "rgba(" + mathRound(this._r) + ", " + mathRound(this._g) + ", " + mathRound(this._b) + ", " + this._roundA + ")";
    },
    toPercentageRgb: function() {
        return { r: mathRound(bound01(this._r, 255) * 100) + "%", g: mathRound(bound01(this._g, 255) * 100) + "%", b: mathRound(bound01(this._b, 255) * 100) + "%", a: this._a };
    },
    toPercentageRgbString: function() {
        return (this._a == 1) ?
          "rgb("  + mathRound(bound01(this._r, 255) * 100) + "%, " + mathRound(bound01(this._g, 255) * 100) + "%, " + mathRound(bound01(this._b, 255) * 100) + "%)" :
          "rgba(" + mathRound(bound01(this._r, 255) * 100) + "%, " + mathRound(bound01(this._g, 255) * 100) + "%, " + mathRound(bound01(this._b, 255) * 100) + "%, " + this._roundA + ")";
    },
    toName: function() {
        if (this._a === 0) {
            return "transparent";
        }

        if (this._a < 1) {
            return false;
        }

        return hexNames[rgbToHex(this._r, this._g, this._b, true)] || false;
    },
    toFilter: function(secondColor) {
        var hex8String = '#' + rgbaToArgbHex(this._r, this._g, this._b, this._a);
        var secondHex8String = hex8String;
        var gradientType = this._gradientType ? "GradientType = 1, " : "";

        if (secondColor) {
            var s = tinycolor(secondColor);
            secondHex8String = '#' + rgbaToArgbHex(s._r, s._g, s._b, s._a);
        }

        return "progid:DXImageTransform.Microsoft.gradient("+gradientType+"startColorstr="+hex8String+",endColorstr="+secondHex8String+")";
    },
    toString: function(format) {
        var formatSet = !!format;
        format = format || this._format;

        var formattedString = false;
        var hasAlpha = this._a < 1 && this._a >= 0;
        var needsAlphaFormat = !formatSet && hasAlpha && (format === "hex" || format === "hex6" || format === "hex3" || format === "hex4" || format === "hex8" || format === "name");

        if (needsAlphaFormat) {
            // Special case for "transparent", all other non-alpha formats
            // will return rgba when there is transparency.
            if (format === "name" && this._a === 0) {
                return this.toName();
            }
            return this.toRgbString();
        }
        if (format === "rgb") {
            formattedString = this.toRgbString();
        }
        if (format === "prgb") {
            formattedString = this.toPercentageRgbString();
        }
        if (format === "hex" || format === "hex6") {
            formattedString = this.toHexString();
        }
        if (format === "hex3") {
            formattedString = this.toHexString(true);
        }
        if (format === "hex4") {
            formattedString = this.toHex8String(true);
        }
        if (format === "hex8") {
            formattedString = this.toHex8String();
        }
        if (format === "name") {
            formattedString = this.toName();
        }
        if (format === "hsl") {
            formattedString = this.toHslString();
        }
        if (format === "hsv") {
            formattedString = this.toHsvString();
        }

        return formattedString || this.toHexString();
    },
    clone: function() {
        return tinycolor(this.toString());
    },

    _applyModification: function(fn, args) {
        var color = fn.apply(null, [this].concat([].slice.call(args)));
        this._r = color._r;
        this._g = color._g;
        this._b = color._b;
        this.setAlpha(color._a);
        return this;
    },
    lighten: function() {
        return this._applyModification(lighten, arguments);
    },
    brighten: function() {
        return this._applyModification(brighten, arguments);
    },
    darken: function() {
        return this._applyModification(darken, arguments);
    },
    desaturate: function() {
        return this._applyModification(desaturate, arguments);
    },
    saturate: function() {
        return this._applyModification(saturate, arguments);
    },
    greyscale: function() {
        return this._applyModification(greyscale, arguments);
    },
    spin: function() {
        return this._applyModification(spin, arguments);
    },

    _applyCombination: function(fn, args) {
        return fn.apply(null, [this].concat([].slice.call(args)));
    },
    analogous: function() {
        return this._applyCombination(analogous, arguments);
    },
    complement: function() {
        return this._applyCombination(complement, arguments);
    },
    monochromatic: function() {
        return this._applyCombination(monochromatic, arguments);
    },
    splitcomplement: function() {
        return this._applyCombination(splitcomplement, arguments);
    },
    triad: function() {
        return this._applyCombination(triad, arguments);
    },
    tetrad: function() {
        return this._applyCombination(tetrad, arguments);
    }
};

// If input is an object, force 1 into "1.0" to handle ratios properly
// String input requires "1.0" as input, so 1 will be treated as 1
tinycolor.fromRatio = function(color, opts) {
    if (typeof color == "object") {
        var newColor = {};
        for (var i in color) {
            if (color.hasOwnProperty(i)) {
                if (i === "a") {
                    newColor[i] = color[i];
                }
                else {
                    newColor[i] = convertToPercentage(color[i]);
                }
            }
        }
        color = newColor;
    }

    return tinycolor(color, opts);
};

// Given a string or object, convert that input to RGB
// Possible string inputs:
//
//     "red"
//     "#f00" or "f00"
//     "#ff0000" or "ff0000"
//     "#ff000000" or "ff000000"
//     "rgb 255 0 0" or "rgb (255, 0, 0)"
//     "rgb 1.0 0 0" or "rgb (1, 0, 0)"
//     "rgba (255, 0, 0, 1)" or "rgba 255, 0, 0, 1"
//     "rgba (1.0, 0, 0, 1)" or "rgba 1.0, 0, 0, 1"
//     "hsl(0, 100%, 50%)" or "hsl 0 100% 50%"
//     "hsla(0, 100%, 50%, 1)" or "hsla 0 100% 50%, 1"
//     "hsv(0, 100%, 100%)" or "hsv 0 100% 100%"
//
function inputToRGB(color) {

    var rgb = { r: 0, g: 0, b: 0 };
    var a = 1;
    var s = null;
    var v = null;
    var l = null;
    var ok = false;
    var format = false;

    if (typeof color == "string") {
        color = stringInputToObject(color);
    }

    if (typeof color == "object") {
        if (isValidCSSUnit(color.r) && isValidCSSUnit(color.g) && isValidCSSUnit(color.b)) {
            rgb = rgbToRgb(color.r, color.g, color.b);
            ok = true;
            format = String(color.r).substr(-1) === "%" ? "prgb" : "rgb";
        }
        else if (isValidCSSUnit(color.h) && isValidCSSUnit(color.s) && isValidCSSUnit(color.v)) {
            s = convertToPercentage(color.s);
            v = convertToPercentage(color.v);
            rgb = hsvToRgb(color.h, s, v);
            ok = true;
            format = "hsv";
        }
        else if (isValidCSSUnit(color.h) && isValidCSSUnit(color.s) && isValidCSSUnit(color.l)) {
            s = convertToPercentage(color.s);
            l = convertToPercentage(color.l);
            rgb = hslToRgb(color.h, s, l);
            ok = true;
            format = "hsl";
        }

        if (color.hasOwnProperty("a")) {
            a = color.a;
        }
    }

    a = boundAlpha(a);

    return {
        ok: ok,
        format: color.format || format,
        r: mathMin(255, mathMax(rgb.r, 0)),
        g: mathMin(255, mathMax(rgb.g, 0)),
        b: mathMin(255, mathMax(rgb.b, 0)),
        a: a
    };
}


// Conversion Functions
// --------------------

// `rgbToHsl`, `rgbToHsv`, `hslToRgb`, `hsvToRgb` modified from:
// <http://mjijackson.com/2008/02/rgb-to-hsl-and-rgb-to-hsv-color-model-conversion-algorithms-in-javascript>

// `rgbToRgb`
// Handle bounds / percentage checking to conform to CSS color spec
// <http://www.w3.org/TR/css3-color/>
// *Assumes:* r, g, b in [0, 255] or [0, 1]
// *Returns:* { r, g, b } in [0, 255]
function rgbToRgb(r, g, b){
    return {
        r: bound01(r, 255) * 255,
        g: bound01(g, 255) * 255,
        b: bound01(b, 255) * 255
    };
}

// `rgbToHsl`
// Converts an RGB color value to HSL.
// *Assumes:* r, g, and b are contained in [0, 255] or [0, 1]
// *Returns:* { h, s, l } in [0,1]
function rgbToHsl(r, g, b) {

    r = bound01(r, 255);
    g = bound01(g, 255);
    b = bound01(b, 255);

    var max = mathMax(r, g, b), min = mathMin(r, g, b);
    var h, s, l = (max + min) / 2;

    if(max == min) {
        h = s = 0; // achromatic
    }
    else {
        var d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch(max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }

        h /= 6;
    }

    return { h: h, s: s, l: l };
}

// `hslToRgb`
// Converts an HSL color value to RGB.
// *Assumes:* h is contained in [0, 1] or [0, 360] and s and l are contained [0, 1] or [0, 100]
// *Returns:* { r, g, b } in the set [0, 255]
function hslToRgb(h, s, l) {
    var r, g, b;

    h = bound01(h, 360);
    s = bound01(s, 100);
    l = bound01(l, 100);

    function hue2rgb(p, q, t) {
        if(t < 0) t += 1;
        if(t > 1) t -= 1;
        if(t < 1/6) return p + (q - p) * 6 * t;
        if(t < 1/2) return q;
        if(t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
    }

    if(s === 0) {
        r = g = b = l; // achromatic
    }
    else {
        var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        var p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1/3);
    }

    return { r: r * 255, g: g * 255, b: b * 255 };
}

// `rgbToHsv`
// Converts an RGB color value to HSV
// *Assumes:* r, g, and b are contained in the set [0, 255] or [0, 1]
// *Returns:* { h, s, v } in [0,1]
function rgbToHsv(r, g, b) {

    r = bound01(r, 255);
    g = bound01(g, 255);
    b = bound01(b, 255);

    var max = mathMax(r, g, b), min = mathMin(r, g, b);
    var h, s, v = max;

    var d = max - min;
    s = max === 0 ? 0 : d / max;

    if(max == min) {
        h = 0; // achromatic
    }
    else {
        switch(max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return { h: h, s: s, v: v };
}

// `hsvToRgb`
// Converts an HSV color value to RGB.
// *Assumes:* h is contained in [0, 1] or [0, 360] and s and v are contained in [0, 1] or [0, 100]
// *Returns:* { r, g, b } in the set [0, 255]
 function hsvToRgb(h, s, v) {

    h = bound01(h, 360) * 6;
    s = bound01(s, 100);
    v = bound01(v, 100);

    var i = Math.floor(h),
        f = h - i,
        p = v * (1 - s),
        q = v * (1 - f * s),
        t = v * (1 - (1 - f) * s),
        mod = i % 6,
        r = [v, q, p, p, t, v][mod],
        g = [t, v, v, q, p, p][mod],
        b = [p, p, t, v, v, q][mod];

    return { r: r * 255, g: g * 255, b: b * 255 };
}

// `rgbToHex`
// Converts an RGB color to hex
// Assumes r, g, and b are contained in the set [0, 255]
// Returns a 3 or 6 character hex
function rgbToHex(r, g, b, allow3Char) {

    var hex = [
        pad2(mathRound(r).toString(16)),
        pad2(mathRound(g).toString(16)),
        pad2(mathRound(b).toString(16))
    ];

    // Return a 3 character hex if possible
    if (allow3Char && hex[0].charAt(0) == hex[0].charAt(1) && hex[1].charAt(0) == hex[1].charAt(1) && hex[2].charAt(0) == hex[2].charAt(1)) {
        return hex[0].charAt(0) + hex[1].charAt(0) + hex[2].charAt(0);
    }

    return hex.join("");
}

// `rgbaToHex`
// Converts an RGBA color plus alpha transparency to hex
// Assumes r, g, b are contained in the set [0, 255] and
// a in [0, 1]. Returns a 4 or 8 character rgba hex
function rgbaToHex(r, g, b, a, allow4Char) {

    var hex = [
        pad2(mathRound(r).toString(16)),
        pad2(mathRound(g).toString(16)),
        pad2(mathRound(b).toString(16)),
        pad2(convertDecimalToHex(a))
    ];

    // Return a 4 character hex if possible
    if (allow4Char && hex[0].charAt(0) == hex[0].charAt(1) && hex[1].charAt(0) == hex[1].charAt(1) && hex[2].charAt(0) == hex[2].charAt(1) && hex[3].charAt(0) == hex[3].charAt(1)) {
        return hex[0].charAt(0) + hex[1].charAt(0) + hex[2].charAt(0) + hex[3].charAt(0);
    }

    return hex.join("");
}

// `rgbaToArgbHex`
// Converts an RGBA color to an ARGB Hex8 string
// Rarely used, but required for "toFilter()"
function rgbaToArgbHex(r, g, b, a) {

    var hex = [
        pad2(convertDecimalToHex(a)),
        pad2(mathRound(r).toString(16)),
        pad2(mathRound(g).toString(16)),
        pad2(mathRound(b).toString(16))
    ];

    return hex.join("");
}

// `equals`
// Can be called with any tinycolor input
tinycolor.equals = function (color1, color2) {
    if (!color1 || !color2) { return false; }
    return tinycolor(color1).toRgbString() == tinycolor(color2).toRgbString();
};

tinycolor.random = function() {
    return tinycolor.fromRatio({
        r: mathRandom(),
        g: mathRandom(),
        b: mathRandom()
    });
};


// Modification Functions
// ----------------------
// Thanks to less.js for some of the basics here
// <https://github.com/cloudhead/less.js/blob/master/lib/less/functions.js>

function desaturate(color, amount) {
    amount = (amount === 0) ? 0 : (amount || 10);
    var hsl = tinycolor(color).toHsl();
    hsl.s -= amount / 100;
    hsl.s = clamp01(hsl.s);
    return tinycolor(hsl);
}

function saturate(color, amount) {
    amount = (amount === 0) ? 0 : (amount || 10);
    var hsl = tinycolor(color).toHsl();
    hsl.s += amount / 100;
    hsl.s = clamp01(hsl.s);
    return tinycolor(hsl);
}

function greyscale(color) {
    return tinycolor(color).desaturate(100);
}

function lighten (color, amount) {
    amount = (amount === 0) ? 0 : (amount || 10);
    var hsl = tinycolor(color).toHsl();
    hsl.l += amount / 100;
    hsl.l = clamp01(hsl.l);
    return tinycolor(hsl);
}

function brighten(color, amount) {
    amount = (amount === 0) ? 0 : (amount || 10);
    var rgb = tinycolor(color).toRgb();
    rgb.r = mathMax(0, mathMin(255, rgb.r - mathRound(255 * - (amount / 100))));
    rgb.g = mathMax(0, mathMin(255, rgb.g - mathRound(255 * - (amount / 100))));
    rgb.b = mathMax(0, mathMin(255, rgb.b - mathRound(255 * - (amount / 100))));
    return tinycolor(rgb);
}

function darken (color, amount) {
    amount = (amount === 0) ? 0 : (amount || 10);
    var hsl = tinycolor(color).toHsl();
    hsl.l -= amount / 100;
    hsl.l = clamp01(hsl.l);
    return tinycolor(hsl);
}

// Spin takes a positive or negative amount within [-360, 360] indicating the change of hue.
// Values outside of this range will be wrapped into this range.
function spin(color, amount) {
    var hsl = tinycolor(color).toHsl();
    var hue = (hsl.h + amount) % 360;
    hsl.h = hue < 0 ? 360 + hue : hue;
    return tinycolor(hsl);
}

// Combination Functions
// ---------------------
// Thanks to jQuery xColor for some of the ideas behind these
// <https://github.com/infusion/jQuery-xcolor/blob/master/jquery.xcolor.js>

function complement(color) {
    var hsl = tinycolor(color).toHsl();
    hsl.h = (hsl.h + 180) % 360;
    return tinycolor(hsl);
}

function triad(color) {
    var hsl = tinycolor(color).toHsl();
    var h = hsl.h;
    return [
        tinycolor(color),
        tinycolor({ h: (h + 120) % 360, s: hsl.s, l: hsl.l }),
        tinycolor({ h: (h + 240) % 360, s: hsl.s, l: hsl.l })
    ];
}

function tetrad(color) {
    var hsl = tinycolor(color).toHsl();
    var h = hsl.h;
    return [
        tinycolor(color),
        tinycolor({ h: (h + 90) % 360, s: hsl.s, l: hsl.l }),
        tinycolor({ h: (h + 180) % 360, s: hsl.s, l: hsl.l }),
        tinycolor({ h: (h + 270) % 360, s: hsl.s, l: hsl.l })
    ];
}

function splitcomplement(color) {
    var hsl = tinycolor(color).toHsl();
    var h = hsl.h;
    return [
        tinycolor(color),
        tinycolor({ h: (h + 72) % 360, s: hsl.s, l: hsl.l}),
        tinycolor({ h: (h + 216) % 360, s: hsl.s, l: hsl.l})
    ];
}

function analogous(color, results, slices) {
    results = results || 6;
    slices = slices || 30;

    var hsl = tinycolor(color).toHsl();
    var part = 360 / slices;
    var ret = [tinycolor(color)];

    for (hsl.h = ((hsl.h - (part * results >> 1)) + 720) % 360; --results; ) {
        hsl.h = (hsl.h + part) % 360;
        ret.push(tinycolor(hsl));
    }
    return ret;
}

function monochromatic(color, results) {
    results = results || 6;
    var hsv = tinycolor(color).toHsv();
    var h = hsv.h, s = hsv.s, v = hsv.v;
    var ret = [];
    var modification = 1 / results;

    while (results--) {
        ret.push(tinycolor({ h: h, s: s, v: v}));
        v = (v + modification) % 1;
    }

    return ret;
}

// Utility Functions
// ---------------------

tinycolor.mix = function(color1, color2, amount) {
    amount = (amount === 0) ? 0 : (amount || 50);

    var rgb1 = tinycolor(color1).toRgb();
    var rgb2 = tinycolor(color2).toRgb();

    var p = amount / 100;

    var rgba = {
        r: ((rgb2.r - rgb1.r) * p) + rgb1.r,
        g: ((rgb2.g - rgb1.g) * p) + rgb1.g,
        b: ((rgb2.b - rgb1.b) * p) + rgb1.b,
        a: ((rgb2.a - rgb1.a) * p) + rgb1.a
    };

    return tinycolor(rgba);
};


// Readability Functions
// ---------------------
// <http://www.w3.org/TR/2008/REC-WCAG20-20081211/#contrast-ratiodef (WCAG Version 2)

// `contrast`
// Analyze the 2 colors and returns the color contrast defined by (WCAG Version 2)
tinycolor.readability = function(color1, color2) {
    var c1 = tinycolor(color1);
    var c2 = tinycolor(color2);
    return (Math.max(c1.getLuminance(),c2.getLuminance())+0.05) / (Math.min(c1.getLuminance(),c2.getLuminance())+0.05);
};

// `isReadable`
// Ensure that foreground and background color combinations meet WCAG2 guidelines.
// The third argument is an optional Object.
//      the 'level' property states 'AA' or 'AAA' - if missing or invalid, it defaults to 'AA';
//      the 'size' property states 'large' or 'small' - if missing or invalid, it defaults to 'small'.
// If the entire object is absent, isReadable defaults to {level:"AA",size:"small"}.

// *Example*
//    tinycolor.isReadable("#000", "#111") => false
//    tinycolor.isReadable("#000", "#111",{level:"AA",size:"large"}) => false
tinycolor.isReadable = function(color1, color2, wcag2) {
    var readability = tinycolor.readability(color1, color2);
    var wcag2Parms, out;

    out = false;

    wcag2Parms = validateWCAG2Parms(wcag2);
    switch (wcag2Parms.level + wcag2Parms.size) {
        case "AAsmall":
        case "AAAlarge":
            out = readability >= 4.5;
            break;
        case "AAlarge":
            out = readability >= 3;
            break;
        case "AAAsmall":
            out = readability >= 7;
            break;
    }
    return out;

};

// `mostReadable`
// Given a base color and a list of possible foreground or background
// colors for that base, returns the most readable color.
// Optionally returns Black or White if the most readable color is unreadable.
// *Example*
//    tinycolor.mostReadable(tinycolor.mostReadable("#123", ["#124", "#125"],{includeFallbackColors:false}).toHexString(); // "#112255"
//    tinycolor.mostReadable(tinycolor.mostReadable("#123", ["#124", "#125"],{includeFallbackColors:true}).toHexString();  // "#ffffff"
//    tinycolor.mostReadable("#a8015a", ["#faf3f3"],{includeFallbackColors:true,level:"AAA",size:"large"}).toHexString(); // "#faf3f3"
//    tinycolor.mostReadable("#a8015a", ["#faf3f3"],{includeFallbackColors:true,level:"AAA",size:"small"}).toHexString(); // "#ffffff"
tinycolor.mostReadable = function(baseColor, colorList, args) {
    var bestColor = null;
    var bestScore = 0;
    var readability;
    var includeFallbackColors, level, size ;
    args = args || {};
    includeFallbackColors = args.includeFallbackColors ;
    level = args.level;
    size = args.size;

    for (var i= 0; i < colorList.length ; i++) {
        readability = tinycolor.readability(baseColor, colorList[i]);
        if (readability > bestScore) {
            bestScore = readability;
            bestColor = tinycolor(colorList[i]);
        }
    }

    if (tinycolor.isReadable(baseColor, bestColor, {"level":level,"size":size}) || !includeFallbackColors) {
        return bestColor;
    }
    else {
        args.includeFallbackColors=false;
        return tinycolor.mostReadable(baseColor,["#fff", "#000"],args);
    }
};


// Big List of Colors
// ------------------
// <http://www.w3.org/TR/css3-color/#svg-color>
var names = tinycolor.names = {
    aliceblue: "f0f8ff",
    antiquewhite: "faebd7",
    aqua: "0ff",
    aquamarine: "7fffd4",
    azure: "f0ffff",
    beige: "f5f5dc",
    bisque: "ffe4c4",
    black: "000",
    blanchedalmond: "ffebcd",
    blue: "00f",
    blueviolet: "8a2be2",
    brown: "a52a2a",
    burlywood: "deb887",
    burntsienna: "ea7e5d",
    cadetblue: "5f9ea0",
    chartreuse: "7fff00",
    chocolate: "d2691e",
    coral: "ff7f50",
    cornflowerblue: "6495ed",
    cornsilk: "fff8dc",
    crimson: "dc143c",
    cyan: "0ff",
    darkblue: "00008b",
    darkcyan: "008b8b",
    darkgoldenrod: "b8860b",
    darkgray: "a9a9a9",
    darkgreen: "006400",
    darkgrey: "a9a9a9",
    darkkhaki: "bdb76b",
    darkmagenta: "8b008b",
    darkolivegreen: "556b2f",
    darkorange: "ff8c00",
    darkorchid: "9932cc",
    darkred: "8b0000",
    darksalmon: "e9967a",
    darkseagreen: "8fbc8f",
    darkslateblue: "483d8b",
    darkslategray: "2f4f4f",
    darkslategrey: "2f4f4f",
    darkturquoise: "00ced1",
    darkviolet: "9400d3",
    deeppink: "ff1493",
    deepskyblue: "00bfff",
    dimgray: "696969",
    dimgrey: "696969",
    dodgerblue: "1e90ff",
    firebrick: "b22222",
    floralwhite: "fffaf0",
    forestgreen: "228b22",
    fuchsia: "f0f",
    gainsboro: "dcdcdc",
    ghostwhite: "f8f8ff",
    gold: "ffd700",
    goldenrod: "daa520",
    gray: "808080",
    green: "008000",
    greenyellow: "adff2f",
    grey: "808080",
    honeydew: "f0fff0",
    hotpink: "ff69b4",
    indianred: "cd5c5c",
    indigo: "4b0082",
    ivory: "fffff0",
    khaki: "f0e68c",
    lavender: "e6e6fa",
    lavenderblush: "fff0f5",
    lawngreen: "7cfc00",
    lemonchiffon: "fffacd",
    lightblue: "add8e6",
    lightcoral: "f08080",
    lightcyan: "e0ffff",
    lightgoldenrodyellow: "fafad2",
    lightgray: "d3d3d3",
    lightgreen: "90ee90",
    lightgrey: "d3d3d3",
    lightpink: "ffb6c1",
    lightsalmon: "ffa07a",
    lightseagreen: "20b2aa",
    lightskyblue: "87cefa",
    lightslategray: "789",
    lightslategrey: "789",
    lightsteelblue: "b0c4de",
    lightyellow: "ffffe0",
    lime: "0f0",
    limegreen: "32cd32",
    linen: "faf0e6",
    magenta: "f0f",
    maroon: "800000",
    mediumaquamarine: "66cdaa",
    mediumblue: "0000cd",
    mediumorchid: "ba55d3",
    mediumpurple: "9370db",
    mediumseagreen: "3cb371",
    mediumslateblue: "7b68ee",
    mediumspringgreen: "00fa9a",
    mediumturquoise: "48d1cc",
    mediumvioletred: "c71585",
    midnightblue: "191970",
    mintcream: "f5fffa",
    mistyrose: "ffe4e1",
    moccasin: "ffe4b5",
    navajowhite: "ffdead",
    navy: "000080",
    oldlace: "fdf5e6",
    olive: "808000",
    olivedrab: "6b8e23",
    orange: "ffa500",
    orangered: "ff4500",
    orchid: "da70d6",
    palegoldenrod: "eee8aa",
    palegreen: "98fb98",
    paleturquoise: "afeeee",
    palevioletred: "db7093",
    papayawhip: "ffefd5",
    peachpuff: "ffdab9",
    peru: "cd853f",
    pink: "ffc0cb",
    plum: "dda0dd",
    powderblue: "b0e0e6",
    purple: "800080",
    rebeccapurple: "663399",
    red: "f00",
    rosybrown: "bc8f8f",
    royalblue: "4169e1",
    saddlebrown: "8b4513",
    salmon: "fa8072",
    sandybrown: "f4a460",
    seagreen: "2e8b57",
    seashell: "fff5ee",
    sienna: "a0522d",
    silver: "c0c0c0",
    skyblue: "87ceeb",
    slateblue: "6a5acd",
    slategray: "708090",
    slategrey: "708090",
    snow: "fffafa",
    springgreen: "00ff7f",
    steelblue: "4682b4",
    tan: "d2b48c",
    teal: "008080",
    thistle: "d8bfd8",
    tomato: "ff6347",
    turquoise: "40e0d0",
    violet: "ee82ee",
    wheat: "f5deb3",
    white: "fff",
    whitesmoke: "f5f5f5",
    yellow: "ff0",
    yellowgreen: "9acd32"
};

// Make it easy to access colors via `hexNames[hex]`
var hexNames = tinycolor.hexNames = flip(names);


// Utilities
// ---------

// `{ 'name1': 'val1' }` becomes `{ 'val1': 'name1' }`
function flip(o) {
    var flipped = { };
    for (var i in o) {
        if (o.hasOwnProperty(i)) {
            flipped[o[i]] = i;
        }
    }
    return flipped;
}

// Return a valid alpha value [0,1] with all invalid values being set to 1
function boundAlpha(a) {
    a = parseFloat(a);

    if (isNaN(a) || a < 0 || a > 1) {
        a = 1;
    }

    return a;
}

// Take input from [0, n] and return it as [0, 1]
function bound01(n, max) {
    if (isOnePointZero(n)) { n = "100%"; }

    var processPercent = isPercentage(n);
    n = mathMin(max, mathMax(0, parseFloat(n)));

    // Automatically convert percentage into number
    if (processPercent) {
        n = parseInt(n * max, 10) / 100;
    }

    // Handle floating point rounding errors
    if ((Math.abs(n - max) < 0.000001)) {
        return 1;
    }

    // Convert into [0, 1] range if it isn't already
    return (n % max) / parseFloat(max);
}

// Force a number between 0 and 1
function clamp01(val) {
    return mathMin(1, mathMax(0, val));
}

// Parse a base-16 hex value into a base-10 integer
function parseIntFromHex(val) {
    return parseInt(val, 16);
}

// Need to handle 1.0 as 100%, since once it is a number, there is no difference between it and 1
// <http://stackoverflow.com/questions/7422072/javascript-how-to-detect-number-as-a-decimal-including-1-0>
function isOnePointZero(n) {
    return typeof n == "string" && n.indexOf('.') != -1 && parseFloat(n) === 1;
}

// Check to see if string passed in is a percentage
function isPercentage(n) {
    return typeof n === "string" && n.indexOf('%') != -1;
}

// Force a hex value to have 2 characters
function pad2(c) {
    return c.length == 1 ? '0' + c : '' + c;
}

// Replace a decimal with it's percentage value
function convertToPercentage(n) {
    if (n <= 1) {
        n = (n * 100) + "%";
    }

    return n;
}

// Converts a decimal to a hex value
function convertDecimalToHex(d) {
    return Math.round(parseFloat(d) * 255).toString(16);
}
// Converts a hex value to a decimal
function convertHexToDecimal(h) {
    return (parseIntFromHex(h) / 255);
}

var matchers = (function() {

    // <http://www.w3.org/TR/css3-values/#integers>
    var CSS_INTEGER = "[-\\+]?\\d+%?";

    // <http://www.w3.org/TR/css3-values/#number-value>
    var CSS_NUMBER = "[-\\+]?\\d*\\.\\d+%?";

    // Allow positive/negative integer/number.  Don't capture the either/or, just the entire outcome.
    var CSS_UNIT = "(?:" + CSS_NUMBER + ")|(?:" + CSS_INTEGER + ")";

    // Actual matching.
    // Parentheses and commas are optional, but not required.
    // Whitespace can take the place of commas or opening paren
    var PERMISSIVE_MATCH3 = "[\\s|\\(]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")\\s*\\)?";
    var PERMISSIVE_MATCH4 = "[\\s|\\(]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")\\s*\\)?";

    return {
        CSS_UNIT: new RegExp(CSS_UNIT),
        rgb: new RegExp("rgb" + PERMISSIVE_MATCH3),
        rgba: new RegExp("rgba" + PERMISSIVE_MATCH4),
        hsl: new RegExp("hsl" + PERMISSIVE_MATCH3),
        hsla: new RegExp("hsla" + PERMISSIVE_MATCH4),
        hsv: new RegExp("hsv" + PERMISSIVE_MATCH3),
        hsva: new RegExp("hsva" + PERMISSIVE_MATCH4),
        hex3: /^#?([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
        hex6: /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/,
        hex4: /^#?([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
        hex8: /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/
    };
})();

// `isValidCSSUnit`
// Take in a single string / number and check to see if it looks like a CSS unit
// (see `matchers` above for definition).
function isValidCSSUnit(color) {
    return !!matchers.CSS_UNIT.exec(color);
}

// `stringInputToObject`
// Permissive string parsing.  Take in a number of formats, and output an object
// based on detected format.  Returns `{ r, g, b }` or `{ h, s, l }` or `{ h, s, v}`
function stringInputToObject(color) {

    color = color.replace(trimLeft,'').replace(trimRight, '').toLowerCase();
    var named = false;
    if (names[color]) {
        color = names[color];
        named = true;
    }
    else if (color == 'transparent') {
        return { r: 0, g: 0, b: 0, a: 0, format: "name" };
    }

    // Try to match string input using regular expressions.
    // Keep most of the number bounding out of this function - don't worry about [0,1] or [0,100] or [0,360]
    // Just return an object and let the conversion functions handle that.
    // This way the result will be the same whether the tinycolor is initialized with string or object.
    var match;
    if ((match = matchers.rgb.exec(color))) {
        return { r: match[1], g: match[2], b: match[3] };
    }
    if ((match = matchers.rgba.exec(color))) {
        return { r: match[1], g: match[2], b: match[3], a: match[4] };
    }
    if ((match = matchers.hsl.exec(color))) {
        return { h: match[1], s: match[2], l: match[3] };
    }
    if ((match = matchers.hsla.exec(color))) {
        return { h: match[1], s: match[2], l: match[3], a: match[4] };
    }
    if ((match = matchers.hsv.exec(color))) {
        return { h: match[1], s: match[2], v: match[3] };
    }
    if ((match = matchers.hsva.exec(color))) {
        return { h: match[1], s: match[2], v: match[3], a: match[4] };
    }
    if ((match = matchers.hex8.exec(color))) {
        return {
            r: parseIntFromHex(match[1]),
            g: parseIntFromHex(match[2]),
            b: parseIntFromHex(match[3]),
            a: convertHexToDecimal(match[4]),
            format: named ? "name" : "hex8"
        };
    }
    if ((match = matchers.hex6.exec(color))) {
        return {
            r: parseIntFromHex(match[1]),
            g: parseIntFromHex(match[2]),
            b: parseIntFromHex(match[3]),
            format: named ? "name" : "hex"
        };
    }
    if ((match = matchers.hex4.exec(color))) {
        return {
            r: parseIntFromHex(match[1] + '' + match[1]),
            g: parseIntFromHex(match[2] + '' + match[2]),
            b: parseIntFromHex(match[3] + '' + match[3]),
            a: convertHexToDecimal(match[4] + '' + match[4]),
            format: named ? "name" : "hex8"
        };
    }
    if ((match = matchers.hex3.exec(color))) {
        return {
            r: parseIntFromHex(match[1] + '' + match[1]),
            g: parseIntFromHex(match[2] + '' + match[2]),
            b: parseIntFromHex(match[3] + '' + match[3]),
            format: named ? "name" : "hex"
        };
    }

    return false;
}

function validateWCAG2Parms(parms) {
    // return valid WCAG2 parms for isReadable.
    // If input parms are invalid, return {"level":"AA", "size":"small"}
    var level, size;
    parms = parms || {"level":"AA", "size":"small"};
    level = (parms.level || "AA").toUpperCase();
    size = (parms.size || "small").toLowerCase();
    if (level !== "AA" && level !== "AAA") {
        level = "AA";
    }
    if (size !== "small" && size !== "large") {
        size = "small";
    }
    return {"level":level, "size":size};
}

// Node: Export function
if (module.exports) {
    module.exports = tinycolor;
}
// AMD/requirejs: Define the module
else {
    window.tinycolor = tinycolor;
}

})(Math);
});

/** Used as references for the maximum length and index of an array. */
var MAX_ARRAY_LENGTH = 4294967295,
    MAX_ARRAY_INDEX = MAX_ARRAY_LENGTH - 1;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeFloor = Math.floor,
    nativeMin = Math.min;

/**
 * The base implementation of `_.sortedIndexBy` and `_.sortedLastIndexBy`
 * which invokes `iteratee` for `value` and each element of `array` to compute
 * their sort ranking. The iteratee is invoked with one argument; (value).
 *
 * @private
 * @param {Array} array The sorted array to inspect.
 * @param {*} value The value to evaluate.
 * @param {Function} iteratee The iteratee invoked per element.
 * @param {boolean} [retHighest] Specify returning the highest qualified index.
 * @returns {number} Returns the index at which `value` should be inserted
 *  into `array`.
 */
function baseSortedIndexBy(array, value, iteratee, retHighest) {
  value = iteratee(value);

  var low = 0,
      high = array == null ? 0 : array.length,
      valIsNaN = value !== value,
      valIsNull = value === null,
      valIsSymbol = isSymbol(value),
      valIsUndefined = value === undefined;

  while (low < high) {
    var mid = nativeFloor((low + high) / 2),
        computed = iteratee(array[mid]),
        othIsDefined = computed !== undefined,
        othIsNull = computed === null,
        othIsReflexive = computed === computed,
        othIsSymbol = isSymbol(computed);

    if (valIsNaN) {
      var setLow = retHighest || othIsReflexive;
    } else if (valIsUndefined) {
      setLow = othIsReflexive && (retHighest || othIsDefined);
    } else if (valIsNull) {
      setLow = othIsReflexive && othIsDefined && (retHighest || !othIsNull);
    } else if (valIsSymbol) {
      setLow = othIsReflexive && othIsDefined && !othIsNull && (retHighest || !othIsSymbol);
    } else if (othIsNull || othIsSymbol) {
      setLow = false;
    } else {
      setLow = retHighest ? (computed <= value) : (computed < value);
    }
    if (setLow) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return nativeMin(high, MAX_ARRAY_INDEX);
}

/**
 * This method is like `_.sortedIndex` except that it accepts `iteratee`
 * which is invoked for `value` and each element of `array` to compute their
 * sort ranking. The iteratee is invoked with one argument: (value).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Array
 * @param {Array} array The sorted array to inspect.
 * @param {*} value The value to evaluate.
 * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
 * @returns {number} Returns the index at which `value` should be inserted
 *  into `array`.
 * @example
 *
 * var objects = [{ 'x': 4 }, { 'x': 5 }];
 *
 * _.sortedIndexBy(objects, { 'x': 4 }, function(o) { return o.x; });
 * // => 0
 *
 * // The `_.property` iteratee shorthand.
 * _.sortedIndexBy(objects, { 'x': 4 }, 'x');
 * // => 0
 */
function sortedIndexBy(array, value, iteratee) {
  return baseSortedIndexBy(array, value, baseIteratee(iteratee, 2));
}

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue(object, key, value) {
  if (key == '__proto__' && defineProperty) {
    defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

/** Used for built-in method references. */
var objectProto$c = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$9 = objectProto$c.hasOwnProperty;

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty$9.call(object, key) && eq(objValue, value)) ||
      (value === undefined && !(key in object))) {
    baseAssignValue(object, key, value);
  }
}

/**
 * Copies properties of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy properties from.
 * @param {Array} props The property identifiers to copy.
 * @param {Object} [object={}] The object to copy properties to.
 * @param {Function} [customizer] The function to customize copied values.
 * @returns {Object} Returns `object`.
 */
function copyObject(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});

  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];

    var newValue = customizer
      ? customizer(object[key], source[key], key, object, source)
      : undefined;

    if (newValue === undefined) {
      newValue = source[key];
    }
    if (isNew) {
      baseAssignValue(object, key, newValue);
    } else {
      assignValue(object, key, newValue);
    }
  }
  return object;
}

/**
 * The base implementation of `_.assign` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssign(object, source) {
  return object && copyObject(source, keys(source), object);
}

/**
 * This function is like
 * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * except that it includes inherited enumerable properties.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function nativeKeysIn(object) {
  var result = [];
  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }
  return result;
}

/** Used for built-in method references. */
var objectProto$d = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$a = objectProto$d.hasOwnProperty;

/**
 * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeysIn(object) {
  if (!isObject(object)) {
    return nativeKeysIn(object);
  }
  var isProto = isPrototype(object),
      result = [];

  for (var key in object) {
    if (!(key == 'constructor' && (isProto || !hasOwnProperty$a.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}

/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */
function keysIn$1(object) {
  return isArrayLike(object) ? arrayLikeKeys(object, true) : baseKeysIn(object);
}

/**
 * The base implementation of `_.assignIn` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssignIn(object, source) {
  return object && copyObject(source, keysIn$1(source), object);
}

/** Detect free variable `exports`. */
var freeExports$2 = typeof exports == 'object' && exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule$2 = freeExports$2 && typeof module == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports$2 = freeModule$2 && freeModule$2.exports === freeExports$2;

/** Built-in value references. */
var Buffer$1 = moduleExports$2 ? root.Buffer : undefined,
    allocUnsafe = Buffer$1 ? Buffer$1.allocUnsafe : undefined;

/**
 * Creates a clone of  `buffer`.
 *
 * @private
 * @param {Buffer} buffer The buffer to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Buffer} Returns the cloned buffer.
 */
function cloneBuffer(buffer, isDeep) {
  if (isDeep) {
    return buffer.slice();
  }
  var length = buffer.length,
      result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

  buffer.copy(result);
  return result;
}

/**
 * Copies the values of `source` to `array`.
 *
 * @private
 * @param {Array} source The array to copy values from.
 * @param {Array} [array=[]] The array to copy values to.
 * @returns {Array} Returns `array`.
 */
function copyArray(source, array) {
  var index = -1,
      length = source.length;

  array || (array = Array(length));
  while (++index < length) {
    array[index] = source[index];
  }
  return array;
}

/**
 * Copies own symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbols(source, object) {
  return copyObject(source, getSymbols(source), object);
}

/** Built-in value references. */
var getPrototype = overArg(Object.getPrototypeOf, Object);

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols$1 = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own and inherited enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbolsIn = !nativeGetSymbols$1 ? stubArray : function(object) {
  var result = [];
  while (object) {
    arrayPush(result, getSymbols(object));
    object = getPrototype(object);
  }
  return result;
};

/**
 * Copies own and inherited symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbolsIn(source, object) {
  return copyObject(source, getSymbolsIn(source), object);
}

/**
 * Creates an array of own and inherited enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeysIn(object) {
  return baseGetAllKeys(object, keysIn$1, getSymbolsIn);
}

/** Used for built-in method references. */
var objectProto$e = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$b = objectProto$e.hasOwnProperty;

/**
 * Initializes an array clone.
 *
 * @private
 * @param {Array} array The array to clone.
 * @returns {Array} Returns the initialized clone.
 */
function initCloneArray(array) {
  var length = array.length,
      result = new array.constructor(length);

  // Add properties assigned by `RegExp#exec`.
  if (length && typeof array[0] == 'string' && hasOwnProperty$b.call(array, 'index')) {
    result.index = array.index;
    result.input = array.input;
  }
  return result;
}

/**
 * Creates a clone of `arrayBuffer`.
 *
 * @private
 * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
 * @returns {ArrayBuffer} Returns the cloned array buffer.
 */
function cloneArrayBuffer(arrayBuffer) {
  var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
  new Uint8Array(result).set(new Uint8Array(arrayBuffer));
  return result;
}

/**
 * Creates a clone of `dataView`.
 *
 * @private
 * @param {Object} dataView The data view to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned data view.
 */
function cloneDataView(dataView, isDeep) {
  var buffer = isDeep ? cloneArrayBuffer(dataView.buffer) : dataView.buffer;
  return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
}

/** Used to match `RegExp` flags from their coerced string values. */
var reFlags = /\w*$/;

/**
 * Creates a clone of `regexp`.
 *
 * @private
 * @param {Object} regexp The regexp to clone.
 * @returns {Object} Returns the cloned regexp.
 */
function cloneRegExp(regexp) {
  var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
  result.lastIndex = regexp.lastIndex;
  return result;
}

/** Used to convert symbols to primitives and strings. */
var symbolProto$2 = Symbol ? Symbol.prototype : undefined,
    symbolValueOf$1 = symbolProto$2 ? symbolProto$2.valueOf : undefined;

/**
 * Creates a clone of the `symbol` object.
 *
 * @private
 * @param {Object} symbol The symbol object to clone.
 * @returns {Object} Returns the cloned symbol object.
 */
function cloneSymbol(symbol) {
  return symbolValueOf$1 ? Object(symbolValueOf$1.call(symbol)) : {};
}

/**
 * Creates a clone of `typedArray`.
 *
 * @private
 * @param {Object} typedArray The typed array to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned typed array.
 */
function cloneTypedArray(typedArray, isDeep) {
  var buffer = isDeep ? cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
  return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
}

/** `Object#toString` result references. */
var boolTag$2 = '[object Boolean]',
    dateTag$2 = '[object Date]',
    mapTag$3 = '[object Map]',
    numberTag$2 = '[object Number]',
    regexpTag$2 = '[object RegExp]',
    setTag$3 = '[object Set]',
    stringTag$3 = '[object String]',
    symbolTag$2 = '[object Symbol]';

var arrayBufferTag$2 = '[object ArrayBuffer]',
    dataViewTag$3 = '[object DataView]',
    float32Tag$1 = '[object Float32Array]',
    float64Tag$1 = '[object Float64Array]',
    int8Tag$1 = '[object Int8Array]',
    int16Tag$1 = '[object Int16Array]',
    int32Tag$1 = '[object Int32Array]',
    uint8Tag$1 = '[object Uint8Array]',
    uint8ClampedTag$1 = '[object Uint8ClampedArray]',
    uint16Tag$1 = '[object Uint16Array]',
    uint32Tag$1 = '[object Uint32Array]';

/**
 * Initializes an object clone based on its `toStringTag`.
 *
 * **Note:** This function only supports cloning values with tags of
 * `Boolean`, `Date`, `Error`, `Map`, `Number`, `RegExp`, `Set`, or `String`.
 *
 * @private
 * @param {Object} object The object to clone.
 * @param {string} tag The `toStringTag` of the object to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneByTag(object, tag, isDeep) {
  var Ctor = object.constructor;
  switch (tag) {
    case arrayBufferTag$2:
      return cloneArrayBuffer(object);

    case boolTag$2:
    case dateTag$2:
      return new Ctor(+object);

    case dataViewTag$3:
      return cloneDataView(object, isDeep);

    case float32Tag$1: case float64Tag$1:
    case int8Tag$1: case int16Tag$1: case int32Tag$1:
    case uint8Tag$1: case uint8ClampedTag$1: case uint16Tag$1: case uint32Tag$1:
      return cloneTypedArray(object, isDeep);

    case mapTag$3:
      return new Ctor;

    case numberTag$2:
    case stringTag$3:
      return new Ctor(object);

    case regexpTag$2:
      return cloneRegExp(object);

    case setTag$3:
      return new Ctor;

    case symbolTag$2:
      return cloneSymbol(object);
  }
}

/** Built-in value references. */
var objectCreate = Object.create;

/**
 * The base implementation of `_.create` without support for assigning
 * properties to the created object.
 *
 * @private
 * @param {Object} proto The object to inherit from.
 * @returns {Object} Returns the new object.
 */
var baseCreate = (function() {
  function object() {}
  return function(proto) {
    if (!isObject(proto)) {
      return {};
    }
    if (objectCreate) {
      return objectCreate(proto);
    }
    object.prototype = proto;
    var result = new object;
    object.prototype = undefined;
    return result;
  };
}());

/**
 * Initializes an object clone.
 *
 * @private
 * @param {Object} object The object to clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneObject(object) {
  return (typeof object.constructor == 'function' && !isPrototype(object))
    ? baseCreate(getPrototype(object))
    : {};
}

/** `Object#toString` result references. */
var mapTag$4 = '[object Map]';

/**
 * The base implementation of `_.isMap` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 */
function baseIsMap(value) {
  return isObjectLike(value) && getTag$1(value) == mapTag$4;
}

/* Node.js helper references. */
var nodeIsMap = nodeUtil && nodeUtil.isMap;

/**
 * Checks if `value` is classified as a `Map` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 * @example
 *
 * _.isMap(new Map);
 * // => true
 *
 * _.isMap(new WeakMap);
 * // => false
 */
var isMap = nodeIsMap ? baseUnary(nodeIsMap) : baseIsMap;

/** `Object#toString` result references. */
var setTag$4 = '[object Set]';

/**
 * The base implementation of `_.isSet` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 */
function baseIsSet(value) {
  return isObjectLike(value) && getTag$1(value) == setTag$4;
}

/* Node.js helper references. */
var nodeIsSet = nodeUtil && nodeUtil.isSet;

/**
 * Checks if `value` is classified as a `Set` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 * @example
 *
 * _.isSet(new Set);
 * // => true
 *
 * _.isSet(new WeakSet);
 * // => false
 */
var isSet = nodeIsSet ? baseUnary(nodeIsSet) : baseIsSet;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG = 1,
    CLONE_FLAT_FLAG = 2,
    CLONE_SYMBOLS_FLAG = 4;

/** `Object#toString` result references. */
var argsTag$3 = '[object Arguments]',
    arrayTag$2 = '[object Array]',
    boolTag$3 = '[object Boolean]',
    dateTag$3 = '[object Date]',
    errorTag$2 = '[object Error]',
    funcTag$2 = '[object Function]',
    genTag$1 = '[object GeneratorFunction]',
    mapTag$5 = '[object Map]',
    numberTag$3 = '[object Number]',
    objectTag$3 = '[object Object]',
    regexpTag$3 = '[object RegExp]',
    setTag$5 = '[object Set]',
    stringTag$4 = '[object String]',
    symbolTag$3 = '[object Symbol]',
    weakMapTag$2 = '[object WeakMap]';

var arrayBufferTag$3 = '[object ArrayBuffer]',
    dataViewTag$4 = '[object DataView]',
    float32Tag$2 = '[object Float32Array]',
    float64Tag$2 = '[object Float64Array]',
    int8Tag$2 = '[object Int8Array]',
    int16Tag$2 = '[object Int16Array]',
    int32Tag$2 = '[object Int32Array]',
    uint8Tag$2 = '[object Uint8Array]',
    uint8ClampedTag$2 = '[object Uint8ClampedArray]',
    uint16Tag$2 = '[object Uint16Array]',
    uint32Tag$2 = '[object Uint32Array]';

/** Used to identify `toStringTag` values supported by `_.clone`. */
var cloneableTags = {};
cloneableTags[argsTag$3] = cloneableTags[arrayTag$2] =
cloneableTags[arrayBufferTag$3] = cloneableTags[dataViewTag$4] =
cloneableTags[boolTag$3] = cloneableTags[dateTag$3] =
cloneableTags[float32Tag$2] = cloneableTags[float64Tag$2] =
cloneableTags[int8Tag$2] = cloneableTags[int16Tag$2] =
cloneableTags[int32Tag$2] = cloneableTags[mapTag$5] =
cloneableTags[numberTag$3] = cloneableTags[objectTag$3] =
cloneableTags[regexpTag$3] = cloneableTags[setTag$5] =
cloneableTags[stringTag$4] = cloneableTags[symbolTag$3] =
cloneableTags[uint8Tag$2] = cloneableTags[uint8ClampedTag$2] =
cloneableTags[uint16Tag$2] = cloneableTags[uint32Tag$2] = true;
cloneableTags[errorTag$2] = cloneableTags[funcTag$2] =
cloneableTags[weakMapTag$2] = false;

/**
 * The base implementation of `_.clone` and `_.cloneDeep` which tracks
 * traversed objects.
 *
 * @private
 * @param {*} value The value to clone.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Deep clone
 *  2 - Flatten inherited properties
 *  4 - Clone symbols
 * @param {Function} [customizer] The function to customize cloning.
 * @param {string} [key] The key of `value`.
 * @param {Object} [object] The parent object of `value`.
 * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
 * @returns {*} Returns the cloned value.
 */
function baseClone(value, bitmask, customizer, key, object, stack) {
  var result,
      isDeep = bitmask & CLONE_DEEP_FLAG,
      isFlat = bitmask & CLONE_FLAT_FLAG,
      isFull = bitmask & CLONE_SYMBOLS_FLAG;

  if (customizer) {
    result = object ? customizer(value, key, object, stack) : customizer(value);
  }
  if (result !== undefined) {
    return result;
  }
  if (!isObject(value)) {
    return value;
  }
  var isArr = isArray(value);
  if (isArr) {
    result = initCloneArray(value);
    if (!isDeep) {
      return copyArray(value, result);
    }
  } else {
    var tag = getTag$1(value),
        isFunc = tag == funcTag$2 || tag == genTag$1;

    if (isBuffer(value)) {
      return cloneBuffer(value, isDeep);
    }
    if (tag == objectTag$3 || tag == argsTag$3 || (isFunc && !object)) {
      result = (isFlat || isFunc) ? {} : initCloneObject(value);
      if (!isDeep) {
        return isFlat
          ? copySymbolsIn(value, baseAssignIn(result, value))
          : copySymbols(value, baseAssign(result, value));
      }
    } else {
      if (!cloneableTags[tag]) {
        return object ? value : {};
      }
      result = initCloneByTag(value, tag, isDeep);
    }
  }
  // Check for circular references and return its corresponding clone.
  stack || (stack = new Stack);
  var stacked = stack.get(value);
  if (stacked) {
    return stacked;
  }
  stack.set(value, result);

  if (isSet(value)) {
    value.forEach(function(subValue) {
      result.add(baseClone(subValue, bitmask, customizer, subValue, value, stack));
    });
  } else if (isMap(value)) {
    value.forEach(function(subValue, key) {
      result.set(key, baseClone(subValue, bitmask, customizer, key, value, stack));
    });
  }

  var keysFunc = isFull
    ? (isFlat ? getAllKeysIn : getAllKeys)
    : (isFlat ? keysIn : keys);

  var props = isArr ? undefined : keysFunc(value);
  arrayEach(props || value, function(subValue, key) {
    if (props) {
      key = subValue;
      subValue = value[key];
    }
    // Recursively populate clone (susceptible to call stack limits).
    assignValue(result, key, baseClone(subValue, bitmask, customizer, key, value, stack));
  });
  return result;
}

/** Used to compose bitmasks for cloning. */
var CLONE_SYMBOLS_FLAG$1 = 4;

/**
 * Creates a shallow clone of `value`.
 *
 * **Note:** This method is loosely based on the
 * [structured clone algorithm](https://mdn.io/Structured_clone_algorithm)
 * and supports cloning arrays, array buffers, booleans, date objects, maps,
 * numbers, `Object` objects, regexes, sets, strings, symbols, and typed
 * arrays. The own enumerable properties of `arguments` objects are cloned
 * as plain objects. An empty object is returned for uncloneable values such
 * as error objects, functions, DOM nodes, and WeakMaps.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to clone.
 * @returns {*} Returns the cloned value.
 * @see _.cloneDeep
 * @example
 *
 * var objects = [{ 'a': 1 }, { 'b': 2 }];
 *
 * var shallow = _.clone(objects);
 * console.log(shallow[0] === objects[0]);
 * // => true
 */
function clone(value) {
  return baseClone(value, CLONE_SYMBOLS_FLAG$1);
}

/** Built-in value references. */
var spreadableSymbol = Symbol ? Symbol.isConcatSpreadable : undefined;

/**
 * Checks if `value` is a flattenable `arguments` object or array.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
 */
function isFlattenable(value) {
  return isArray(value) || isArguments(value) ||
    !!(spreadableSymbol && value && value[spreadableSymbol]);
}

/**
 * The base implementation of `_.flatten` with support for restricting flattening.
 *
 * @private
 * @param {Array} array The array to flatten.
 * @param {number} depth The maximum recursion depth.
 * @param {boolean} [predicate=isFlattenable] The function invoked per iteration.
 * @param {boolean} [isStrict] Restrict to values that pass `predicate` checks.
 * @param {Array} [result=[]] The initial result value.
 * @returns {Array} Returns the new flattened array.
 */
function baseFlatten(array, depth, predicate, isStrict, result) {
  var index = -1,
      length = array.length;

  predicate || (predicate = isFlattenable);
  result || (result = []);

  while (++index < length) {
    var value = array[index];
    if (depth > 0 && predicate(value)) {
      if (depth > 1) {
        // Recursively flatten arrays (susceptible to call stack limits).
        baseFlatten(value, depth - 1, predicate, isStrict, result);
      } else {
        arrayPush(result, value);
      }
    } else if (!isStrict) {
      result[result.length] = value;
    }
  }
  return result;
}

/**
 * The base implementation of `_.sortBy` which uses `comparer` to define the
 * sort order of `array` and replaces criteria objects with their corresponding
 * values.
 *
 * @private
 * @param {Array} array The array to sort.
 * @param {Function} comparer The function to define sort order.
 * @returns {Array} Returns `array`.
 */
function baseSortBy(array, comparer) {
  var length = array.length;

  array.sort(comparer);
  while (length--) {
    array[length] = array[length].value;
  }
  return array;
}

/**
 * Compares values to sort them in ascending order.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {number} Returns the sort order indicator for `value`.
 */
function compareAscending(value, other) {
  if (value !== other) {
    var valIsDefined = value !== undefined,
        valIsNull = value === null,
        valIsReflexive = value === value,
        valIsSymbol = isSymbol(value);

    var othIsDefined = other !== undefined,
        othIsNull = other === null,
        othIsReflexive = other === other,
        othIsSymbol = isSymbol(other);

    if ((!othIsNull && !othIsSymbol && !valIsSymbol && value > other) ||
        (valIsSymbol && othIsDefined && othIsReflexive && !othIsNull && !othIsSymbol) ||
        (valIsNull && othIsDefined && othIsReflexive) ||
        (!valIsDefined && othIsReflexive) ||
        !valIsReflexive) {
      return 1;
    }
    if ((!valIsNull && !valIsSymbol && !othIsSymbol && value < other) ||
        (othIsSymbol && valIsDefined && valIsReflexive && !valIsNull && !valIsSymbol) ||
        (othIsNull && valIsDefined && valIsReflexive) ||
        (!othIsDefined && valIsReflexive) ||
        !othIsReflexive) {
      return -1;
    }
  }
  return 0;
}

/**
 * Used by `_.orderBy` to compare multiple properties of a value to another
 * and stable sort them.
 *
 * If `orders` is unspecified, all values are sorted in ascending order. Otherwise,
 * specify an order of "desc" for descending or "asc" for ascending sort order
 * of corresponding values.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {boolean[]|string[]} orders The order to sort by for each property.
 * @returns {number} Returns the sort order indicator for `object`.
 */
function compareMultiple(object, other, orders) {
  var index = -1,
      objCriteria = object.criteria,
      othCriteria = other.criteria,
      length = objCriteria.length,
      ordersLength = orders.length;

  while (++index < length) {
    var result = compareAscending(objCriteria[index], othCriteria[index]);
    if (result) {
      if (index >= ordersLength) {
        return result;
      }
      var order = orders[index];
      return result * (order == 'desc' ? -1 : 1);
    }
  }
  // Fixes an `Array#sort` bug in the JS engine embedded in Adobe applications
  // that causes it, under certain circumstances, to provide the same value for
  // `object` and `other`. See https://github.com/jashkenas/underscore/pull/1247
  // for more details.
  //
  // This also ensures a stable sort in V8 and other engines.
  // See https://bugs.chromium.org/p/v8/issues/detail?id=90 for more details.
  return object.index - other.index;
}

/**
 * The base implementation of `_.orderBy` without param guards.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function[]|Object[]|string[]} iteratees The iteratees to sort by.
 * @param {string[]} orders The sort orders of `iteratees`.
 * @returns {Array} Returns the new sorted array.
 */
function baseOrderBy(collection, iteratees, orders) {
  var index = -1;
  iteratees = arrayMap(iteratees.length ? iteratees : [identity], baseUnary(baseIteratee));

  var result = baseMap(collection, function(value, key, collection) {
    var criteria = arrayMap(iteratees, function(iteratee) {
      return iteratee(value);
    });
    return { 'criteria': criteria, 'index': ++index, 'value': value };
  });

  return baseSortBy(result, function(object, other) {
    return compareMultiple(object, other, orders);
  });
}

/**
 * Checks if the given arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
 *  else `false`.
 */
function isIterateeCall(value, index, object) {
  if (!isObject(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number'
        ? (isArrayLike(object) && isIndex(index, object.length))
        : (type == 'string' && index in object)
      ) {
    return eq(object[index], value);
  }
  return false;
}

/**
 * Creates an array of elements, sorted in ascending order by the results of
 * running each element in a collection thru each iteratee. This method
 * performs a stable sort, that is, it preserves the original sort order of
 * equal elements. The iteratees are invoked with one argument: (value).
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {...(Function|Function[])} [iteratees=[_.identity]]
 *  The iteratees to sort by.
 * @returns {Array} Returns the new sorted array.
 * @example
 *
 * var users = [
 *   { 'user': 'fred',   'age': 48 },
 *   { 'user': 'barney', 'age': 36 },
 *   { 'user': 'fred',   'age': 40 },
 *   { 'user': 'barney', 'age': 34 }
 * ];
 *
 * _.sortBy(users, [function(o) { return o.user; }]);
 * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 40]]
 *
 * _.sortBy(users, ['user', 'age']);
 * // => objects for [['barney', 34], ['barney', 36], ['fred', 40], ['fred', 48]]
 */
var sortBy = baseRest(function(collection, iteratees) {
  if (collection == null) {
    return [];
  }
  var length = iteratees.length;
  if (length > 1 && isIterateeCall(collection, iteratees[0], iteratees[1])) {
    iteratees = [];
  } else if (length > 2 && isIterateeCall(iteratees[0], iteratees[1], iteratees[2])) {
    iteratees = [iteratees[0]];
  }
  return baseOrderBy(collection, baseFlatten(iteratees, 1), []);
});

var CaptionCollection = function(params) {
   var _captions = [];
   var _activeLanguageIdentifier = LanguageIdentifier$1.create(languageIdentifiers.identifiers.default);
   var _captionLanguages = {};

   var addCaptionFromLanguageCodeCache = function(caption) {
      caption.getAvailableLanguages().forEach(function(language) {
         if (!_captionLanguages[language]) {
            _captionLanguages[language] = 1;
         } else {
            _captionLanguages[language]++;
         }
      });
   };

   var removeCaptionFromLanguageCodeCache = function(caption) {
      caption.getAvailableLanguages().forEach(function(language) {
         if (_captionLanguages[language]) {
            _captionLanguages[language]--;
         }

         if (_captionLanguages[language] === 0) {
            delete _captionLanguages[language];
         }
      });
   };

   var addCaption = function(caption) {
      addCaptionFromLanguageCodeCache(caption);
      caption.activeLanguageIdentifier = _activeLanguageIdentifier;
      var index = sortedIndexBy(_captions, caption, 'startTime');
      _captions.splice(index, 0, caption);
      return UndoAction$1.create(removeCaption, caption, addCaption, caption);
   };

   var removeCaption = function(caption) {
      removeCaptionFromLanguageCodeCache(caption);
      var index = findIndex(_captions, caption);
      if (index !== -1) {
         _captions.splice(index, 1);
         return UndoAction$1.create(addCaption, caption, removeCaption, caption);
      }
      return UndoAction$1.noOp();
   };

   var setCaptions = function(captions) {
      _captions = captions;
      _captionLanguages = [];
      _captions.forEach(function(caption) {
         addCaptionFromLanguageCodeCache(caption);
      });
   };

   var mergeCaptions = function(captionsToMerge) {
      captionsToMerge.forEach(function(captionToMerge) {
         var matchingCaption = find(_captions, function(existingCaption) {
            return existingCaption.startTime === captionToMerge.startTime;
         });
         if (matchingCaption) {
            addCaptionFromLanguageCodeCache(captionToMerge);
            captionToMerge.captionTextList.forEach(function(captionText) {
               matchingCaption.addOrReplaceCaptionText(captionText);
            });
         }
      });
   };

   var clear = function() {
      var captionsClone = clone(_captions);
      _captions = [];
      _captionLanguages = [];
      return UndoAction$1.create(setCaptions, captionsClone, setCaptions, []);
   };

   var getAvailableLanguages = function() {
      return Object.keys(_captionLanguages);
   };

   var setActiveLanguageIdentifier = function(languageIdentifierString) {
      if (!languageIdentifiers.isSupportedLanguage(languageIdentifierString)) {
         languageIdentifierString = languageIdentifiers.identifiers.default;
      }
      var languageIdentifier = LanguageIdentifier$1.create(languageIdentifierString);
      var previousActiveLanguageIdentifierString = _activeLanguageIdentifier.identifier;
      _activeLanguageIdentifier = languageIdentifier;
      forEach(_captions, function(caption) {
         caption.activeLanguageIdentifier = languageIdentifier;
      });
      return UndoAction$1.create(setActiveLanguageIdentifier, previousActiveLanguageIdentifierString, setActiveLanguageIdentifier, languageIdentifierString);
   };

   var getActiveLanguageIdentifier = function() {
      return _activeLanguageIdentifier;
   };

   if (params) {
      if (params.hasOwnProperty('captions') && isArray(params.captions)) {
         _captions = sortBy(params.captions, ['startTime']);
         _captions.forEach(function(caption) {
            addCaptionFromLanguageCodeCache(caption);
         });
      } else {
         throw new Error('CaptionCollection params is expecting an object eg. { captions: [caption1Object, ...] }');
      }
   }

   var _public = Object.defineProperties({},
      {
         captions: {
            get: function() {
               return _captions;
            }
         }
      });

   _public.setActiveLanguageIdentifier = setActiveLanguageIdentifier;
   _public.getActiveLanguageIdentifier = getActiveLanguageIdentifier;
   _public.getAvailableLanguages = getAvailableLanguages;
   _public.addCaption = addCaption;
   _public.removeCaption = removeCaption;
   _public.mergeCaptions = mergeCaptions;
   _public.clear = clear;

   _public.toPOJO = function() {
      return {
         captions: _captions
      };
   };

   return _public;
};

/** @module CaptionCollection */
var CaptionCollection$1 = {
   /**
    *  Factory method that returns a new CaptionCollection object.
    *  @function create
    *  @param {Object} params - Optional params for caption
    *  @param {Array} [params.captions] - array containing {module:Caption} objects.
    *  @static
    *  @return {module:CaptionCollection} new CaptionCollection instance
    */
   create: function(params) {
      return CaptionCollection(params);
   }
};

var _defaults = Object.freeze({
   fontFamily: 'Arial, Helvetica, sans-serif;',
   fontSize: 24,
   fgColor: tinycolor({r: 255, g: 255, b: 255}),
   bgColor: tinycolor({r: 0, g: 0, b: 0, a: 0.9})
});

var CaptionTrack = function(params) {
   params = params || {};

   var _languageIdentifier = params.languageIdentifierString ? LanguageIdentifier$1.create(params.languageIdentifierString) : LanguageIdentifier$1.create(languageIdentifiers.identifiers.default);
   var _fgColor = params.fgColor ? tinycolor(params.fgColor) : _defaults.fgColor.clone();
   var _bgColor = params.bgColor ? tinycolor(params.bgColor) : _defaults.bgColor.clone();
   var _fontFamily = params.fontFamily ? params.fontFamily : _defaults.fontFamily;
   var _fontSize = parseFontSize(params, _defaults.fontSize);
   var _position = params.position ? params.position : undefined;
   var _barHeight = params.barHeight ? params.barHeight : undefined;
   var _captionCollection = params.captions ? CaptionCollection$1.create({captions: params.captions}) : CaptionCollection$1.create();
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'xmpDM:trackType': 'Caption',
                  'xmpDM:frameRate': 'f1000',
                  'xmpDM:trackName': 'Captioning',
                  'stFnt:fontFamily': _fontFamily,
                  'tscDM:fontSize': _fontSize,
                  'tscDM:bgOpacity': _bgColor.getAlpha(),
                  'tscDM:position': _position,
                  'tscDM:captionbarheight': _barHeight
               },
               children: [
                  {
                     name: 'xmpDM:markers',
                     children: [
                        {
                           name: 'rdf:Seq',
                           children: _captionCollection.captions
                        }
                     ]
                  },
                  {
                     name: 'tsc:fgColor',
                     atts: attsFromColor(_fgColor.toRgb())
                  },
                  {
                     name: 'tsc:bgColor',
                     atts: attsFromColor(_bgColor.toRgb())
                  }
               ]
            }
         ]
      };

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var attsFromColor = function(color) {
      return {
         'xmpG:red': color.r,
         'xmpG:green': color.g,
         'xmpG:blue': color.b,
         'xmpG:mode': 'RGB'
      };
   };

   var tinycolorToSimpleRgb = function(tinycolorObj) {
      var rgbaColorObj = tinycolorObj.toRgb();
      return {
         r: rgbaColorObj.r,
         g: rgbaColorObj.g,
         b: rgbaColorObj.b
      };
   };

   var _public = Object.defineProperties({},
      {
         languageIdentifier: {
            get: function() {
               return _languageIdentifier;
            }
         },
         fgColor: {
            get: function() {
               return tinycolorToSimpleRgb(_fgColor);
            }
         },
         fgHexColor: {
            get: function() {
               return _fgColor.toHexString();
            }
         },
         bgColor: {
            get: function() {
               return tinycolorToSimpleRgb(_bgColor);
            }
         },
         bgOpacity: {
            get: function() {
               return _bgColor.getAlpha();
            }
         },
         bgHexColor: {
            get: function() {
               return _bgColor.toHexString();
            }
         },
         bgRGBACssColor: {
            get: function() {
               return _bgColor.toRgbString();
            }
         },
         fontFamily: {
            get: function() {
               return _fontFamily;
            }
         },
         fontSize: {
            get: function() {
               return _fontSize;
            }
         },
         position: {
            get: function() {
               return _position;
            }
         },
         barHeight: {
            get: function() {
               return _barHeight;
            }
         },
         captionCollection: {
            get: function() {
               return _captionCollection;
            }
         }
      });

   _public.setActiveLanguageIdentifier = _captionCollection.setActiveLanguageIdentifier;
   _public.getActiveLanguageIdentifier = _captionCollection.getActiveLanguageIdentifier;
   _public.getAvailableLanguages = _captionCollection.getAvailableLanguages;
   _public.toXmp = toXmp;
   _public.toPOJO = function() {
      var pojo = {
         fgColor: _fgColor.toRgb(),
         bgColor: _bgColor.toRgb(),
         bgOpacity: _bgColor.getAlpha(),
         fontFamily: _fontFamily,
         fontSize: _fontSize,
         position: _position,
         barHeight: _barHeight,
         captions: invokeMap(_captionCollection.captions, 'toPOJO')
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      return pojo;
   };

   return _public;
};

var getRGBColor = function(element) {
   return {
      r: Number(element.getAttribute('xmpG:red')),
      g: Number(element.getAttribute('xmpG:green')),
      b: Number(element.getAttribute('xmpG:blue'))
   };
};

var parseFontSize = function(params, defaultFontSize) {
   if (!params.fontSize) {
      return defaultFontSize;
   }

   var parsedFontSize = +params.fontSize;
   if (isString(params.fontSize)) {
      parsedFontSize = +params.fontSize.replace('px', '').replace('pt', '');
   }
   return parsedFontSize ? parsedFontSize : defaultFontSize;
};

var createFromXmpElement$2 = function(captionTrackElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);
   var captionTrack = new CaptionTrack();

   if (captionTrackElement) {
      var fgColorElement = _xmlUtility.getFirstElementByTagNameNSPrefix(captionTrackElement, 'tsc:fgColor');
      var bgColorElement = _xmlUtility.getFirstElementByTagNameNSPrefix(captionTrackElement, 'tsc:bgColor');

      var captionElements = _xmlUtility.getElementsByTagNameNSPrefix(captionTrackElement, 'rdf:Description');
      var captions = map(captionElements, Caption$1.createFromXmpElement);

      var params = {
         fgColor: getRGBColor(fgColorElement),
         bgColor: getRGBColor(bgColorElement),
         fontFamily: captionTrackElement.getAttribute('stFnt:fontFamily'),
         fontSize: Number(captionTrackElement.getAttribute('tscDM:fontSize')),
         position: captionTrackElement.getAttribute('tscDM:position'),
         barHeight: Number(captionTrackElement.getAttribute('tscDM:captionbarheight')),
         captions: captions
      };
      params.bgColor.a = !isNil(captionTrackElement.getAttribute('tscDM:bgOpacity')) ?
         Number(captionTrackElement.getAttribute('tscDM:bgOpacity')) : _defaults.bgColor.getAlpha();

      captionTrack = new CaptionTrack(params);
   }

   return captionTrack;
};

/** @module CaptionTrack */
var CaptionTrack$1 = {
   /**
    *  Factory method that returns a new CaptionTrack object.
    *  @function create
    *  @param {Object} params - Optional params for caption track
    *  @param {String} [params.languageIdentifierString] - Local identifier string for caption track
    *  @param {Object} [params.fgColor] - RGB values for foreground color.
    *  @param {Number} [params.bgOpacity] - Opacity of background.
    *  @param {String} [params.fontFamily] - Font family for caption track.
    *  @param {String} [params.fontSize] - Font size for caption track.
    *  @param {String} [params.position] - Position for caption track.
    *  @param {String} [params.barHeight] - Bar height for caption track.
    *  @static
    *  @return {module:CaptionTrack} new CaptionTrack instance
    */
   create: function(params) {
      return CaptionTrack(params);
   },

   /**
    *  Factory method that returns a new CaptionTrack object from an XMP caption track element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP caption track element.
    *  @static
    *  @return {module:CaptionTrack} new CaptionTrack instance
    */
   createFromXmpElement: createFromXmpElement$2
};

/**
 * Iterates over elements of `collection`, returning an array of all elements
 * `predicate` returns truthy for. The predicate is invoked with three
 * arguments: (value, index|key, collection).
 *
 * **Note:** Unlike `_.remove`, this method returns a new array.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 * @see _.reject
 * @example
 *
 * var users = [
 *   { 'user': 'barney', 'age': 36, 'active': true },
 *   { 'user': 'fred',   'age': 40, 'active': false }
 * ];
 *
 * _.filter(users, function(o) { return !o.active; });
 * // => objects for ['fred']
 *
 * // The `_.matches` iteratee shorthand.
 * _.filter(users, { 'age': 36, 'active': true });
 * // => objects for ['barney']
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.filter(users, ['active', false]);
 * // => objects for ['fred']
 *
 * // The `_.property` iteratee shorthand.
 * _.filter(users, 'active');
 * // => objects for ['barney']
 */
function filter(collection, predicate) {
  var func = isArray(collection) ? arrayFilter : baseFilter;
  return func(collection, baseIteratee(predicate, 3));
}

var CaptionController = function() {
   var _activeLanguageIdentifier = LanguageIdentifier$1.create(languageIdentifiers.identifiers.default);
   var _activeCaptionTrack = null;
   var _captionTracks = [];
   var _captionLanguages = {};

   var storeCaptionLanguage = function(languageIdentifier) {
      _captionLanguages[languageIdentifier.identifier] = languageIdentifier.identifier;
   };

   var removeCaptionLanguage = function(languageIdentifier) {
      if (_captionLanguages[languageIdentifier.identifier]) {
         delete _captionLanguages[languageIdentifier.identifier];
      }
   };

   var getAvailableLanguages = function() {
      return Object.keys(_captionLanguages);
   };

   var cacheActiveCaptionTrack = function() {
      if (_captionTracks.length === 0) {
         _activeCaptionTrack = null;
         return;
      }

      if (_activeLanguageIdentifier.identifier === languageIdentifiers.identifiers.default) {
         _activeCaptionTrack = _captionTracks[0];
      }

      var preferredCaptionTrack = find(_captionTracks, function(captionTrack) {
         return captionTrack.languageIdentifier.identifier === _activeLanguageIdentifier.identifier;
      });

      if (preferredCaptionTrack) {
         _activeCaptionTrack = preferredCaptionTrack;
      } else {
         _activeCaptionTrack = _captionTracks[0];
      }
   };

   var setActiveLanguageIdentifier = function(languageIdentifierString) {
      if (!languageIdentifiers.isSupportedLanguage(languageIdentifierString)) {
         languageIdentifierString = languageIdentifiers.identifiers.default;
      }

      var previousLanguageIdentifierString = _activeLanguageIdentifier.identifier;
      _activeLanguageIdentifier = LanguageIdentifier$1.create(languageIdentifierString);
      cacheActiveCaptionTrack();
      return UndoAction$1.create(setActiveLanguageIdentifier, previousLanguageIdentifierString, setActiveLanguageIdentifier, languageIdentifierString);
   };

   var getActiveLanguageIdentifier = function() {
      return _activeLanguageIdentifier;
   };

   var doesCaptionTrackExistForLanguageIdentifier = function(languageIdentifier) {
      return _captionTracks.some(function(captionTrack) {
         return captionTrack.languageIdentifier.identifier === languageIdentifier.identifier;
      });
   };

   var addCaptionTrack = function(captionTrack) {
      if (doesCaptionTrackExistForLanguageIdentifier(captionTrack.languageIdentifier)) {
         throw new Error('CaptionController::addCaptionTrack - Caption track already exist for the ', captionTrack.languageIdentifier.identifier, ' langauge identifier.');
      }
      storeCaptionLanguage(captionTrack.languageIdentifier);
      _captionTracks.push(captionTrack);
      cacheActiveCaptionTrack();
      return UndoAction$1.create(removeCaptionTrack, captionTrack, addCaptionTrack, captionTrack);
   };

   var removeCaptionTrack = function(captionTrackToRemove) {
      var removedCaptionTracks = remove(_captionTracks, function(captionTrack) {
         return captionTrack.languageIdentifier.identifier === captionTrackToRemove.languageIdentifier.identifier;
      });
      var removedCaptionTrack = removedCaptionTracks.length > 0 ? removedCaptionTracks[0] : undefined;
      if (removedCaptionTrack) {
         removeCaptionLanguage(removedCaptionTrack.languageIdentifier);
         cacheActiveCaptionTrack();
      }
      return UndoAction$1.create(addCaptionTrack, removedCaptionTrack, removeCaptionTrack, captionTrackToRemove);
   };

   var addCaptionTrackFromXmpElement = function(captionTrackElement) {
      var parsedCaptionTrack = CaptionTrack$1.createFromXmpElement(captionTrackElement);

      var availableLanguages = parsedCaptionTrack.getAvailableLanguages();
      if (availableLanguages.length > 1) {
         availableLanguages.forEach(function(language) {
            var captionTrackParams = {
               languageIdentifierString: language,
               fgColor: parsedCaptionTrack.fgColor,
               bgColor: parsedCaptionTrack.bgColor,
               bgOpacity: parsedCaptionTrack.bgOpacity,
               fontFamily: parsedCaptionTrack.fontFamily,
               fontSize: parsedCaptionTrack.fontSize,
               position: parsedCaptionTrack.position,
               barHeight: parsedCaptionTrack.barHeight
            };
            var captionTrack = CaptionTrack$1.create(captionTrackParams);
            parsedCaptionTrack.captionCollection.captions.forEach(function(caption) {
               var filteredCaptionTextList = filter(caption.captionTextList, function(captionText) {
                  return captionText.languageIdentifier.identifier === language;
               });

               var captionParams = {
                  startTime: caption.startTime,
                  duration: caption.duration,
                  captionTextList: filteredCaptionTextList
               };

               if (caption.horizontalAlignment) {
                  captionParams.horizontalAlignment = caption.horizontalAlignment;
               }

               if (caption.verticalAlignment) {
                  captionParams.verticalAlignment = caption.verticalAlignment;
               }

               if (caption.file) {
                  captionParams.file = caption.file;
               }

               if (caption.image) {
                  captionParams.image = caption.image;
               }

               if (caption.imageIndex) {
                  captionParams.imageIndex = caption.imageIndex;
               }

               if (caption.imageOffset) {
                  captionParams.imageOffset = caption.imageOffset;
               }

               if (caption.imageRectangle) {
                  captionParams.imageRectangle = caption.imageRectangle;
               }

               var newCaption = Caption$1.create(captionParams);
               captionTrack.captionCollection.addCaption(newCaption);
            });
            addCaptionTrack(captionTrack);
         });
      } else {
         addCaptionTrack(parsedCaptionTrack);
      }
   };

   var getMergedCaptionTrack = function() {
      var captionTrackParams = {
         languageIdentifierString: _activeCaptionTrack ? _activeCaptionTrack.languageIdentifierString : undefined,
         fgColor: _activeCaptionTrack ? _activeCaptionTrack.fgColor : undefined,
         bgOpacity: _activeCaptionTrack ? _activeCaptionTrack.bgOpacity : undefined,
         fontFamily: _activeCaptionTrack ? _activeCaptionTrack.fontFamily : undefined,
         fontSize: _activeCaptionTrack ? _activeCaptionTrack.fontSize : undefined,
         position: _activeCaptionTrack ? _activeCaptionTrack.position : undefined,
         barHeight: _activeCaptionTrack ? _activeCaptionTrack.barHeight : undefined
      };
      var mergedCaptionTrack = CaptionTrack$1.create(captionTrackParams);
      _captionTracks.forEach(function(captionTrack) {
         mergedCaptionTrack.captionCollection.mergeCaptions(captionTrack.captionCollection.captions);
      });
      return mergedCaptionTrack;
   };

   var toXmp = function() {
      return getMergedCaptionTrack().toXmp();
   };

   var toPOJO = function() {
      return getMergedCaptionTrack().toPOJO();
   };

   var getActiveCaptionTrack = function() {
      return _activeCaptionTrack;
   };

   var clearCaptions = function() {
      _captionTracks = [];
      _captionLanguages = {};
      _activeLanguageIdentifier = LanguageIdentifier$1.create(languageIdentifiers.identifiers.default);
      _activeCaptionTrack = null;
   };

   var activeCaptionTrackIsDefinedWithCaptions = function() {
      return _activeCaptionTrack !== null && _activeCaptionTrack.captionCollection.captions.length > 0;
   };

   var getActiveCaptionsTocThumbnailImage = function() {
      return activeCaptionTrackIsDefinedWithCaptions() ? _activeCaptionTrack.captionCollection.captions[0].image : null;
   };

   var _public = Object.defineProperties({},
      {
         activeCaptionTrack: {
            get: getActiveCaptionTrack
         },
         numberCaptionTracks: {
            get: function() {
               return _captionTracks.length;
            }
         },
         containsCaptions: {
            get: activeCaptionTrackIsDefinedWithCaptions
         },
         captionsContainsTocThumbnails: {
            get: function() {
               return activeCaptionTrackIsDefinedWithCaptions() && _activeCaptionTrack.captionCollection.captions[0].image !== undefined;
            }
         }
      });

   _public.setActiveLanguageIdentifier = setActiveLanguageIdentifier;
   _public.getActiveLanguageIdentifier = getActiveLanguageIdentifier;
   _public.clearCaptions = clearCaptions;
   _public.addCaptionTrackFromXmpElement = addCaptionTrackFromXmpElement;
   _public.addCaptionTrack = addCaptionTrack;
   _public.removeCaptionTrack = removeCaptionTrack;
   _public.getAvailableLanguages = getAvailableLanguages;
   _public.getActiveCaptionsTocThumbnailImage = getActiveCaptionsTocThumbnailImage;
   _public.toXmp = toXmp;
   _public.toPOJO = toPOJO;
   return _public;
};

/** @module CaptionController */
var CaptionController$1 = {
   /**
    *  Factory method that returns a new CaptionController object.
    *  @function create
    *  @static
    *  @return {module:CaptionController} new CaptionController instance
    */
   create: function() {
      return CaptionController();
   }
};

var TableOfContentsItem = function(params) {
   var _name;
   var _startTime;
   var _image;
   var _imageIndex;
   var _imageOffset;
   var _imageRectangle;
   var _file;
   var _additionalParamProperties = {};
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      forEach(keys(params), function(key) {
         if (key === 'name') {
            _name = params.name;
         } else if (key === 'startTime') {
            _startTime = params.startTime;
         } else if (key === 'image') {
            _image = params.image;
         } else if (key === 'imageIndex') {
            _imageIndex = params.imageIndex;
         } else if (key === 'imageOffset') {
            _imageOffset = params.imageOffset;
         } else if (key === 'imageRectangle') {
            _imageRectangle = params.imageRectangle;
         } else if (key === 'file') {
            _file = params.file;
         } else if (!isFunction(_additionalParamProperties[key])) {
            _additionalParamProperties[key] = params[key];
         }
      });
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'xmpDM:name': _name,
                  'xmpDM:startTime': _startTime,
                  'tscDM:image': _image,
                  'tscDM:imageindex': _imageIndex,
                  'tscDM:imageoffset': _imageOffset,
                  'tscDM:imagerect': _imageRectangle ? _imageRectangle.toXmpAtt() : null,
                  'tscDM:file': _file
               }
            }
         ]
      };

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         name: {
            get: function() {
               return _name;
            }
         },
         startTime: {
            get: function() {
               return _startTime;
            }
         },
         image: {
            get: function() {
               return _image;
            }
         },
         imageIndex: {
            get: function() {
               return _imageIndex;
            }
         },
         imageOffset: {
            get: function() {
               return _imageOffset;
            }
         },
         imageRectangle: {
            get: function() {
               return _imageRectangle;
            }
         },
         file: {
            get: function() {
               return _file;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         name: _name,
         startTime: _startTime,
         image: _image,
         imageIndex: _imageIndex,
         imageOffset: _imageOffset,
         imageRectangle: _imageRectangle ? _imageRectangle.toPOJO() : undefined,
         file: _file
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      forEach(keys(_additionalParamProperties), function(key) {
         pojo[key] = _additionalParamProperties[key];
      });
      return pojo;
   };

   return _public;
};

var createFromXmpElement$3 = function(tableOfContentsItemElement) {
   var params = {
      name: tableOfContentsItemElement.getAttribute('xmpDM:name'),
      startTime: Number(tableOfContentsItemElement.getAttribute('xmpDM:startTime'))
   };

   // optional attributes below

   params.image = tableOfContentsItemElement.getAttribute('tscDM:image') || undefined;

   if (tableOfContentsItemElement.getAttribute('tscDM:imageindex')) {
      params.imageIndex = Number(tableOfContentsItemElement.getAttribute('tscDM:imageindex'));
   }

   if (tableOfContentsItemElement.getAttribute('tscDM:imageoffset')) {
      params.imageOffset = Number(tableOfContentsItemElement.getAttribute('tscDM:imageoffset'));
   }

   params.file = tableOfContentsItemElement.getAttribute('tscDM:file') || undefined;

   if (tableOfContentsItemElement.getAttribute('tscDM:imagerect')) {
      params.imageRectangle = ImageRectangle$1.create(tableOfContentsItemElement.getAttribute('tscDM:imagerect'));
   }

   return new TableOfContentsItem(params);
};

/** @module TableOfContentsItem */
var TableOfContentsItem$1 = {
   /**
    *  Factory method that returns a new TableOfContentsItem object.
    *  @function create
    *  @param {Object} params - Required params for table of contents item
    *  @param {String} [params.name] - Table of contents item name.
    *  @param {Number} [params.startTime] - Start time of table of contents item (milliseconds).
    *  @param {String} [params.image] - Table of contents item image.
    *  @param {Number} [params.imageIndex] - Table of contents item image index.
    *  @param {Number} [params.imageOffset] - Table of contents item image offset.
    *  @param {module:ImageRectangle} [params.imageRectangle] - Table of contents item image rectangle.
    *  @param {String} [params.file] - File string associated with toc item.
    *  @static
    *  @return {module:TableOfContentsItem} new TableOfContentsItem instance
    */
   create: function(params) {
      return TableOfContentsItem(params);
   },

   /**
    *  Factory method that returns a new TableOfContentsItem object from an XMP tableOfContentsItem element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP tableOfContentsItem element.
    *  @static
    *  @return {module:TableOfContentsItem} new TableOfContentsItem instance
    */
   createFromXmpElement: createFromXmpElement$3
};

var TableOfContentsTrack = function(params) {
   var _items = [];
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      if (params.hasOwnProperty('items') && isArray(params.items)) {
         _items = params.items;
      } else {
         throw new Error('TableOfContentsTrack params is expecting an object eg. { items: [item1Object, ...] }');
      }
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'xmpDM:trackType': 'TableOfContents',
                  'xmpDM:trackName': 'Table of Contents',
                  'xmpDM:frameRate': 'f1000',
                  'tscDM:image': params ? params.image : undefined,
                  'tscDM:tocCellLayout': params ? params.cellLayout : undefined
               },
               children: [
                  {
                     name: 'xmpDM:markers',
                     children: [
                        {
                           name: 'rdf:Seq',
                           children: _items
                        }
                     ]
                  }
               ]
            }
         ]
      };

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         image: {
            get: function() {
               return params.image;
            }
         },
         cellLayout: {
            get: function() {
               return params.cellLayout;
            }
         },
         items: {
            get: function() {
               return _items;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         image: params.image,
         cellLayout: params.cellLayout,
         items: invokeMap(_items, 'toPOJO')
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      return pojo;
   };

   return _public;
};

var createFromXmpElement$4 = function(tableOfContentsTrackElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var tableOfContentsTrack = new TableOfContentsTrack();

   if (tableOfContentsTrackElement) {
      var tableOfContentsItemElements = _xmlUtility.getElementsByTagNameNSPrefix(tableOfContentsTrackElement, 'rdf:Description');
      var image = tableOfContentsTrackElement.getAttribute('tscDM:image');
      var cellLayout = tableOfContentsTrackElement.getAttribute('tscDM:tocCellLayout');
      var items = map(tableOfContentsItemElements, TableOfContentsItem$1.createFromXmpElement);

      var params = {
         image: image,
         cellLayout: cellLayout,
         items: items
      };

      tableOfContentsTrack = new TableOfContentsTrack(params);
   }

   return tableOfContentsTrack;
};

/** @module TableOfContentsTrack */
var TableOfContentsTrack$1 = {
   /**
    *  Factory method that returns a new TableOfContentsTrack object.
    *  @function create
    *  @param {Object} params - Optional params for table of contents track
    *  @param {module:TableOfContentsItem[]} [params.items] - Array of table of content items.
    *  @static
    *  @return {module:TableOfContentsTrack} new TableOfContentsTrack instance
    */
   create: function(params) {
      return TableOfContentsTrack(params);
   },

   /**
    *  Factory method that returns a new TableOfContentsTrack object from an XMP table of contents track element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP table of contents track element.
    *  @static
    *  @return {module:TableOfContentsTrack} new TableOfContentsTrack instance
    */
   createFromXmpElement: createFromXmpElement$4
};

/**
 * Creates an array with all falsey values removed. The values `false`, `null`,
 * `0`, `""`, `undefined`, and `NaN` are falsey.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to compact.
 * @returns {Array} Returns the new array of filtered values.
 * @example
 *
 * _.compact([0, 1, false, 2, '', 3]);
 * // => [1, 2, 3]
 */
function compact(array) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (value) {
      result[resIndex++] = value;
    }
  }
  return result;
}

var BoundingPoly = function(coordinatesString) {
   var _coordinates = [];

   var createCoordinate = function(coordinateString) {
      var tokens = coordinateString.split(',');
      return {
         x: Number(tokens[0]),
         y: Number(tokens[1])
      };
   };

   if (coordinatesString && isString(coordinatesString)) {
      var coordinateTokens = compact(coordinatesString.split(';'));
      _coordinates = map(coordinateTokens, createCoordinate);
   } else {
      throw new Error('BoundingPoly is expecting a coordinate string eg. "0,94;111,90;112,191;0,194;"');
   }

   var _public = Object.defineProperties({},
      {
         coordinates: {
            get: function() {
               return _coordinates;
            }
         }
      });

   _public.toPOJO = function() {
      return {
         coordinates: _coordinates
      };
   };

   _public.toXmpAtt = function() {
      return map(_coordinates, function(coord) {
         return coord.x + ',' + coord.y;
      }).join(';') + ';';
   };

   return _public;
};

/** @module BoundingPoly */
var BoundingPoly$1 = {
   /**
    *  Factory method that returns a new BoundingPoly object.
    *  @function create
    *  @param {String} coordinatesString - Required coordinate string of the bounding poly eg. "0,94;111,90;112,191;0,194;"
    *  @static
    *  @return {module:BoundingPoly} new BoundingPoly instance
    */
   create: function(coordinatesString) {
      return BoundingPoly(coordinatesString);
   }
};

var Hotspot = function(params) {
   var _label;
   var _startTime;
   var _duration;
   var _boundingPoly;
   var _rotate;
   var _pause;
   var _jumpTime;
   var _location;
   var _newWindow;
   var _jumpFile;
   var _file;
   var _additionalParamProperties = {};
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      forEach(keys(params), function(key) {
         if (key === 'label') {
            _label = params.label;
         } else if (key === 'startTime') {
            _startTime = params.startTime;
         } else if (key === 'duration') {
            _duration = params.duration;
         } else if (key === 'boundingPoly') {
            _boundingPoly = params.boundingPoly;
         } else if (key === 'rotate') {
            _rotate = params.rotate;
         } else if (key === 'pause') {
            _pause = params.pause;
         } else if (key === 'jumpTime') {
            _jumpTime = params.jumpTime;
         } else if (key === 'location') {
            _location = params.location;
         } else if (key === 'newWindow') {
            _newWindow = params.newWindow;
         } else if (key === 'jumpFile') {
            _jumpFile = params.jumpFile;
         } else if (key === 'file') {
            _file = params.file;
         } else if (!isFunction(_additionalParamProperties[key])) {
            _additionalParamProperties[key] = params[key];
         }
      });
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'xmpDM:label': _label,
                  'xmpDM:duration': _duration,
                  'xmpDM:startTime': _startTime,
                  'tscDM:boundingPoly': _boundingPoly ? _boundingPoly.toXmpAtt() : undefined,
                  'tscDM:rotate': _rotate,
                  'tscHS:pause': _pause ? '1' : '0',
                  'tscHS:jumpTime': _jumpTime,
                  'tscHS:jumpFile': _jumpFile,
                  'xmpDM:location': _location,
                  'tscDM:file': _file,
                  'tscHS:newWindow': _newWindow ? '1' : '0'
               }
            }
         ]
      };

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         label: {
            get: function() {
               return _label;
            }
         },
         startTime: {
            get: function() {
               return _startTime;
            }
         },
         endTime: {
            get: function() {
               return _startTime + _duration;
            }
         },
         duration: {
            get: function() {
               return _duration;
            }
         },
         boundingPoly: {
            get: function() {
               return _boundingPoly;
            }
         },
         rotate: {
            get: function() {
               return _rotate;
            }
         },
         pause: {
            get: function() {
               return _pause;
            }
         },
         jumpTime: {
            get: function() {
               return _jumpTime;
            }
         },
         location: {
            get: function() {
               return _location;
            }
         },
         newWindow: {
            get: function() {
               return _newWindow;
            }
         },
         jumpFile: {
            get: function() {
               return _jumpFile;
            }
         },
         file: {
            get: function() {
               return _file;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         label: _label,
         startTime: _startTime,
         duration: _duration,
         boundingPoly: _boundingPoly,
         rotate: _rotate,
         pause: _pause,
         jumpTime: _jumpTime,
         location: _location,
         newWindow: _newWindow,
         jumpFile: _jumpFile,
         file: _file
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      forEach(keys(_additionalParamProperties), function(key) {
         pojo[key] = _additionalParamProperties[key];
      });
      return pojo;
   };

   return _public;
};

var createFromXmpElement$5 = function(hotspotElement) {
   var params = {
      label: hotspotElement.getAttribute('xmp:label'),
      startTime: Number(hotspotElement.getAttribute('xmpDM:startTime')),
      duration: Number(hotspotElement.getAttribute('xmpDM:duration')),
      boundingPoly: BoundingPoly$1.create(hotspotElement.getAttribute('tscDM:boundingPoly')),
      rotate: Number(hotspotElement.getAttribute('tscDM:rotate')),
      pause: hotspotElement.getAttribute('tscHS:pause') === '1',
      newWindow: hotspotElement.getAttribute('tscHS:newWindow') === '1'
   };

   if (hotspotElement.getAttribute('tscHS:jumpTime')) {
      params.jumpTime = Number(hotspotElement.getAttribute('tscHS:jumpTime'));
   }

   params.jumpFile = hotspotElement.getAttribute('tscHS:jumpFile') || undefined;

   params.location = hotspotElement.getAttribute('xmpDM:location') || undefined;

   params.file = hotspotElement.getAttribute('tscDM:file') || undefined;

   return new Hotspot(params);
};

/** @module Hotspot */
var Hotspot$1 = {
   /**
    *  Factory method that returns a new Hotspot object.
    *  @function create
    *  @param {Object} params - Required params for hotspot
    *  @param {String} [params.label] - Hotspot label.
    *  @param {Number} [params.startTime] - Start time of hotspot (milliseconds).
    *  @param {Number} [params.duration] - Duration of hotspot (milliseconds).
    *  @param {module:BoundingPoly} [params.boundingPoly] - Bounding polygon of hotspot.
    *  @param {Number} [params.rotate] - Rotation value of polygon.
    *  @param {Boolean} [params.pause] - Boolean if hotspot should pause at end.
    *  @param {Number} [params.jumpTime] - Specific time within video that hotspot will jump to (milliseconds)
    *  @param {Number} [params.jumpTime] - Specific time within video that hotspot will jump to (milliseconds)
    *  @param {String} [params.location] - URL to launch for hotspot.
    *  @param {Boolean} [params.newWindow] - True if the hotspot location should open in a new window.
    *  @param {String} [params.jumpFile] - Url to file.
    *  @param {String} [params.file] - Url to file.
    *  @static
    *  @return {module:Hotspot} new Hotspot instance
    */
   create: function(params) {
      return Hotspot(params);
   },

   /**
    *  Factory method that returns a new Hotspot object from an XMP hotspot element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP hotspot element.
    *  @static
    *  @return {module:Hotspot} new Hotspot instance
    */
   createFromXmpElement: createFromXmpElement$5
};

var HotspotTrack = function(params) {
   var _hotspots = [];
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      if (params.hasOwnProperty('hotspots') && isArray(params.hotspots)) {
         _hotspots = params.hotspots;
      } else {
         throw new Error('HotspotTrack params is expecting an object eg. { hotspots: [hotspot1Object, ...] }');
      }
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'xmpDM:trackType': 'Hotspot',
                  'xmpDM:trackName': 'Hotspots',
                  'xmpDM:frameRate': 'f1000'
               },
               children: [
                  {
                     name: 'xmpDM:markers',
                     children: [
                        {
                           name: 'rdf:Seq',
                           children: _hotspots
                        }
                     ]
                  }
               ]
            }
         ]
      };

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         hotspots: {
            get: function() {
               return _hotspots;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         hotspots: invokeMap(_hotspots, 'toPOJO')
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      return pojo;
   };

   return _public;
};

var createFromXmpElement$6 = function(hotspotTrackElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var hotspotTrack = new HotspotTrack();

   if (hotspotTrackElement) {
      var hotspotElements = _xmlUtility.getElementsByTagNameNSPrefix(hotspotTrackElement, 'rdf:Description');
      var hotspots = map(hotspotElements, Hotspot$1.createFromXmpElement);

      var params = {
         hotspots: hotspots
      };

      hotspotTrack = new HotspotTrack(params);
   }

   return hotspotTrack;
};

/** @module HotspotTrack */
var HotspotTrack$1 = {
   /**
    *  Factory method that returns a new HotspotTrack object.
    *  @function create
    *  @param {Object} params - Optional params for caption track
    *  @param {module:Hotspot[]} [params.hotspots] - Array of hotspots.
    *  @static
    *  @return {module:HotspotTrack} new HotspotTrack instance
    */
   create: function(params) {
      return HotspotTrack(params);
   },

   /**
    *  Factory method that returns a new HotspotTrack object from an XMP hotspot track element.
    *  @function create
    *  @param {Object} element - XMP hotspot track element.
    *  @static
    *  @return {module:HotspotTrack} new HotspotTrack instance
    */
   createFromXmpElement: createFromXmpElement$6
};

var Answer = function(params) {
   var _orderId;
   var _text;
   var _additionalParamProperties = {};
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      forEach(keys(params), function(key) {
         if (key === 'orderId') {
            _orderId = params.orderId;
         } else if (key === 'text') {
            _text = params.text;
         } else if (!isFunction(_additionalParamProperties[key])) {
            _additionalParamProperties[key] = params[key];
         }
      });
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'tscIQ:orderId': _orderId
               },
               children: [
                  {
                     name: 'tscIQ:answer',
                     text: _text
                  }
               ]
            }
         ]
      };
      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         orderId: {
            get: function() {
               return _orderId;
            }
         },
         text: {
            get: function() {
               return _text;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         orderId: _orderId,
         text: _text
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      forEach(keys(_additionalParamProperties), function(key) {
         pojo[key] = _additionalParamProperties[key];
      });
      return pojo;
   };

   return _public;
};

var createFromXmpElement$7 = function(answerElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var answerText = _xmlUtility.getFirstChildElementTextByTagNameNSPrefix(answerElement, 'tscIQ:answer');
   var params = {
      orderId: Number(answerElement.getAttribute('tscIQ:orderId')),
      text: answerText
   };
   return new Answer(params);
};

/** @module Answer */
var Answer$1 = {
   /**
    *  Factory method that returns a new Answer object.
    *  @function create
    *  @param {Object} params - Required params for answer
    *  @param {Number} [params.orderId] - Answer order id.
    *  @param {String} [params.text] - Answer text.
    *  @static
    *  @return {module:Answer} new Answer instance
    */
   create: function(params) {
      return Answer(params);
   },

   /**
    *  Factory method that returns a new Answer object from an XMP answer element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP answer element.
    *  @static
    *  @return {module:Answer} new Answer instance
    */
   createFromXmpElement: createFromXmpElement$7
};

var Feedback = function(params) {
   var _reason;
   var _jumpToTime;
   var _location;
   var _text;
   var _plainText;
   var _markupText;
   var _additionalParamProperties = {};
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      forEach(keys(params), function(key) {
         if (key === 'reason') {
            _reason = params.reason;
         } else if (key === 'text') {
            _text = params.text;
            _plainText = Rtf.toText(_text);
            _markupText = Rtf.toHtml(_text);
         } else if (key === 'jumpToTime') {
            _jumpToTime = params.jumpToTime;
         } else if (key === 'location') {
            _location = params.location;
         } else if (!isFunction(_additionalParamProperties[key])) {
            _additionalParamProperties[key] = params[key];
         }
      });
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'tscIQ:reason': _reason,
                  'tscIQ:jumpToTime': _jumpToTime,
                  'tscIQ:location': _location
               },
               children: [
                  {
                     name: 'xmpDM:name',
                     children: [
                        {
                           name: 'rdf:Alt',
                           // todo: according to the latest branch this is now a language collection of strings
                           // for now i hardcoded a single english version to match the expected output
                           children: [
                              {
                                 name: 'rdf:li',
                                 atts: {
                                    'xml:lang': 'en-US'
                                 },
                                 text: _text
                              }
                           ]
                        }
                     ]
                  }
               ]
            }
         ]
      };
      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         reason: {
            get: function() {
               return _reason;
            }
         },
         jumpToTime: {
            get: function() {
               return _jumpToTime;
            }
         },
         location: {
            get: function() {
               return _location;
            }
         },
         text: {
            get: function() {
               return _text;
            }
         },
         plainText: {
            get: function() {
               return _plainText;
            }
         },
         markupText: {
            get: function() {
               return _markupText;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         reason: _reason,
         jumpToTime: _jumpToTime,
         location: _location,
         text: _text,
         plainText: _plainText,
         markupText: _markupText
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      forEach(keys(_additionalParamProperties), function(key) {
         pojo[key] = _additionalParamProperties[key];
      });
      return pojo;
   };

   return _public;
};

var createFromXmpElement$8 = function(feedbackElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var feedbackText = _xmlUtility.getFirstChildElementTextByTagNameNSPrefix(feedbackElement, 'rdf:li');
   var params = {
      reason: feedbackElement.getAttribute('tscIQ:reason'),
      text: feedbackText
   };

   if (feedbackElement.getAttribute('tscIQ:jumpToTime')) {
      params.jumpToTime = Number(feedbackElement.getAttribute('tscIQ:jumpToTime'));
   }

   params.location = feedbackElement.getAttribute('tscIQ:location') || undefined;

   return Feedback(params);
};

/** @module Feedback */
var Feedback$1 = {
   /**
    *  Factory method that returns a new Feedback object.
    *  @function create
    *  @param {Object} params - Required params for feedback
    *  @param {String} [params.reason] - Feedback reason type.
    *  @param {String} [params.text] - Feedback text.
    *  @param {Number} [params.jumpToTime] - Feedback jumpToTime.
    *  @param {String} [params.location] - Feedback location.
    *  @static
    *  @return {module:Feedback} new Feedback instance
    */
   create: function(params) {
      return Feedback(params);
   },

   /**
    *  Factory method that returns a new Feedback object from an XMP feedback element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP feedback element.
    *  @static
    *  @return {module:Feedback} new Feedback instance
    */
   createFromXmpElement: createFromXmpElement$8
};

var Question = function(params) {
   var _id;
   var _type;
   var _text;
   var _correctAnswer;
   var _answers = [];
   var _feedback = [];
   var _additionalParamProperties = {};
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      forEach(keys(params), function(key) {
         if (key === 'id') {
            _id = params.id;
         } else if (key === 'type') {
            _type = params.type;
         } else if (key === 'text') {
            _text = params.text;
         } else if (key === 'correctAnswer') {
            _correctAnswer = params.correctAnswer;
         } else if (key === 'answers') {
            _answers = params.answers;
         } else if (key === 'feedback') {
            _feedback = params.feedback;
         } else if (!isFunction(_additionalParamProperties[key])) {
            _additionalParamProperties[key] = params[key];
         }
      });
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'tscIQ:id': _id,
                  'tscIQ:type': _type
               },
               children: [
                  {
                     name: 'tscIQ:question',
                     text: _text
                  },
                  {
                     name: 'tscIQ:correctAnswer',
                     text: _type === 'MC' ? _correctAnswer : null
                  },
                  {
                     name: 'tscIQ:answerArray',
                     children: [
                        {
                           name: 'rdf:Seq',
                           children: _answers
                        }
                     ]
                  },
                  {
                     name: 'tscIQ:feedback',
                     children: [
                        {
                           name: 'rdf:Bag',
                           children: _feedback
                        }
                     ]
                  }
               ]
            }
         ]
      };
      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         id: {
            get: function() {
               return _id;
            }
         },
         type: {
            get: function() {
               return _type;
            }
         },
         text: {
            get: function() {
               return _text;
            }
         },
         correctAnswer: {
            get: function() {
               return _correctAnswer;
            }
         },
         answers: {
            get: function() {
               return _answers;
            }
         },
         feedback: {
            get: function() {
               return _feedback;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         id: _id,
         type: _type,
         text: _text,
         correctAnswer: _correctAnswer,
         answers: invokeMap(_answers, 'toPOJO'),
         feedback: _feedback !== undefined ? invokeMap(_feedback, 'toPOJO') : undefined
      };

      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      forEach(keys(_additionalParamProperties), function(key) {
         pojo[key] = _additionalParamProperties[key];
      });
      return pojo;
   };

   return _public;
};

var createFromXmpElement$9 = function(questionElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var text = _xmlUtility.getFirstChildElementTextByTagNameNSPrefix(questionElement, 'tscIQ:question');
   var correctAnswerText = _xmlUtility.getFirstChildElementTextByTagNameNSPrefix(questionElement, 'tscIQ:correctAnswer');

   var descriptionElements = _xmlUtility.getElementsByTagNameNSPrefix(questionElement, 'rdf:Description');
   var answerElements = filter(descriptionElements, function(element) {
      return _xmlUtility.hasAttribute(element, 'tscIQ:orderId');
   });
   var answers = map(answerElements, Answer$1.createFromXmpElement);

   var feedbackElements = filter(descriptionElements, function(element) {
      return _xmlUtility.hasAttribute(element, 'tscIQ:reason');
   });

   var feedback = map(feedbackElements, Feedback$1.createFromXmpElement);

   var params = {
      id: Number(questionElement.getAttribute('tscIQ:id')),
      type: questionElement.getAttribute('tscIQ:type'),
      text: text,
      correctAnswer: correctAnswerText ? Number(correctAnswerText) : undefined,
      answers: answers,
      feedback: feedback
   };

   return Question(params);
};

/** @module Question */
var Question$1 = {
   /**
    *  Factory method that returns a new Question object.
    *  @function create
    *  @param {Object} params - Required params for question
    *  @param {Number} [params.id] - Question id.
    *  @param {String} [params.text] - Question text.
    *  @param {Number} [params.correctAnswer] - Index of the correct answer within answer array (1 based).
    *  @param {module:Answer[]} [params.answers] - Question answer array.
    *  @param {module:Feedback[]} [params.feedback] - Question feedback array.
    *  @static
    *  @return {module:Question} new Question instance
    */
   create: function(params) {
      return Question(params);
   },

   /**
    *  Factory method that returns a new Question object from an XMP question element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP question element.
    *  @static
    *  @return {module:Question} new Question instance
    */
   createFromXmpElement: createFromXmpElement$9
};

var QuestionSet = function(params) {
   var _name;
   var _feedback;
   var _startTime;
   var _questions;
   var _file;
   var _additionalParamProperties = {};
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      forEach(keys(params), function(key) {
         if (key === 'name') {
            _name = params.name;
         } else if (key === 'feedback') {
            _feedback = params.feedback;
         } else if (key === 'startTime') {
            _startTime = params.startTime;
         } else if (key === 'file') {
            _file = params.file;
         } else if (key === 'questions') {
            _questions = params.questions;
         } else if (!isFunction(_additionalParamProperties[key])) {
            _additionalParamProperties[key] = params[key];
         }
      });
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'tscIQ:questionSetName': _name,
                  'tscIQ:feedback': _feedback ? '1' : '0',
                  'tscDM:file': _file,
                  'xmpDM:startTime': _startTime
               },
               children: [
                  {
                     name: 'tscIQ:questions',
                     children: [
                        {
                           name: 'rdf:Seq',
                           children: _questions
                        }
                     ]
                  }
               ]
            }
         ]
      };
      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         name: {
            get: function() {
               return _name;
            }
         },
         feedback: {
            get: function() {
               return _feedback;
            }
         },
         startTime: {
            get: function() {
               return _startTime;
            }
         },
         file: {
            get: function() {
               return _file;
            }
         },
         questions: {
            get: function() {
               return _questions;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         name: _name,
         feedback: _feedback,
         startTime: _startTime,
         file: _file,
         questions: invokeMap(_questions, 'toPOJO')
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      forEach(keys(_additionalParamProperties), function(key) {
         pojo[key] = _additionalParamProperties[key];
      });
      return pojo;
   };

   return _public;
};

var createFromXmpElement$a = function(questionSetElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var descriptionElements = _xmlUtility.getElementsByTagNameNSPrefix(questionSetElement, 'rdf:Description');
   var questionElements = filter(descriptionElements, function(element) {
      return _xmlUtility.hasAttribute(element, 'tscIQ:type');
   });

   var questions = map(questionElements, Question$1.createFromXmpElement);
   var params = {
      name: questionSetElement.getAttribute('tscIQ:questionSetName'),
      feedback: questionSetElement.getAttribute('tscIQ:feedback') === '1',
      startTime: Number(questionSetElement.getAttribute('xmpDM:startTime')),
      questions: questions
   };

   params.file = questionSetElement.getAttribute('tscDM:file') || undefined;

   return new QuestionSet(params);
};

/** @module QuestionSet */
var QuestionSet$1 = {
   /**
    *  Factory method that returns a new QuestionSet object.
    *  @function create
    *  @param {Object} params - Required params for question set
    *  @param {String} [params.questionsSetName] - Question set name.
    *  @param {Boolean} [params.feedback] - Question set feedback.
    *  @param {Number} [params.startTime] - Question set start time (milliseconds)
    *  @param {String} [params.file] - Associated file
    *  @param {module:Question[]} [params.questions] - Question set questions.
    *  @static
    *  @return {module:QuestionSet} new QuestionSet instance
    */
   create: function(params) {
      return QuestionSet(params);
   },

   /**
    *  Factory method that returns a new QuestionSet object from an XMP questionSet element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP questionSet element.
    *  @static
    *  @return {module:QuestionSet} new QuestionSet instance
    */
   createFromXmpElement: createFromXmpElement$a
};

var QuizTrack = function(params) {
   var _quizGuid;
   var _authoredEmail;
   var _requireUserId;
   var _locale;
   var _reportMethod;
   var _clientId;
   var _quizHash;
   var _hideReplay;
   var _allowSkipQuiz;
   var _questionSets = [];
   var _parameters;
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      forEach(keys(params), function(key) {
         if (key === 'quizGuid') {
            _quizGuid = params.quizGuid;
         } else if (key === 'authoredEmail') {
            _authoredEmail = params.authoredEmail;
         } else if (key === 'requireUserId') {
            _requireUserId = params.requireUserId;
         } else if (key === 'locale') {
            _locale = params.locale;
         } else if (key === 'reportMethod') {
            _reportMethod = params.reportMethod;
         } else if (key === 'clientId') {
            _clientId = params.clientId;
         } else if (key === 'quizHash') {
            _quizHash = params.quizHash;
         } else if (key === 'hideReplay') {
            _hideReplay = params.hideReplay;
         } else if (key === 'allowSkipQuiz') {
            _allowSkipQuiz = params.allowSkipQuiz;
         } else if (key === 'parameters' && isArray(params.parameters)) {
            _parameters = params.parameters;
         } else if (key === 'questionSets' && isArray(params.questionSets)) {
            _questionSets = params.questionSets;
         }
      });
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'xmpDM:trackType': 'Quiz',
                  'xmpDM:trackName': 'Quiz',
                  'xmpDM:frameRate': 'f1000',
                  'tscIQ:clientId': _clientId,
                  'tscIQ:quizHash': _quizHash,
                  'tscIQ:quizGuid': _quizGuid,
                  'tscIQ:authoredEmail': _authoredEmail,
                  'tscIQ:requireUserId': _requireUserId ? '1' : '0',
                  'tscIQ:locale': _locale,
                  'tscIQ:reportMethod': _reportMethod,
                  'tscIQ:hideReplay': _hideReplay ? '1' : '0',
                  'tscIQ:allowSkipQuiz': _allowSkipQuiz ? '1' : '0'
               },
               children: [
                  {
                     name: 'xmpDM:markers',
                     children: [
                        {
                           name: 'rdf:Seq',
                           children: _questionSets
                        }
                     ]
                  },
                  {
                     name: 'tscIQ:QuizParams',
                     children: [
                        {
                           name: 'rdf:Bag',
                           children: map(_parameters, function(param) {
                              return {
                                 name: 'rdf:li',
                                 atts: {
                                    'xmpDM:name': param.name,
                                    'xmpDM:value': param.value
                                 }
                              };
                           })
                        }
                     ]
                  }
               ]
            }
         ]
      };

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         quizGuid: {
            get: function() {
               return _quizGuid;
            }
         },
         authoredEmail: {
            get: function() {
               return _authoredEmail;
            }
         },
         requireUserId: {
            get: function() {
               return _requireUserId;
            }
         },
         locale: {
            get: function() {
               return _locale;
            }
         },
         reportMethod: {
            get: function() {
               return _reportMethod;
            }
         },
         clientId: {
            get: function() {
               return _clientId;
            }
         },
         quizHash: {
            get: function() {
               return _quizHash;
            }
         },
         hideReplay: {
            get: function() {
               return _hideReplay;
            }
         },
         allowSkipQuiz: {
            get: function() {
               return _allowSkipQuiz;
            }
         },
         parameters: {
            get: function() {
               return _parameters;
            }
         },
         questionSets: {
            get: function() {
               return _questionSets;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         quizGuid: _quizGuid,
         authoredEmail: _authoredEmail,
         requireUserId: _requireUserId,
         locale: _locale,
         reportMethod: _reportMethod,
         clientId: _clientId,
         quizHash: _quizHash,
         hideReplay: _hideReplay,
         allowSkipQuiz: _allowSkipQuiz,
         questionSets: invokeMap(_questionSets, 'toPOJO'),
         parameters: _parameters || undefined
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      return pojo;
   };

   return _public;
};

var parseParameter = function(parameterElement) {
   return {
      name: parameterElement.getAttribute('xmpDM:name'),
      value: parameterElement.getAttribute('xmpDM:value')
   };
};

var parseParameters = function(quizParametersElement, xmlUtility) {
   if (quizParametersElement) {
      var parameterElements = xmlUtility.getElementsByTagNameNSPrefix(quizParametersElement, 'rdf:li');
      var parameters = map(parameterElements, parseParameter);
      return parameters;
   }

   return undefined;
};

var createFromXmpElement$b = function(quizTrackElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var quizTrack = new QuizTrack();

   if (quizTrackElement) {
      var descriptionElements = _xmlUtility.getElementsByTagNameNSPrefix(quizTrackElement, 'rdf:Description');
      var questionSetElements = filter(descriptionElements, function(element) {
         return _xmlUtility.hasAttribute(element, 'tscIQ:questionSetName');
      });
      var questionSets = map(questionSetElements, QuestionSet$1.createFromXmpElement);

      var quizParametersElement = _xmlUtility.getFirstElementByTagNameNSPrefix(quizTrackElement, 'tscIQ:QuizParams');

      var params = {
         quizGuid: quizTrackElement.getAttribute('tscIQ:quizGuid'),
         authoredEmail: quizTrackElement.getAttribute('tscIQ:authoredEmail'),
         requireUserId: quizTrackElement.getAttribute('tscIQ:requireUserId') === '1',
         locale: quizTrackElement.getAttribute('tscIQ:locale'),
         reportMethod: quizTrackElement.getAttribute('tscIQ:reportMethod'),
         clientId: quizTrackElement.getAttribute('tscIQ:clientId'),
         quizHash: quizTrackElement.getAttribute('tscIQ:quizHash'),
         hideReplay: quizTrackElement.getAttribute('tscIQ:hideReplay') === '1',
         allowSkipQuiz: quizTrackElement.getAttribute('tscIQ:allowSkipQuiz') !== '0',
         parameters: parseParameters(quizParametersElement, _xmlUtility),
         questionSets: questionSets
      };

      quizTrack = new QuizTrack(params);
   }

   return quizTrack;
};

/** @module QuizTrack */
var QuizTrack$1 = {
   /**
    *  Factory method that returns a new QuizTrack object.
    *  @function create
    *  @param {Object} params - Optional params for caption track
    *  @param {module:QuestionSet[]} [params.questionSets] - Array of question sets.
    *  @static
    *  @return {module:QuizTrack} new QuizTrack instance
    */
   create: function(params) {
      return QuizTrack(params);
   },

   /**
    *  Factory method that returns a new QuizTrack object from an XMP quiz track element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP quiz track element.
    *  @static
    *  @return {module:QuizTrack} new QuizTrack instance
    */
   createFromXmpElement: createFromXmpElement$b
};

var SpeechMarker = function(params) {
   var _startTime;
   var _duration;
   var _name;
   var _probability;
   var _image;
   var _imageIndex;
   var _imageOffset;
   var _file;
   var _imageRectangle;
   var _additionalParamProperties = {};
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      forEach(keys(params), function(key) {
         if (key === 'startTime') {
            _startTime = params.startTime;
         } else if (key === 'duration') {
            _duration = params.duration;
         } else if (key === 'name') {
            _name = params.name;
         } else if (key === 'probability') {
            _probability = params.probability;
         } else if (key === 'image') {
            _image = params.image;
         } else if (key === 'imageIndex') {
            _imageIndex = params.imageIndex;
         } else if (key === 'imageOffset') {
            _imageOffset = params.imageOffset;
         } else if (key === 'file') {
            _file = params.file;
         } else if (key === 'imageRectangle') {
            _imageRectangle = params.imageRectangle;
         } else if (!isFunction(_additionalParamProperties[key])) {
            _additionalParamProperties[key] = params[key];
         }
      });
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'xmpDM:name': _name,
                  'xmpDM:startTime': _startTime,
                  'xmpDM:duration': _duration,
                  'xmpDM:probability': _probability,
                  'tscDM:image': _image,
                  'tscDM:imageindex': _imageIndex,
                  'tscDM:imageoffset': _imageOffset,
                  'tscDM:imagerect': _imageRectangle ? _imageRectangle.toXmpAtt() : null,
                  'tscDM:file': _file
               }
            }
         ]
      };

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         startTime: {
            get: function() {
               return _startTime;
            }
         },
         duration: {
            get: function() {
               return _duration;
            }
         },
         name: {
            get: function() {
               return _name;
            }
         },
         probability: {
            get: function() {
               return _probability;
            }
         },
         image: {
            get: function() {
               return _image;
            }
         },
         imageIndex: {
            get: function() {
               return _imageIndex;
            }
         },
         imageOffset: {
            get: function() {
               return _imageOffset;
            }
         },
         file: {
            get: function() {
               return _file;
            }
         },
         imageRectangle: {
            get: function() {
               return _imageRectangle;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         startTime: _startTime,
         duration: _duration,
         name: _name,
         probability: _probability,
         image: _image,
         imageIndex: _imageIndex,
         imageOffset: _imageOffset,
         file: _file,
         imageRectangle: _imageRectangle ? _imageRectangle.toPOJO() : undefined
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      forEach(keys(_additionalParamProperties), function(key) {
         pojo[key] = _additionalParamProperties[key];
      });
      return pojo;
   };

   return _public;
};

var createFromXmpElement$c = function(speechMarkerElement) {
   var params = {
      startTime: Number(speechMarkerElement.getAttribute('xmpDM:startTime')),
      duration: Number(speechMarkerElement.getAttribute('xmpDM:duration')),
      name: speechMarkerElement.getAttribute('xmpDM:name'),
      probability: Number(speechMarkerElement.getAttribute('xmpDM:probability'))
   };

   // optional attributes below

   params.image = speechMarkerElement.getAttribute('tscDM:image') || undefined;

   if (speechMarkerElement.getAttribute('tscDM:imageindex')) {
      params.imageIndex = Number(speechMarkerElement.getAttribute('tscDM:imageindex'));
   }

   if (speechMarkerElement.getAttribute('tscDM:imageoffset')) {
      params.imageOffset = Number(speechMarkerElement.getAttribute('tscDM:imageoffset'));
   }

   params.file = speechMarkerElement.getAttribute('tscDM:file') || undefined;

   if (speechMarkerElement.getAttribute('tscDM:imagerect')) {
      params.imageRectangle = ImageRectangle$1.create(speechMarkerElement.getAttribute('tscDM:imagerect'));
   }

   return new SpeechMarker(params);
};

/** @module SpeechMarker */
var SpeechMarker$1 = {
   /**
    *  Factory method that returns a new SpeechMarker object.
    *  @function create
    *  @param {Object} params - Required params for table of contents item
    *  @param {Number} [params.startTime] - Start time of the speech marker (milliseconds).
    *  @param {Number} [params.duration] - Duration of the speech marker (milliseconds).
    *  @param {String} [params.name] - Speech marker name.
    *  @param {String} [params.image] - Speech marker image.
    *  @param {Number} [params.imageIndex] - Speech marker image index.
    *  @param {Number} [params.imageOffset] - Speech marker image offset.
    *  @param {module:ImageRectangle} [params.imageRectangle] - Speech marker image rectangle.
    *  @static
    *  @return {module:SpeechMarker} new SpeechMarker instance
    */
   create: function(params) {
      return SpeechMarker(params);
   },

   /**
    *  Factory method that returns a new SpeechMarker object from an XMP speech marker element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP speechMarker element.
    *  @static
    *  @return {module:SpeechMarker} new SpeechMarker instance
    */
   createFromXmpElement: createFromXmpElement$c
};

var SpeechTrack = function(params) {
   var _markers = [];
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      if (params.hasOwnProperty('markers') && isArray(params.markers)) {
         _markers = params.markers;
      } else {
         throw new Error('SpeechTrack params is expecting an object eg. { markers: [marker1Object, ...] }');
      }
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'xmpDM:trackType': 'Speech',
                  'xmpDM:trackName': 'Speech to Text',
                  'xmpDM:frameRate': 'f1000'
               },
               children: [
                  {
                     name: 'xmpDM:markers',
                     children: [
                        {
                           name: 'rdf:Seq',
                           children: _markers
                        }
                     ]
                  }
               ]
            }
         ]
      };

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         markers: {
            get: function() {
               return _markers;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         markers: invokeMap(_markers, 'toPOJO')
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      return pojo;
   };

   return _public;
};

var createFromXmpElement$d = function(speechTrackElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var speechTrack = new SpeechTrack();

   if (speechTrackElement) {
      var markerElements = _xmlUtility.getElementsByTagNameNSPrefix(speechTrackElement, 'rdf:li');
      var markers = map(markerElements, SpeechMarker$1.createFromXmpElement);

      var params = {
         markers: markers
      };

      speechTrack = new SpeechTrack(params);
   }

   return speechTrack;
};

/** @module SpeechTrack */
var SpeechTrack$1 = {
   /**
    *  Factory method that returns a new SpeechTrack object.
    *  @function create
    *  @param {Object} params - Optional params for speech track
    *  @param {module:SpeechMarker[]} [params.markers] - Array of speech marker items.
    *  @static
    *  @return {module:SpeechTrack} new SpeechTrack instance
    */
   create: function(params) {
      return SpeechTrack(params);
   },

   /**
    *  Factory method that returns a new SpeechTrack object from an XMP speech track element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP speech track element.
    *  @static
    *  @return {module:SpeechTrack} new SpeechTrack instance
    */
   createFromXmpElement: createFromXmpElement$d
};

var ScreenTextMarker = function(params) {
   var _name;
   var _startTime;
   var _duration;
   var _tableOfContentsEntry;
   var _image;
   var _imageIndex;
   var _imageOffset;
   var _imageRectangle;
   var _file;
   var _additionalParamProperties = {};
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      forEach(keys(params), function(key) {
         if (key === 'name') {
            _name = params.name;
         } else if (key === 'startTime') {
            _startTime = params.startTime;
         } else if (key === 'duration') {
            _duration = params.duration;
         } else if (key === 'tableOfContentsEntry') {
            _tableOfContentsEntry = params.tableOfContentsEntry;
         } else if (key === 'image') {
            _image = params.image;
         } else if (key === 'imageIndex') {
            _imageIndex = params.imageIndex;
         } else if (key === 'imageOffset') {
            _imageOffset = params.imageOffset;
         } else if (key === 'imageRectangle') {
            _imageRectangle = params.imageRectangle;
         } else if (key === 'file') {
            _file = params.file;
         } else if (!isFunction(_additionalParamProperties[key])) {
            _additionalParamProperties[key] = params[key];
         }
      });
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'xmpDM:name': _name,
                  'xmpDM:startTime': _startTime,
                  'xmpDM:duration': _duration,
                  'tscDM:tocEntry': _tableOfContentsEntry,
                  'tscDM:image': _image,
                  'tscDM:imageindex': _imageIndex,
                  'tscDM:imageoffset': _imageOffset,
                  'tscDM:imagerect': _imageRectangle ? _imageRectangle.toXmpAtt() : null,
                  'tscDM:file': _file
               }
            }
         ]
      };

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         name: {
            get: function() {
               return _name;
            }
         },
         startTime: {
            get: function() {
               return _startTime;
            }
         },
         duration: {
            get: function() {
               return _duration;
            }
         },
         tableOfContentsEntry: {
            get: function() {
               return _tableOfContentsEntry;
            }
         },
         image: {
            get: function() {
               return _image;
            }
         },
         imageIndex: {
            get: function() {
               return _imageIndex;
            }
         },
         imageOffset: {
            get: function() {
               return _imageOffset;
            }
         },
         imageRectangle: {
            get: function() {
               return _imageRectangle;
            }
         },
         file: {
            get: function() {
               return _file;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         name: _name,
         startTime: _startTime,
         duration: _duration,
         tableOfContentsEntry: _tableOfContentsEntry,
         image: _image,
         imageIndex: _imageIndex,
         imageOffset: _imageOffset,
         imageRectangle: _imageRectangle ? _imageRectangle.toPOJO() : _imageRectangle,
         file: _file
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      forEach(keys(_additionalParamProperties), function(key) {
         pojo[key] = _additionalParamProperties[key];
      });
      return pojo;
   };

   return _public;
};

var createFromXmpElement$e = function(screenTextMarkerElement) {
   var params = {
      name: screenTextMarkerElement.getAttribute('xmpDM:name'),
      startTime: Number(screenTextMarkerElement.getAttribute('xmpDM:startTime')),
      duration: Number(screenTextMarkerElement.getAttribute('xmpDM:duration'))
   };

   // optional attributes below

   if (screenTextMarkerElement.getAttribute('tscDM:tocEntry')) {
      params.tableOfContentsEntry = Number(screenTextMarkerElement.getAttribute('tscDM:tocEntry'));
   }

   params.image = screenTextMarkerElement.getAttribute('tscDM:image') || undefined;

   if (screenTextMarkerElement.getAttribute('tscDM:imageindex')) {
      params.imageIndex = Number(screenTextMarkerElement.getAttribute('tscDM:imageindex'));
   }

   if (screenTextMarkerElement.getAttribute('tscDM:imageoffset')) {
      params.imageOffset = Number(screenTextMarkerElement.getAttribute('tscDM:imageoffset'));
   }

   if (screenTextMarkerElement.getAttribute('tscDM:imagerect')) {
      params.imageRectangle = ImageRectangle$1.create(screenTextMarkerElement.getAttribute('tscDM:imagerect'));
   }

   params.file = screenTextMarkerElement.getAttribute('tscDM:file') || undefined;

   return new ScreenTextMarker(params);
};

/** @module ScreenTextMarker */
var ScreenTextMarker$1 = {
   /**
    *  Factory method that returns a new ScreenTextMarker object.
    *  @function create
    *  @param {Object} params - Required params for table of contents item
    *  @param {String} [params.name] - ScreenText marker name.
    *  @param {Number} [params.startTime] - Start time of the screenText marker (milliseconds).
    *  @param {Number} [params.duration] - Duration of the screenText marker (milliseconds).
    *  @param {String} [params.tableOfContentsEntry] - Index of the table of contents entry corresponding to this screen text marker.
    *  @param {String} [params.image] - ScreenText marker image.
    *  @param {Number} [params.imageIndex] - ScreenText marker image index.
    *  @param {Number} [params.imageOffset] - ScreenText marker image offset.
    *  @param {module:ImageRectangle} [params.imageRectangle] - ScreenText marker image rectangle.
    *  @param {String} [params.file] - file associated with ScreenText marker.
    *  @static
    *  @return {module:ScreenTextMarker} new ScreenTextMarker instance
    */
   create: function(params) {
      return ScreenTextMarker(params);
   },

   /**
    *  Factory method that returns a new ScreenTextMarker object from an XMP screenText marker element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP screenTextMarker element.
    *  @static
    *  @return {module:ScreenTextMarker} new ScreenTextMarker instance
    */
   createFromXmpElement: createFromXmpElement$e
};

var ScreenTextTrack = function(params) {
   var _markers = [];
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      if (params.hasOwnProperty('markers') && isArray(params.markers)) {
         _markers = params.markers;
      } else {
         throw new Error('ScreenTextTrack params is expecting an object eg. { markers: [marker1Object, ...] }');
      }
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'rdf:li',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'xmpDM:trackType': 'ScreenText',
                  'xmpDM:trackName': 'Screen Text',
                  'xmpDM:frameRate': 'f1000'
               },
               children: [
                  {
                     name: 'xmpDM:markers',
                     children: [
                        {
                           name: 'rdf:Seq',
                           children: _markers
                        }
                     ]
                  }
               ]
            }
         ]
      };

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         markers: {
            get: function() {
               return _markers;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         markers: invokeMap(_markers, 'toPOJO')
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      return pojo;
   };

   return _public;
};

var createFromXmpElement$f = function(screenTextTrackElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var screenTextTrack = new ScreenTextTrack();

   if (screenTextTrackElement) {
      var markerElements = _xmlUtility.getElementsByTagNameNSPrefix(screenTextTrackElement, 'rdf:Description');
      var markers = map(markerElements, ScreenTextMarker$1.createFromXmpElement);

      var params = {
         markers: markers
      };

      screenTextTrack = new ScreenTextTrack(params);
   }

   return screenTextTrack;
};

/** @module ScreenTextTrack */
var ScreenTextTrack$1 = {
   /**
    *  Factory method that returns a new ScreenTextTrack object.
    *  @function create
    *  @param {Object} params - Optional params for screenText track
    *  @param {module:ScreenTextMarker[]} [params.markers] - Array of screenText marker items.
    *  @static
    *  @return {module:ScreenTextTrack} new ScreenTextTrack instance
    */
   create: function(params) {
      return ScreenTextTrack(params);
   },

   /**
    *  Factory method that returns a new ScreenTextTrack object from an XMP screenText track element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP screenText track element.
    *  @static
    *  @return {module:ScreenTextTrack} new ScreenTextTrack instance
    */
   createFromXmpElement: createFromXmpElement$f
};

var _xmlUtility;
var getXmlUtility = function() {
   return _xmlUtility || (_xmlUtility = new NamespaceXmlUtility(namespaceResolver));
};

var Controller = function(params) {
   var _name;
   var _parameters;
   var _controllerTextItems;
   var _additionalParamProperties = {};

   if (params) {
      forEach(keys(params), function(key) {
         if (key === 'name') {
            _name = params.name;
         } else if (key === 'parameters') {
            _parameters = params.parameters;
         } else if (key === 'controllerTextItems') {
            _controllerTextItems = params.controllerTextItems;
         } else if (!isFunction(_additionalParamProperties[key])) {
            _additionalParamProperties[key] = params[key];
         }
      });
   }

   var toXmp = function() {
      var localizedTextTree = map(_controllerTextItems, function(item) {
         return {
            name: 'rdf:li',
            children: [
               {
                  name: 'rdf:Description',
                  atts: {
                     'xmp:label': item.label
                  },
                  children: [
                     {
                        name: 'tscDM:localizedText',
                        children: [
                           {
                              name: 'rdf:Alt',
                              children: map(item.localizedTexts, function(localizedText) {
                                 return {
                                    name: 'rdf:li',
                                    atts: {
                                       'xml:lang': localizedText.lang
                                    },
                                    text: localizedText.text
                                 };
                              })
                           }
                        ]
                     }
                  ]
               }
            ]
         };
      });

      var treeDesc = {
         name: 'tscDM:controller',
         children: [
            {
               name: 'rdf:Description',
               atts: {
                  'xmpDM:name': _name
               },
               children: [
                  {
                     name: 'tscDM:parameters',
                     children: [
                        {
                           name: 'rdf:Bag',
                           children: map(_parameters, function(param) {
                              return {
                                 name: 'rdf:li',
                                 atts: {
                                    'xmpDM:name': param.name,
                                    'xmpDM:value': param.value
                                 }
                              };
                           })
                        }
                     ]
                  },
                  {
                     name: 'tscDM:controllerText',
                     children: [
                        {
                           name: 'rdf:Bag',
                           children: localizedTextTree
                        }
                     ]
                  }
               ]
            }
         ]
      };

      return getXmlUtility().createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         name: {
            get: function() {
               return _name;
            }
         },
         parameters: {
            get: function() {
               return _parameters;
            }
         },
         controllerTextItems: {
            get: function() {
               return _controllerTextItems;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         name: _name,
         parameters: _parameters,
         controllerTextItems: _controllerTextItems
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      forEach(keys(_additionalParamProperties), function(key) {
         pojo[key] = _additionalParamProperties[key];
      });
      return pojo;
   };

   return _public;
};

var parseParameter$1 = function(parameterElement) {
   return {
      name: parameterElement.getAttribute('xmpDM:name'),
      value: parameterElement.getAttribute('xmpDM:value')
   };
};

var parseParameters$1 = function(parametersElement) {
   if (parametersElement) {
      var parameterElements = getXmlUtility().getElementsByTagNameNSPrefix(parametersElement, 'rdf:li');
      var parameters = map(parameterElements, parseParameter$1);
      return parameters;
   }

   return undefined;
};

var parseLocalizedTextItem = function(textElement) {
   if (textElement) {
      return {
         lang: textElement.getAttribute('xml:lang'),
         text: getXmlUtility().getElementText(textElement)
      };
   }

   return undefined;
};

var parseControllerText = function(controllerTextItemElement) {
   var textElements = getXmlUtility().getElementsByTagNameNSPrefix(controllerTextItemElement, 'rdf:li');
   return {
      label: controllerTextItemElement.getAttribute('xmp:label'),
      localizedTexts: map(textElements, parseLocalizedTextItem)
   };
};

var parseControllerTextItems = function(controllerTextElement) {
   if (controllerTextElement) {
      var controllerTextElements = getXmlUtility().getElementsByTagNameNSPrefix(controllerTextElement, 'rdf:Description');
      var controllerTextItems = map(controllerTextElements, parseControllerText);
      return controllerTextItems;
   }

   return undefined;
};

var createFromXmpElement$g = function(controllerElement) {
   if (controllerElement) {
      var descriptionElement = getXmlUtility().getFirstElementByTagNameNSPrefix(controllerElement, 'rdf:Description');
      var parametersElement = getXmlUtility().getFirstElementByTagNameNSPrefix(descriptionElement, 'tscDM:parameters');
      var controllerTextElement = getXmlUtility().getFirstElementByTagNameNSPrefix(descriptionElement, 'tscDM:controllerText');

      var params = {
         name: descriptionElement.getAttribute('xmpDM:name'),
         parameters: parseParameters$1(parametersElement),
         controllerTextItems: parseControllerTextItems(controllerTextElement)
      };
      return new Controller(params);
   }

   return undefined;
};

/** @module Controller */
var Controller$1 = {
   /**
    *  Factory method that returns a new Controller object.
    *  @function create
    *  @param {Object} params - Required params for controller
    *  @param {String} [params.name] - Controller label.
    *  @param {Object[]} [params.parameters] - Array of Objects with name and value properties.
    *  @param {Object[]} [params.controllerTextItems] - Array of localized controller text items.
    *  @static
    *  @return {module:Controller} new Controller instance
    */
   create: function(params) {
      return Controller(params);
   },

   /**
    *  Factory method that returns a new Controller object from an XMP controller element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP controller element.
    *  @static
    *  @return {module:Controller} new Controller instance
    */
   createFromXmpElement: createFromXmpElement$g
};

var ContentList = function(params) {
   var _items;
   var _additionalParamProperties = {};
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   if (params) {
      forEach(keys(params), function(key) {
         if (key === 'items') {
            _items = params.items;
         } else if (!isFunction(_additionalParamProperties[key])) {
            _additionalParamProperties[key] = params[key];
         }
      });
   }

   var toXmp = function() {
      var treeDesc = {
         name: 'tscDM:contentList',
         children: [
            {
               name: 'rdf:Description',
               children: [
                  {
                     name: 'tscDM:files',
                     children: [
                        {
                           name: 'rdf:Seq',
                           children: map(_items, function(item) {
                              return {
                                 name: 'rdf:li',
                                 atts: {
                                    'xmpDM:name': item.name,
                                    'xmpDM:value': item.value
                                 }
                              };
                           })
                        }
                     ]
                  }
               ]
            }
         ]
      };

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var _public = Object.defineProperties({},
      {
         items: {
            get: function() {
               return _items;
            }
         }
      });

   _public.toXmp = toXmp;

   _public.toPOJO = function() {
      var pojo = {
         items: _items
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      forEach(keys(_additionalParamProperties), function(key) {
         pojo[key] = _additionalParamProperties[key];
      });
      return pojo;
   };

   return _public;
};

var parseContentListItems = function(contentListItemElements) {
   if (contentListItemElements) {
      var items = map(contentListItemElements, function(itemElement) {
         return {
            name: itemElement.getAttribute('xmpDM:name'),
            value: itemElement.getAttribute('xmpDM:value')
         };
      });
      return items;
   }

   return undefined;
};

var createFromXmpElement$h = function(contentListElement) {
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);
   if (contentListElement) {
      var contentListItemElements = _xmlUtility.getElementsByTagNameNSPrefix(contentListElement, 'rdf:li');

      var params = {
         items: parseContentListItems(contentListItemElements)
      };
      return new ContentList(params);
   }

   return undefined;
};

/** @module ContentList */
var ContentList$1 = {
   /**
    *  Factory method that returns a new ContentList object.
    *  @function create
    *  @param {Object} params - Required params for contentList
    *  @param {Object[]} [params.items] - Array of content list items.
    *  @static
    *  @return {module:ContentList} new ContentList instance
    */
   create: function(params) {
      return ContentList(params);
   },

   /**
    *  Factory method that returns a new ContentList object from an XMP contentList element.
    *  @function createFromXmpElement
    *  @param {Object} element - XMP contentList element.
    *  @static
    *  @return {module:ContentList} new ContentList instance
    */
   createFromXmpElement: createFromXmpElement$h
};

/**
 * Casts `array` to a slice if it's needed.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {number} start The start position.
 * @param {number} [end=array.length] The end position.
 * @returns {Array} Returns the cast slice.
 */
function castSlice(array, start, end) {
  var length = array.length;
  end = end === undefined ? length : end;
  return (!start && end >= length) ? array : baseSlice(array, start, end);
}

/**
 * The base implementation of `_.isNaN` without support for number objects.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
 */
function baseIsNaN(value) {
  return value !== value;
}

/**
 * A specialized version of `_.indexOf` which performs strict equality
 * comparisons of values, i.e. `===`.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function strictIndexOf(array, value, fromIndex) {
  var index = fromIndex - 1,
      length = array.length;

  while (++index < length) {
    if (array[index] === value) {
      return index;
    }
  }
  return -1;
}

/**
 * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseIndexOf(array, value, fromIndex) {
  return value === value
    ? strictIndexOf(array, value, fromIndex)
    : baseFindIndex(array, baseIsNaN, fromIndex);
}

/**
 * Used by `_.trim` and `_.trimStart` to get the index of the first string symbol
 * that is not found in the character symbols.
 *
 * @private
 * @param {Array} strSymbols The string symbols to inspect.
 * @param {Array} chrSymbols The character symbols to find.
 * @returns {number} Returns the index of the first unmatched string symbol.
 */
function charsStartIndex(strSymbols, chrSymbols) {
  var index = -1,
      length = strSymbols.length;

  while (++index < length && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
  return index;
}

/**
 * Converts an ASCII `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */
function asciiToArray(string) {
  return string.split('');
}

/** Used to compose unicode character classes. */
var rsAstralRange = '\\ud800-\\udfff',
    rsComboMarksRange = '\\u0300-\\u036f',
    reComboHalfMarksRange = '\\ufe20-\\ufe2f',
    rsComboSymbolsRange = '\\u20d0-\\u20ff',
    rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange,
    rsVarRange = '\\ufe0e\\ufe0f';

/** Used to compose unicode capture groups. */
var rsZWJ = '\\u200d';

/** Used to detect strings with [zero-width joiners or code points from the astral planes](http://eev.ee/blog/2015/09/12/dark-corners-of-unicode/). */
var reHasUnicode = RegExp('[' + rsZWJ + rsAstralRange  + rsComboRange + rsVarRange + ']');

/**
 * Checks if `string` contains Unicode symbols.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {boolean} Returns `true` if a symbol is found, else `false`.
 */
function hasUnicode(string) {
  return reHasUnicode.test(string);
}

/** Used to compose unicode character classes. */
var rsAstralRange$1 = '\\ud800-\\udfff',
    rsComboMarksRange$1 = '\\u0300-\\u036f',
    reComboHalfMarksRange$1 = '\\ufe20-\\ufe2f',
    rsComboSymbolsRange$1 = '\\u20d0-\\u20ff',
    rsComboRange$1 = rsComboMarksRange$1 + reComboHalfMarksRange$1 + rsComboSymbolsRange$1,
    rsVarRange$1 = '\\ufe0e\\ufe0f';

/** Used to compose unicode capture groups. */
var rsAstral = '[' + rsAstralRange$1 + ']',
    rsCombo = '[' + rsComboRange$1 + ']',
    rsFitz = '\\ud83c[\\udffb-\\udfff]',
    rsModifier = '(?:' + rsCombo + '|' + rsFitz + ')',
    rsNonAstral = '[^' + rsAstralRange$1 + ']',
    rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}',
    rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]',
    rsZWJ$1 = '\\u200d';

/** Used to compose unicode regexes. */
var reOptMod = rsModifier + '?',
    rsOptVar = '[' + rsVarRange$1 + ']?',
    rsOptJoin = '(?:' + rsZWJ$1 + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*',
    rsSeq = rsOptVar + reOptMod + rsOptJoin,
    rsSymbol = '(?:' + [rsNonAstral + rsCombo + '?', rsCombo, rsRegional, rsSurrPair, rsAstral].join('|') + ')';

/** Used to match [string symbols](https://mathiasbynens.be/notes/javascript-unicode). */
var reUnicode = RegExp(rsFitz + '(?=' + rsFitz + ')|' + rsSymbol + rsSeq, 'g');

/**
 * Converts a Unicode `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */
function unicodeToArray(string) {
  return string.match(reUnicode) || [];
}

/**
 * Converts `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */
function stringToArray(string) {
  return hasUnicode(string)
    ? unicodeToArray(string)
    : asciiToArray(string);
}

/** Used to match leading and trailing whitespace. */
var reTrimStart = /^\s+/;

/**
 * Removes leading whitespace or specified characters from `string`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category String
 * @param {string} [string=''] The string to trim.
 * @param {string} [chars=whitespace] The characters to trim.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {string} Returns the trimmed string.
 * @example
 *
 * _.trimStart('  abc  ');
 * // => 'abc  '
 *
 * _.trimStart('-_-abc-_-', '_-');
 * // => 'abc-_-'
 */
function trimStart(string, chars, guard) {
  string = toString(string);
  if (string && (guard || chars === undefined)) {
    return string.replace(reTrimStart, '');
  }
  if (!string || !(chars = baseToString(chars))) {
    return string;
  }
  var strSymbols = stringToArray(string),
      start = charsStartIndex(strSymbols, stringToArray(chars));

  return castSlice(strSymbols, start).join('');
}

/**
 * The base implementation of `_.values` and `_.valuesIn` which creates an
 * array of `object` property values corresponding to the property names
 * of `props`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array} props The property names to get values for.
 * @returns {Object} Returns the array of property values.
 */
function baseValues(object, props) {
  return arrayMap(props, function(key) {
    return object[key];
  });
}

/**
 * Creates an array of the own enumerable string keyed property values of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property values.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.values(new Foo);
 * // => [1, 2] (iteration order is not guaranteed)
 *
 * _.values('hi');
 * // => ['h', 'i']
 */
function values(object) {
  return object == null ? [] : baseValues(object, keys(object));
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$2 = Math.max;

/**
 * Checks if `value` is in `collection`. If `collection` is a string, it's
 * checked for a substring of `value`, otherwise
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * is used for equality comparisons. If `fromIndex` is negative, it's used as
 * the offset from the end of `collection`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object|string} collection The collection to inspect.
 * @param {*} value The value to search for.
 * @param {number} [fromIndex=0] The index to search from.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.reduce`.
 * @returns {boolean} Returns `true` if `value` is found, else `false`.
 * @example
 *
 * _.includes([1, 2, 3], 1);
 * // => true
 *
 * _.includes([1, 2, 3], 1, 2);
 * // => false
 *
 * _.includes({ 'a': 1, 'b': 2 }, 1);
 * // => true
 *
 * _.includes('abcd', 'bc');
 * // => true
 */
function includes(collection, value, fromIndex, guard) {
  collection = isArrayLike(collection) ? collection : values(collection);
  fromIndex = (fromIndex && !guard) ? toInteger(fromIndex) : 0;

  var length = collection.length;
  if (fromIndex < 0) {
    fromIndex = nativeMax$2(length + fromIndex, 0);
  }
  return isString(collection)
    ? (fromIndex <= length && collection.indexOf(value, fromIndex) > -1)
    : (!!length && baseIndexOf(collection, value, fromIndex) > -1);
}

var _parsers = [];

function trimLeadingDot(extension) {
   return trimStart(extension, '.');
}

var registerParser = function(name, supportedExtensions, parserCreator) {
   _parsers.push({
      name: name,
      extensions: map(supportedExtensions, trimLeadingDot),
      create: parserCreator
   });
};

var resolveParser = function(extension) {
   var parser = find(_parsers, function(theParser) {
      return includes(theParser.extensions, trimLeadingDot(extension));
   });
   return parser ? parser.create() : null;
};

var captionParsers = {
   register: registerParser,
   resolveByExtension: resolveParser
};

/**
 *  Factory method that returns a new CaptionText object.
 *  @function importFromFile
 *  @param {module:CaptionController} captionController - CaptionController object
 *  @param {String} fileName - Name of caption file
 *  @param {String} fileContents - Contents of the caption file
 *  @param {String} [languageIdentifierString='en-US'] - RFC 3066 notation language string
 *  @static
 *  @return {boolean} Did import succeed
 */
var importFromFile = function(captionController, fileName, fileContents, languageIdentifierString) {
   try {
      var extension = fileName.toLowerCase().split('.').pop();
      var parser = captionParsers.resolveByExtension(extension);
      if (parser) {
         var parsedCaptionTrack = parser.deserialize(fileContents, languageIdentifierString);
         captionController.addCaptionTrack(parsedCaptionTrack);
         return true;
      }
      return false;
   } catch (e) {
      console.error(e); // eslint-disable-line no-console
      return false;
   }
};

var captionImporter = {importFromFile: importFromFile};

var Xmp = function(xmpXml) {
   var _version;
   var _title;
   var _date;
   var _creator;
   var _presenterEmail;
   var _description;
   var _source;
   var _about;
   var _keywords;
   var _thumbnail;
   var _firstFrame;
   var _project;
   var _originId;
   var _iPodExplicit;
   var _documentId;
   var _duration;
   var _videoFrameSize;
   var _captionController = CaptionController$1.create();
   var _hotspotTrack = null;
   var _tableOfContentsTrack = null;
   var _quizTrack = null;
   var _speechTrack = null;
   var _screenTextTrack = null;
   var _controller;
   var _contentList;
   var _languages = [];
   var _containsTocThumbnails = false;
   var _containsToc = false;
   var _containsCaptions = false;
   var _containsHotspots = false;
   var _containsQuiz = false;
   var _containsQuizQuestions = false;
   var _containsScreenText = false;
   var _containsSpeechText = false;
   var _xmlUtility = new NamespaceXmlUtility(namespaceResolver);

   var toXmp = function() {
      var _captionXmpProvider = _captionController.containsCaptions ? _captionController : null;
      var tracksContainer = filter([_captionXmpProvider, _hotspotTrack, _quizTrack, _screenTextTrack, _speechTrack, _tableOfContentsTrack], function(track) {
         return track !== null;
      });

      var treeDesc = {
         name: 'rdf:Description',
         atts: {
            'tsc:version': _version,
            'dc:title': _title,
            'tscDM:presenteremail': _presenterEmail,
            'dc:source': _source,
            'dc:date': _date,
            'rdf:about': _about,
            'dc:creator': _creator,
            'dc:description': _description,
            'tscDM:keywords': _keywords,
            'tscDM:thumbnail': _thumbnail,
            'tscDM:firstFrame': _firstFrame,
            'tscDM:project': _project,
            'tscDM:originId': _originId,
            'tscDM:iPodExplicit': _iPodExplicit,
            'xmpMM:DocumentID': _documentId
         },
         children: [
            {
               name: 'xmpDM:Tracks',
               children: [
                  {
                     name: 'rdf:Bag',
                     children: tracksContainer
                  }
               ]
            },
            {
               name: 'tscDM:controller',
               children: [_controller]
            },
            {
               name: 'tscDM:contentList',
               children: [_contentList]
            },
            {
               name: 'tsc:langName',
               children: [
                  {
                     name: 'rdf:Bag',
                     children: map(_languages, function(language) {
                        return {
                           name: 'rdf:li',
                           atts: {
                              'xml:lang': language.langName
                           },
                           text: language.langValue
                        };
                     })
                  }
               ]
            },
            {
               name: 'xmpDM:videoFrameSize',
               atts: {
                  'stDim:unit': _videoFrameSize.unit,
                  'stDim:w': _videoFrameSize.width,
                  'stDim:h': _videoFrameSize.height
               }
            }
         ]
      };

      if (_duration) {
         treeDesc.children.push({
            name: 'xmpDM:duration',
            atts: {
               'xmpDM:scale': _duration.scale,
               'xmpDM:value': _duration.value
            }
         });
      }

      return _xmlUtility.createElementFromObject(treeDesc);
   };

   var toString = function() {
      var doc = _xmlUtility.createDocument();
      var nsElement = _xmlUtility.createElementNS(doc, namespaceResolver('x'), 'x', 'xmpmeta');

      forEach(wellKnownNamespaces, function(nsUrl, nsPrefix) {
         if (nsPrefix !== 'xml') {
            nsElement.setAttributeNS('http://www.w3.org/2000/xmlns/', ['xmlns', nsPrefix].join(':'), nsUrl);
         }
      });
      var rdfElement = _xmlUtility.createElementNS(doc, namespaceResolver('rdf'), 'rdf', 'RDF');
      rdfElement.appendChild(toXmp());
      nsElement.appendChild(rdfElement);
      doc.appendChild(nsElement);
      return _xmlUtility.xmlDocToString(doc);
   };

   var _public = Object.defineProperties({},
      {
         version: {
            get: function() {
               return _version;
            }
         },
         title: {
            get: function() {
               return _title;
            }
         },
         date: {
            get: function() {
               return _date;
            }
         },
         creator: {
            get: function() {
               return _creator;
            }
         },
         presenterEmail: {
            get: function() {
               return _presenterEmail;
            }
         },
         description: {
            get: function() {
               return _description;
            }
         },
         source: {
            get: function() {
               return _source;
            }
         },
         about: {
            get: function() {
               return _about;
            }
         },
         keywords: {
            get: function() {
               return _keywords;
            }
         },
         thumbnail: {
            get: function() {
               return _thumbnail;
            }
         },
         firstFrame: {
            get: function() {
               return _firstFrame;
            }
         },
         project: {
            get: function() {
               return _project;
            }
         },
         originId: {
            get: function() {
               return _originId;
            }
         },
         iPodExplicit: {
            get: function() {
               return _iPodExplicit;
            }
         },
         documentId: {
            get: function() {
               return _documentId;
            }
         },
         duration: {
            get: function() {
               return _duration;
            }
         },
         videoFrameSize: {
            get: function() {
               return _videoFrameSize;
            }
         },
         tocThumbnailImageSrc: {
            get: function() {
               if (_containsToc && _tableOfContentsTrack.items[0].image !== undefined) {
                  return _tableOfContentsTrack.items[0].image;
               } else if (_containsSpeechText && _speechTrack.markers[0].image !== undefined) {
                  return _speechTrack.markers[0].image;
               } else if (_containsScreenText && _screenTextTrack.markers[0].image !== undefined) {
                  return _screenTextTrack.markers[0].image;
               } else if (_captionController.captionsContainsTocThumbnails) {
                  return _captionController.getActiveCaptionsTocThumbnailImage();
               }
               return '';
            }
         },
         containsTocThumbnails: {
            get: function() {
               return _containsTocThumbnails;
            }
         },
         containsToc: {
            get: function() {
               return _containsToc;
            }
         },
         containsCaptions: {
            get: function() {
               return _containsCaptions;
            }
         },
         containsHotspots: {
            get: function() {
               return _containsHotspots;
            }
         },
         containsQuiz: {
            get: function() {
               return _containsQuiz;
            }
         },
         containsQuizQuestions: {
            get: function() {
               return _containsQuizQuestions;
            }
         },
         containsScreenText: {
            get: function() {
               return _containsScreenText;
            }
         },
         containsSpeechText: {
            get: function() {
               return _containsSpeechText;
            }
         },
         captionController: {
            get: function() {
               return _captionController;
            }
         },
         captionTrack: {
            get: function() {
               return _captionController.activeCaptionTrack;
            }
         },
         hotspotTrack: {
            get: function() {
               return _hotspotTrack;
            },
            set: function(value) {
               _hotspotTrack = value;
            }
         },
         tableOfContentsTrack: {
            get: function() {
               return _tableOfContentsTrack;
            },
            set: function(value) {
               _tableOfContentsTrack = value;
            }
         },
         quizTrack: {
            get: function() {
               return _quizTrack;
            },
            set: function(value) {
               _quizTrack = value;
            }
         },
         speechTrack: {
            get: function() {
               return _speechTrack;
            }
         },
         screenTextTrack: {
            get: function() {
               return _screenTextTrack;
            }
         },
         controller: {
            get: function() {
               return _controller;
            }
         },
         contentList: {
            get: function() {
               return _contentList;
            }
         }
      });

   var parseLanguageName = function(languageNameElement) {
      return {
         langName: languageNameElement.getAttribute('xml:lang'),
         langValue: _xmlUtility.getElementText(languageNameElement)
      };
   };

   var parseLanguages = function(langNameElement) {
      if (langNameElement) {
         var languageNameElements = _xmlUtility.getElementsByTagNameNSPrefix(langNameElement, 'rdf:li');
         var languageNames = map(languageNameElements, parseLanguageName);
         return languageNames;
      }

      return null;
   };

   var parseProductionDetails = function(doc) {
      var rootDescriptionElement = _xmlUtility.getFirstElementByTagNameNSPrefix(doc, 'rdf:Description');
      _version = rootDescriptionElement.getAttribute('tsc:version');
      _title = rootDescriptionElement.getAttribute('dc:title');
      _documentId = rootDescriptionElement.getAttribute('xmpMM:DocumentID');

      var videoFrameSizeElement = _xmlUtility.getFirstElementByTagNameNSPrefix(doc, 'xmpDM:videoFrameSize');
      _videoFrameSize = {
         unit: videoFrameSizeElement ? videoFrameSizeElement.getAttribute('stDim:unit') : 'pixel',
         width: videoFrameSizeElement ? Number(videoFrameSizeElement.getAttribute('stDim:w')) : 0,
         height: videoFrameSizeElement ? Number(videoFrameSizeElement.getAttribute('stDim:h')) : 0
      };

      var langNameElement = _xmlUtility.getFirstElementByTagNameNSPrefix(doc, 'tsc:langName');
      _languages = parseLanguages(langNameElement);

      // optional attributes below

      _presenterEmail = rootDescriptionElement.getAttribute('tscDM:presenteremail') || undefined;
      _source = rootDescriptionElement.getAttribute('dc:source') || undefined;
      _date = rootDescriptionElement.getAttribute('dc:date') || undefined;
      _about = rootDescriptionElement.getAttribute('rdf:about') || undefined;
      _creator = rootDescriptionElement.getAttribute('dc:creator') || undefined;
      _description = rootDescriptionElement.getAttribute('dc:description') || undefined;
      _keywords = rootDescriptionElement.getAttribute('tscDM:keywords') || undefined;
      _thumbnail = rootDescriptionElement.getAttribute('tscDM:thumbnail') || undefined;
      _firstFrame = rootDescriptionElement.getAttribute('tscDM:firstFrame') || undefined;
      _project = rootDescriptionElement.getAttribute('tscDM:project') || undefined;
      _originId = rootDescriptionElement.getAttribute('tscDM:originId') || undefined;
      _iPodExplicit = rootDescriptionElement.getAttribute('tscDM:iPodExplicit') || undefined;

      var durationElement = _xmlUtility.getFirstElementByTagNameNSPrefix(doc, 'xmpDM:duration') || undefined;
      if (durationElement) {
         _duration = {
            scale: durationElement.getAttribute('xmpDM:scale'),
            value: Number(durationElement.getAttribute('xmpDM:value'))
         };
      }
   };

   var getElementForTrackType = function(elements, trackType) {
      return find(elements, function(element) {
         return _xmlUtility.hasAttributeWithValue(element, 'xmpDM:trackType', trackType);
      });
   };

   var parseCaptionTrack = function(tracksDescriptionElements) {
      var captionTrackElement = getElementForTrackType(tracksDescriptionElements, 'Caption');
      if (captionTrackElement) {
         _captionController.addCaptionTrackFromXmpElement(captionTrackElement);
      }
   };

   var parseHotspotTrack = function(tracksDescriptionElements) {
      var hotspotTrackElement = getElementForTrackType(tracksDescriptionElements, 'Hotspot');
      if (hotspotTrackElement) {
         return HotspotTrack$1.createFromXmpElement(hotspotTrackElement);
      }
      return null;
   };

   var parseTableOfContentsTrack = function(tracksDescriptionElements) {
      var tableOfContentsTrackElement = getElementForTrackType(tracksDescriptionElements, 'TableOfContents');
      if (tableOfContentsTrackElement) {
         return TableOfContentsTrack$1.createFromXmpElement(tableOfContentsTrackElement);
      }
      return null;
   };

   var parseQuizTrack = function(tracksDescriptionElements) {
      var quizTrackElement = getElementForTrackType(tracksDescriptionElements, 'Quiz');
      if (quizTrackElement) {
         return QuizTrack$1.createFromXmpElement(quizTrackElement);
      }
      return null;
   };

   var parseSpeechTrack = function(tracksDescriptionElements) {
      var speechTrackElement = getElementForTrackType(tracksDescriptionElements, 'Speech');
      if (speechTrackElement) {
         return SpeechTrack$1.createFromXmpElement(speechTrackElement);
      }
      return null;
   };

   var parseScreenTextTrack = function(tracksDescriptionElements) {
      var screenTextTrackElement = getElementForTrackType(tracksDescriptionElements, 'ScreenText');
      if (screenTextTrackElement) {
         return ScreenTextTrack$1.createFromXmpElement(screenTextTrackElement);
      }
      return null;
   };

   var parseTracks = function(doc) {
      var tracksElement = _xmlUtility.getFirstElementByTagNameNSPrefix(doc, 'xmpDM:Tracks');
      var tracksDescriptionElements = _xmlUtility.getElementsByTagNameNSPrefix(tracksElement, 'rdf:Description');

      parseCaptionTrack(tracksDescriptionElements);
      _hotspotTrack = parseHotspotTrack(tracksDescriptionElements);
      _tableOfContentsTrack = parseTableOfContentsTrack(tracksDescriptionElements);
      _quizTrack = parseQuizTrack(tracksDescriptionElements);
      _speechTrack = parseSpeechTrack(tracksDescriptionElements);
      _screenTextTrack = parseScreenTextTrack(tracksDescriptionElements);

      var controllerElement = _xmlUtility.getFirstElementByTagNameNSPrefix(doc, 'tscDM:controller');
      _controller = Controller$1.createFromXmpElement(controllerElement);

      var contentListElement = _xmlUtility.getFirstElementByTagNameNSPrefix(doc, 'tscDM:contentList');
      _contentList = ContentList$1.createFromXmpElement(contentListElement);

      recordXmpContentFlags();
   };

   var recordXmpContentFlags = function() {
      _containsToc = _tableOfContentsTrack !== null && _tableOfContentsTrack.items.length > 0;
      _containsCaptions = _captionController.containsCaptions;
      _containsHotspots = _hotspotTrack !== null && _hotspotTrack.hotspots.length > 0;
      _containsQuiz = _quizTrack !== null;
      _containsQuizQuestions = _quizTrack !== null && _quizTrack.questionSets.length > 0;
      _containsScreenText = _screenTextTrack !== null && _screenTextTrack.markers.length > 0;
      _containsSpeechText = _speechTrack !== null && _speechTrack.markers.length > 0;

      _containsTocThumbnails = _containsToc && _tableOfContentsTrack.items[0].image !== undefined ||
                               _containsSpeechText && _speechTrack.markers[0].image !== undefined ||
                               _containsScreenText && _screenTextTrack.markers[0].image !== undefined ||
                               _captionController.captionsContainsTocThumbnails;
   };

   var importCaptionsFromFile = function(fileName, fileContents, languageIdentifierString) {
      var importedCaptionTrack = captionImporter.importFromFile(_captionController, fileName, fileContents, languageIdentifierString);
      if (importedCaptionTrack) {
         recordXmpContentFlags();
      }
   };

   var parse = function() {
      // current 'parse' does NOT deal with different versions of the TSC-XMP schema, as far as we have tested
      // all existing versions of the schema from 2.0.0 - 2.1.0 will parse using this mechanism, but neither parser
      // nor output formatter attempt to take a 2.0.0 and formally migrate it forward to 2.1.0.  Nor is the 'version'
      // property of the xml doc ever modified during reading a writing of a particular source.  this note is here
      // because will have to deal with versioning someday.  (JMD: 2016-05)
      var doc = _xmlUtility.parseXml(xmpXml);
      parseProductionDetails(doc);
      parseTracks(doc);
   };

   if (xmpXml) {
      parse();
   }

   _public.toXmp = toXmp;
   _public.toString = toString;
   _public.importCaptionsFromFile = importCaptionsFromFile;

   _public.toPOJO = function() {
      var pojo = {version: _version,
         title: _title,
         documentId: _documentId,
         languages: _languages,
         videoFrameSize: _videoFrameSize,
         source: _source,
         presenterEmail: _presenterEmail,
         date: _date,
         about: _about,
         creator: _creator,
         description: _description,
         keywords: _keywords,
         thumbnail: _thumbnail,
         firstFrame: _firstFrame,
         project: _project,
         originId: _originId,
         iPodExplicit: _iPodExplicit,
         duration: _duration,
         captionTrack: _captionController.toPOJO(),
         hotspotTrack: _hotspotTrack ? _hotspotTrack.toPOJO() : undefined,
         quizTrack: _quizTrack ? _quizTrack.toPOJO() : undefined,
         speechTrack: _speechTrack ? _speechTrack.toPOJO() : undefined,
         screenTextTrack: _screenTextTrack ? _screenTextTrack.toPOJO() : undefined,
         controller: _controller ? _controller.toPOJO() : undefined,
         contentList: _contentList ? _contentList.toPOJO() : undefined
      };
      var that = this;
      var additionalProperties = reject(keys(that), function(key) {
         return isFunction(that[key]);
      });
      forEach(additionalProperties, function(key) {
         pojo[key] = that[key];
      });
      return pojo;
   };

   return _public;
};

/** @module Xmp */
var xmp = {
   /**
    *  Factory method that returns a new Xmp object.
    *  @function create
    *  @param {String=} xmpXml - XMP XML string to parse.
    *  @static
    *  @return {module:Xmp} new Xmp instance
    */
   create: function(xmpXml) {
      return Xmp(xmpXml);
   }
};

/** Used for built-in method references. */
var objectProto$f = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$c = objectProto$f.hasOwnProperty;

/**
 * The base implementation of `_.has` without support for deep paths.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {Array|string} key The key to check.
 * @returns {boolean} Returns `true` if `key` exists, else `false`.
 */
function baseHas(object, key) {
  return object != null && hasOwnProperty$c.call(object, key);
}

/**
 * Checks if `path` is a direct property of `object`.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = { 'a': { 'b': 2 } };
 * var other = _.create({ 'a': _.create({ 'b': 2 }) });
 *
 * _.has(object, 'a');
 * // => true
 *
 * _.has(object, 'a.b');
 * // => true
 *
 * _.has(object, ['a', 'b']);
 * // => true
 *
 * _.has(other, 'a');
 * // => false
 */
function has(object, path) {
  return object != null && hasPath(object, path, baseHas);
}

/** Used for built-in method references. */
var arrayProto$2 = Array.prototype;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeJoin = arrayProto$2.join;

/**
 * Converts all elements in `array` into a string separated by `separator`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Array
 * @param {Array} array The array to convert.
 * @param {string} [separator=','] The element separator.
 * @returns {string} Returns the joined string.
 * @example
 *
 * _.join(['a', 'b', 'c'], '~');
 * // => 'a~b~c'
 */
function join(array, separator) {
  return array == null ? '' : nativeJoin.call(array, separator);
}

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last string symbol
 * that is not found in the character symbols.
 *
 * @private
 * @param {Array} strSymbols The string symbols to inspect.
 * @param {Array} chrSymbols The character symbols to find.
 * @returns {number} Returns the index of the last unmatched string symbol.
 */
function charsEndIndex(strSymbols, chrSymbols) {
  var index = strSymbols.length;

  while (index-- && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
  return index;
}

/** Used to match leading and trailing whitespace. */
var reTrim$1 = /^\s+|\s+$/g;

/**
 * Removes leading and trailing whitespace or specified characters from `string`.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to trim.
 * @param {string} [chars=whitespace] The characters to trim.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {string} Returns the trimmed string.
 * @example
 *
 * _.trim('  abc  ');
 * // => 'abc'
 *
 * _.trim('-_-abc-_-', '_-');
 * // => 'abc'
 *
 * _.map(['  foo  ', '  bar  '], _.trim);
 * // => ['foo', 'bar']
 */
function trim(string, chars, guard) {
  string = toString(string);
  if (string && (guard || chars === undefined)) {
    return string.replace(reTrim$1, '');
  }
  if (!string || !(chars = baseToString(chars))) {
    return string;
  }
  var strSymbols = stringToArray(string),
      chrSymbols = stringToArray(chars),
      start = charsStartIndex(strSymbols, chrSymbols),
      end = charsEndIndex(strSymbols, chrSymbols) + 1;

  return castSlice(strSymbols, start, end).join('');
}

/**
 * Gets the first element of `array`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @alias first
 * @category Array
 * @param {Array} array The array to query.
 * @returns {*} Returns the first element of `array`.
 * @example
 *
 * _.head([1, 2, 3]);
 * // => 1
 *
 * _.head([]);
 * // => undefined
 */
function head(array) {
  return (array && array.length) ? array[0] : undefined;
}

/**
 * Creates a function like `_.lowerFirst`.
 *
 * @private
 * @param {string} methodName The name of the `String` case method to use.
 * @returns {Function} Returns the new case function.
 */
function createCaseFirst(methodName) {
  return function(string) {
    string = toString(string);

    var strSymbols = hasUnicode(string)
      ? stringToArray(string)
      : undefined;

    var chr = strSymbols
      ? strSymbols[0]
      : string.charAt(0);

    var trailing = strSymbols
      ? castSlice(strSymbols, 1).join('')
      : string.slice(1);

    return chr[methodName]() + trailing;
  };
}

/**
 * Converts the first character of `string` to upper case.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category String
 * @param {string} [string=''] The string to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.upperFirst('fred');
 * // => 'Fred'
 *
 * _.upperFirst('FRED');
 * // => 'FRED'
 */
var upperFirst = createCaseFirst('toUpperCase');

/**
 * Converts the first character of `string` to upper case and the remaining
 * to lower case.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to capitalize.
 * @returns {string} Returns the capitalized string.
 * @example
 *
 * _.capitalize('FRED');
 * // => 'Fred'
 */
function capitalize(string) {
  return upperFirst(toString(string).toLowerCase());
}

/**
 * A specialized version of `_.reduce` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {*} [accumulator] The initial value.
 * @param {boolean} [initAccum] Specify using the first element of `array` as
 *  the initial value.
 * @returns {*} Returns the accumulated value.
 */
function arrayReduce(array, iteratee, accumulator, initAccum) {
  var index = -1,
      length = array == null ? 0 : array.length;

  if (initAccum && length) {
    accumulator = array[++index];
  }
  while (++index < length) {
    accumulator = iteratee(accumulator, array[index], index, array);
  }
  return accumulator;
}

/**
 * The base implementation of `_.reduce` and `_.reduceRight`, without support
 * for iteratee shorthands, which iterates over `collection` using `eachFunc`.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {*} accumulator The initial value.
 * @param {boolean} initAccum Specify using the first or last element of
 *  `collection` as the initial value.
 * @param {Function} eachFunc The function to iterate over `collection`.
 * @returns {*} Returns the accumulated value.
 */
function baseReduce(collection, iteratee, accumulator, initAccum, eachFunc) {
  eachFunc(collection, function(value, index, collection) {
    accumulator = initAccum
      ? (initAccum = false, value)
      : iteratee(accumulator, value, index, collection);
  });
  return accumulator;
}

/**
 * Reduces `collection` to a value which is the accumulated result of running
 * each element in `collection` thru `iteratee`, where each successive
 * invocation is supplied the return value of the previous. If `accumulator`
 * is not given, the first element of `collection` is used as the initial
 * value. The iteratee is invoked with four arguments:
 * (accumulator, value, index|key, collection).
 *
 * Many lodash methods are guarded to work as iteratees for methods like
 * `_.reduce`, `_.reduceRight`, and `_.transform`.
 *
 * The guarded methods are:
 * `assign`, `defaults`, `defaultsDeep`, `includes`, `merge`, `orderBy`,
 * and `sortBy`
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} [iteratee=_.identity] The function invoked per iteration.
 * @param {*} [accumulator] The initial value.
 * @returns {*} Returns the accumulated value.
 * @see _.reduceRight
 * @example
 *
 * _.reduce([1, 2], function(sum, n) {
 *   return sum + n;
 * }, 0);
 * // => 3
 *
 * _.reduce({ 'a': 1, 'b': 2, 'c': 1 }, function(result, value, key) {
 *   (result[value] || (result[value] = [])).push(key);
 *   return result;
 * }, {});
 * // => { '1': ['a', 'c'], '2': ['b'] } (iteration order is not guaranteed)
 */
function reduce(collection, iteratee, accumulator) {
  var func = isArray(collection) ? arrayReduce : baseReduce,
      initAccum = arguments.length < 3;

  return func(collection, baseIteratee(iteratee, 4), accumulator, initAccum, baseEach);
}

/** Used to store function metadata. */
var metaMap = WeakMap && new WeakMap;

/**
 * The base implementation of `setData` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to associate metadata with.
 * @param {*} data The metadata.
 * @returns {Function} Returns `func`.
 */
var baseSetData = !metaMap ? identity : function(func, data) {
  metaMap.set(func, data);
  return func;
};

/**
 * Creates a function that produces an instance of `Ctor` regardless of
 * whether it was invoked as part of a `new` expression or by `call` or `apply`.
 *
 * @private
 * @param {Function} Ctor The constructor to wrap.
 * @returns {Function} Returns the new wrapped function.
 */
function createCtor(Ctor) {
  return function() {
    // Use a `switch` statement to work with class constructors. See
    // http://ecma-international.org/ecma-262/7.0/#sec-ecmascript-function-objects-call-thisargument-argumentslist
    // for more details.
    var args = arguments;
    switch (args.length) {
      case 0: return new Ctor;
      case 1: return new Ctor(args[0]);
      case 2: return new Ctor(args[0], args[1]);
      case 3: return new Ctor(args[0], args[1], args[2]);
      case 4: return new Ctor(args[0], args[1], args[2], args[3]);
      case 5: return new Ctor(args[0], args[1], args[2], args[3], args[4]);
      case 6: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5]);
      case 7: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5], args[6]);
    }
    var thisBinding = baseCreate(Ctor.prototype),
        result = Ctor.apply(thisBinding, args);

    // Mimic the constructor's `return` behavior.
    // See https://es5.github.io/#x13.2.2 for more details.
    return isObject(result) ? result : thisBinding;
  };
}

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG = 1;

/**
 * Creates a function that wraps `func` to invoke it with the optional `this`
 * binding of `thisArg`.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {*} [thisArg] The `this` binding of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createBind(func, bitmask, thisArg) {
  var isBind = bitmask & WRAP_BIND_FLAG,
      Ctor = createCtor(func);

  function wrapper() {
    var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
    return fn.apply(isBind ? thisArg : this, arguments);
  }
  return wrapper;
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$3 = Math.max;

/**
 * Creates an array that is the composition of partially applied arguments,
 * placeholders, and provided arguments into a single array of arguments.
 *
 * @private
 * @param {Array} args The provided arguments.
 * @param {Array} partials The arguments to prepend to those provided.
 * @param {Array} holders The `partials` placeholder indexes.
 * @params {boolean} [isCurried] Specify composing for a curried function.
 * @returns {Array} Returns the new array of composed arguments.
 */
function composeArgs(args, partials, holders, isCurried) {
  var argsIndex = -1,
      argsLength = args.length,
      holdersLength = holders.length,
      leftIndex = -1,
      leftLength = partials.length,
      rangeLength = nativeMax$3(argsLength - holdersLength, 0),
      result = Array(leftLength + rangeLength),
      isUncurried = !isCurried;

  while (++leftIndex < leftLength) {
    result[leftIndex] = partials[leftIndex];
  }
  while (++argsIndex < holdersLength) {
    if (isUncurried || argsIndex < argsLength) {
      result[holders[argsIndex]] = args[argsIndex];
    }
  }
  while (rangeLength--) {
    result[leftIndex++] = args[argsIndex++];
  }
  return result;
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$4 = Math.max;

/**
 * This function is like `composeArgs` except that the arguments composition
 * is tailored for `_.partialRight`.
 *
 * @private
 * @param {Array} args The provided arguments.
 * @param {Array} partials The arguments to append to those provided.
 * @param {Array} holders The `partials` placeholder indexes.
 * @params {boolean} [isCurried] Specify composing for a curried function.
 * @returns {Array} Returns the new array of composed arguments.
 */
function composeArgsRight(args, partials, holders, isCurried) {
  var argsIndex = -1,
      argsLength = args.length,
      holdersIndex = -1,
      holdersLength = holders.length,
      rightIndex = -1,
      rightLength = partials.length,
      rangeLength = nativeMax$4(argsLength - holdersLength, 0),
      result = Array(rangeLength + rightLength),
      isUncurried = !isCurried;

  while (++argsIndex < rangeLength) {
    result[argsIndex] = args[argsIndex];
  }
  var offset = argsIndex;
  while (++rightIndex < rightLength) {
    result[offset + rightIndex] = partials[rightIndex];
  }
  while (++holdersIndex < holdersLength) {
    if (isUncurried || argsIndex < argsLength) {
      result[offset + holders[holdersIndex]] = args[argsIndex++];
    }
  }
  return result;
}

/**
 * Gets the number of `placeholder` occurrences in `array`.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} placeholder The placeholder to search for.
 * @returns {number} Returns the placeholder count.
 */
function countHolders(array, placeholder) {
  var length = array.length,
      result = 0;

  while (length--) {
    if (array[length] === placeholder) {
      ++result;
    }
  }
  return result;
}

/**
 * The function whose prototype chain sequence wrappers inherit from.
 *
 * @private
 */
function baseLodash() {
  // No operation performed.
}

/** Used as references for the maximum length and index of an array. */
var MAX_ARRAY_LENGTH$1 = 4294967295;

/**
 * Creates a lazy wrapper object which wraps `value` to enable lazy evaluation.
 *
 * @private
 * @constructor
 * @param {*} value The value to wrap.
 */
function LazyWrapper(value) {
  this.__wrapped__ = value;
  this.__actions__ = [];
  this.__dir__ = 1;
  this.__filtered__ = false;
  this.__iteratees__ = [];
  this.__takeCount__ = MAX_ARRAY_LENGTH$1;
  this.__views__ = [];
}

// Ensure `LazyWrapper` is an instance of `baseLodash`.
LazyWrapper.prototype = baseCreate(baseLodash.prototype);
LazyWrapper.prototype.constructor = LazyWrapper;

/**
 * This method returns `undefined`.
 *
 * @static
 * @memberOf _
 * @since 2.3.0
 * @category Util
 * @example
 *
 * _.times(2, _.noop);
 * // => [undefined, undefined]
 */
function noop$1() {
  // No operation performed.
}

/**
 * Gets metadata for `func`.
 *
 * @private
 * @param {Function} func The function to query.
 * @returns {*} Returns the metadata for `func`.
 */
var getData = !metaMap ? noop$1 : function(func) {
  return metaMap.get(func);
};

/** Used to lookup unminified function names. */
var realNames = {};

/** Used for built-in method references. */
var objectProto$g = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$d = objectProto$g.hasOwnProperty;

/**
 * Gets the name of `func`.
 *
 * @private
 * @param {Function} func The function to query.
 * @returns {string} Returns the function name.
 */
function getFuncName(func) {
  var result = (func.name + ''),
      array = realNames[result],
      length = hasOwnProperty$d.call(realNames, result) ? array.length : 0;

  while (length--) {
    var data = array[length],
        otherFunc = data.func;
    if (otherFunc == null || otherFunc == func) {
      return data.name;
    }
  }
  return result;
}

/**
 * The base constructor for creating `lodash` wrapper objects.
 *
 * @private
 * @param {*} value The value to wrap.
 * @param {boolean} [chainAll] Enable explicit method chain sequences.
 */
function LodashWrapper(value, chainAll) {
  this.__wrapped__ = value;
  this.__actions__ = [];
  this.__chain__ = !!chainAll;
  this.__index__ = 0;
  this.__values__ = undefined;
}

LodashWrapper.prototype = baseCreate(baseLodash.prototype);
LodashWrapper.prototype.constructor = LodashWrapper;

/**
 * Creates a clone of `wrapper`.
 *
 * @private
 * @param {Object} wrapper The wrapper to clone.
 * @returns {Object} Returns the cloned wrapper.
 */
function wrapperClone(wrapper) {
  if (wrapper instanceof LazyWrapper) {
    return wrapper.clone();
  }
  var result = new LodashWrapper(wrapper.__wrapped__, wrapper.__chain__);
  result.__actions__ = copyArray(wrapper.__actions__);
  result.__index__  = wrapper.__index__;
  result.__values__ = wrapper.__values__;
  return result;
}

/** Used for built-in method references. */
var objectProto$h = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$e = objectProto$h.hasOwnProperty;

/**
 * Creates a `lodash` object which wraps `value` to enable implicit method
 * chain sequences. Methods that operate on and return arrays, collections,
 * and functions can be chained together. Methods that retrieve a single value
 * or may return a primitive value will automatically end the chain sequence
 * and return the unwrapped value. Otherwise, the value must be unwrapped
 * with `_#value`.
 *
 * Explicit chain sequences, which must be unwrapped with `_#value`, may be
 * enabled using `_.chain`.
 *
 * The execution of chained methods is lazy, that is, it's deferred until
 * `_#value` is implicitly or explicitly called.
 *
 * Lazy evaluation allows several methods to support shortcut fusion.
 * Shortcut fusion is an optimization to merge iteratee calls; this avoids
 * the creation of intermediate arrays and can greatly reduce the number of
 * iteratee executions. Sections of a chain sequence qualify for shortcut
 * fusion if the section is applied to an array and iteratees accept only
 * one argument. The heuristic for whether a section qualifies for shortcut
 * fusion is subject to change.
 *
 * Chaining is supported in custom builds as long as the `_#value` method is
 * directly or indirectly included in the build.
 *
 * In addition to lodash methods, wrappers have `Array` and `String` methods.
 *
 * The wrapper `Array` methods are:
 * `concat`, `join`, `pop`, `push`, `shift`, `sort`, `splice`, and `unshift`
 *
 * The wrapper `String` methods are:
 * `replace` and `split`
 *
 * The wrapper methods that support shortcut fusion are:
 * `at`, `compact`, `drop`, `dropRight`, `dropWhile`, `filter`, `find`,
 * `findLast`, `head`, `initial`, `last`, `map`, `reject`, `reverse`, `slice`,
 * `tail`, `take`, `takeRight`, `takeRightWhile`, `takeWhile`, and `toArray`
 *
 * The chainable wrapper methods are:
 * `after`, `ary`, `assign`, `assignIn`, `assignInWith`, `assignWith`, `at`,
 * `before`, `bind`, `bindAll`, `bindKey`, `castArray`, `chain`, `chunk`,
 * `commit`, `compact`, `concat`, `conforms`, `constant`, `countBy`, `create`,
 * `curry`, `debounce`, `defaults`, `defaultsDeep`, `defer`, `delay`,
 * `difference`, `differenceBy`, `differenceWith`, `drop`, `dropRight`,
 * `dropRightWhile`, `dropWhile`, `extend`, `extendWith`, `fill`, `filter`,
 * `flatMap`, `flatMapDeep`, `flatMapDepth`, `flatten`, `flattenDeep`,
 * `flattenDepth`, `flip`, `flow`, `flowRight`, `fromPairs`, `functions`,
 * `functionsIn`, `groupBy`, `initial`, `intersection`, `intersectionBy`,
 * `intersectionWith`, `invert`, `invertBy`, `invokeMap`, `iteratee`, `keyBy`,
 * `keys`, `keysIn`, `map`, `mapKeys`, `mapValues`, `matches`, `matchesProperty`,
 * `memoize`, `merge`, `mergeWith`, `method`, `methodOf`, `mixin`, `negate`,
 * `nthArg`, `omit`, `omitBy`, `once`, `orderBy`, `over`, `overArgs`,
 * `overEvery`, `overSome`, `partial`, `partialRight`, `partition`, `pick`,
 * `pickBy`, `plant`, `property`, `propertyOf`, `pull`, `pullAll`, `pullAllBy`,
 * `pullAllWith`, `pullAt`, `push`, `range`, `rangeRight`, `rearg`, `reject`,
 * `remove`, `rest`, `reverse`, `sampleSize`, `set`, `setWith`, `shuffle`,
 * `slice`, `sort`, `sortBy`, `splice`, `spread`, `tail`, `take`, `takeRight`,
 * `takeRightWhile`, `takeWhile`, `tap`, `throttle`, `thru`, `toArray`,
 * `toPairs`, `toPairsIn`, `toPath`, `toPlainObject`, `transform`, `unary`,
 * `union`, `unionBy`, `unionWith`, `uniq`, `uniqBy`, `uniqWith`, `unset`,
 * `unshift`, `unzip`, `unzipWith`, `update`, `updateWith`, `values`,
 * `valuesIn`, `without`, `wrap`, `xor`, `xorBy`, `xorWith`, `zip`,
 * `zipObject`, `zipObjectDeep`, and `zipWith`
 *
 * The wrapper methods that are **not** chainable by default are:
 * `add`, `attempt`, `camelCase`, `capitalize`, `ceil`, `clamp`, `clone`,
 * `cloneDeep`, `cloneDeepWith`, `cloneWith`, `conformsTo`, `deburr`,
 * `defaultTo`, `divide`, `each`, `eachRight`, `endsWith`, `eq`, `escape`,
 * `escapeRegExp`, `every`, `find`, `findIndex`, `findKey`, `findLast`,
 * `findLastIndex`, `findLastKey`, `first`, `floor`, `forEach`, `forEachRight`,
 * `forIn`, `forInRight`, `forOwn`, `forOwnRight`, `get`, `gt`, `gte`, `has`,
 * `hasIn`, `head`, `identity`, `includes`, `indexOf`, `inRange`, `invoke`,
 * `isArguments`, `isArray`, `isArrayBuffer`, `isArrayLike`, `isArrayLikeObject`,
 * `isBoolean`, `isBuffer`, `isDate`, `isElement`, `isEmpty`, `isEqual`,
 * `isEqualWith`, `isError`, `isFinite`, `isFunction`, `isInteger`, `isLength`,
 * `isMap`, `isMatch`, `isMatchWith`, `isNaN`, `isNative`, `isNil`, `isNull`,
 * `isNumber`, `isObject`, `isObjectLike`, `isPlainObject`, `isRegExp`,
 * `isSafeInteger`, `isSet`, `isString`, `isUndefined`, `isTypedArray`,
 * `isWeakMap`, `isWeakSet`, `join`, `kebabCase`, `last`, `lastIndexOf`,
 * `lowerCase`, `lowerFirst`, `lt`, `lte`, `max`, `maxBy`, `mean`, `meanBy`,
 * `min`, `minBy`, `multiply`, `noConflict`, `noop`, `now`, `nth`, `pad`,
 * `padEnd`, `padStart`, `parseInt`, `pop`, `random`, `reduce`, `reduceRight`,
 * `repeat`, `result`, `round`, `runInContext`, `sample`, `shift`, `size`,
 * `snakeCase`, `some`, `sortedIndex`, `sortedIndexBy`, `sortedLastIndex`,
 * `sortedLastIndexBy`, `startCase`, `startsWith`, `stubArray`, `stubFalse`,
 * `stubObject`, `stubString`, `stubTrue`, `subtract`, `sum`, `sumBy`,
 * `template`, `times`, `toFinite`, `toInteger`, `toJSON`, `toLength`,
 * `toLower`, `toNumber`, `toSafeInteger`, `toString`, `toUpper`, `trim`,
 * `trimEnd`, `trimStart`, `truncate`, `unescape`, `uniqueId`, `upperCase`,
 * `upperFirst`, `value`, and `words`
 *
 * @name _
 * @constructor
 * @category Seq
 * @param {*} value The value to wrap in a `lodash` instance.
 * @returns {Object} Returns the new `lodash` wrapper instance.
 * @example
 *
 * function square(n) {
 *   return n * n;
 * }
 *
 * var wrapped = _([1, 2, 3]);
 *
 * // Returns an unwrapped value.
 * wrapped.reduce(_.add);
 * // => 6
 *
 * // Returns a wrapped value.
 * var squares = wrapped.map(square);
 *
 * _.isArray(squares);
 * // => false
 *
 * _.isArray(squares.value());
 * // => true
 */
function lodash(value) {
  if (isObjectLike(value) && !isArray(value) && !(value instanceof LazyWrapper)) {
    if (value instanceof LodashWrapper) {
      return value;
    }
    if (hasOwnProperty$e.call(value, '__wrapped__')) {
      return wrapperClone(value);
    }
  }
  return new LodashWrapper(value);
}

// Ensure wrappers are instances of `baseLodash`.
lodash.prototype = baseLodash.prototype;
lodash.prototype.constructor = lodash;

/**
 * Checks if `func` has a lazy counterpart.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` has a lazy counterpart,
 *  else `false`.
 */
function isLaziable(func) {
  var funcName = getFuncName(func),
      other = lodash[funcName];

  if (typeof other != 'function' || !(funcName in LazyWrapper.prototype)) {
    return false;
  }
  if (func === other) {
    return true;
  }
  var data = getData(other);
  return !!data && func === data[0];
}

/**
 * Sets metadata for `func`.
 *
 * **Note:** If this function becomes hot, i.e. is invoked a lot in a short
 * period of time, it will trip its breaker and transition to an identity
 * function to avoid garbage collection pauses in V8. See
 * [V8 issue 2070](https://bugs.chromium.org/p/v8/issues/detail?id=2070)
 * for more details.
 *
 * @private
 * @param {Function} func The function to associate metadata with.
 * @param {*} data The metadata.
 * @returns {Function} Returns `func`.
 */
var setData = shortOut(baseSetData);

/** Used to match wrap detail comments. */
var reWrapDetails = /\{\n\/\* \[wrapped with (.+)\] \*/,
    reSplitDetails = /,? & /;

/**
 * Extracts wrapper details from the `source` body comment.
 *
 * @private
 * @param {string} source The source to inspect.
 * @returns {Array} Returns the wrapper details.
 */
function getWrapDetails(source) {
  var match = source.match(reWrapDetails);
  return match ? match[1].split(reSplitDetails) : [];
}

/** Used to match wrap detail comments. */
var reWrapComment = /\{(?:\n\/\* \[wrapped with .+\] \*\/)?\n?/;

/**
 * Inserts wrapper `details` in a comment at the top of the `source` body.
 *
 * @private
 * @param {string} source The source to modify.
 * @returns {Array} details The details to insert.
 * @returns {string} Returns the modified source.
 */
function insertWrapDetails(source, details) {
  var length = details.length;
  if (!length) {
    return source;
  }
  var lastIndex = length - 1;
  details[lastIndex] = (length > 1 ? '& ' : '') + details[lastIndex];
  details = details.join(length > 2 ? ', ' : ' ');
  return source.replace(reWrapComment, '{\n/* [wrapped with ' + details + '] */\n');
}

/**
 * A specialized version of `_.includes` for arrays without support for
 * specifying an index to search from.
 *
 * @private
 * @param {Array} [array] The array to inspect.
 * @param {*} target The value to search for.
 * @returns {boolean} Returns `true` if `target` is found, else `false`.
 */
function arrayIncludes(array, value) {
  var length = array == null ? 0 : array.length;
  return !!length && baseIndexOf(array, value, 0) > -1;
}

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$1 = 1,
    WRAP_BIND_KEY_FLAG = 2,
    WRAP_CURRY_FLAG = 8,
    WRAP_CURRY_RIGHT_FLAG = 16,
    WRAP_PARTIAL_FLAG = 32,
    WRAP_PARTIAL_RIGHT_FLAG = 64,
    WRAP_ARY_FLAG = 128,
    WRAP_REARG_FLAG = 256,
    WRAP_FLIP_FLAG = 512;

/** Used to associate wrap methods with their bit flags. */
var wrapFlags = [
  ['ary', WRAP_ARY_FLAG],
  ['bind', WRAP_BIND_FLAG$1],
  ['bindKey', WRAP_BIND_KEY_FLAG],
  ['curry', WRAP_CURRY_FLAG],
  ['curryRight', WRAP_CURRY_RIGHT_FLAG],
  ['flip', WRAP_FLIP_FLAG],
  ['partial', WRAP_PARTIAL_FLAG],
  ['partialRight', WRAP_PARTIAL_RIGHT_FLAG],
  ['rearg', WRAP_REARG_FLAG]
];

/**
 * Updates wrapper `details` based on `bitmask` flags.
 *
 * @private
 * @returns {Array} details The details to modify.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @returns {Array} Returns `details`.
 */
function updateWrapDetails(details, bitmask) {
  arrayEach(wrapFlags, function(pair) {
    var value = '_.' + pair[0];
    if ((bitmask & pair[1]) && !arrayIncludes(details, value)) {
      details.push(value);
    }
  });
  return details.sort();
}

/**
 * Sets the `toString` method of `wrapper` to mimic the source of `reference`
 * with wrapper details in a comment at the top of the source body.
 *
 * @private
 * @param {Function} wrapper The function to modify.
 * @param {Function} reference The reference function.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @returns {Function} Returns `wrapper`.
 */
function setWrapToString(wrapper, reference, bitmask) {
  var source = (reference + '');
  return setToString(wrapper, insertWrapDetails(source, updateWrapDetails(getWrapDetails(source), bitmask)));
}

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$2 = 1,
    WRAP_BIND_KEY_FLAG$1 = 2,
    WRAP_CURRY_BOUND_FLAG = 4,
    WRAP_CURRY_FLAG$1 = 8,
    WRAP_PARTIAL_FLAG$1 = 32,
    WRAP_PARTIAL_RIGHT_FLAG$1 = 64;

/**
 * Creates a function that wraps `func` to continue currying.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {Function} wrapFunc The function to create the `func` wrapper.
 * @param {*} placeholder The placeholder value.
 * @param {*} [thisArg] The `this` binding of `func`.
 * @param {Array} [partials] The arguments to prepend to those provided to
 *  the new function.
 * @param {Array} [holders] The `partials` placeholder indexes.
 * @param {Array} [argPos] The argument positions of the new function.
 * @param {number} [ary] The arity cap of `func`.
 * @param {number} [arity] The arity of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createRecurry(func, bitmask, wrapFunc, placeholder, thisArg, partials, holders, argPos, ary, arity) {
  var isCurry = bitmask & WRAP_CURRY_FLAG$1,
      newHolders = isCurry ? holders : undefined,
      newHoldersRight = isCurry ? undefined : holders,
      newPartials = isCurry ? partials : undefined,
      newPartialsRight = isCurry ? undefined : partials;

  bitmask |= (isCurry ? WRAP_PARTIAL_FLAG$1 : WRAP_PARTIAL_RIGHT_FLAG$1);
  bitmask &= ~(isCurry ? WRAP_PARTIAL_RIGHT_FLAG$1 : WRAP_PARTIAL_FLAG$1);

  if (!(bitmask & WRAP_CURRY_BOUND_FLAG)) {
    bitmask &= ~(WRAP_BIND_FLAG$2 | WRAP_BIND_KEY_FLAG$1);
  }
  var newData = [
    func, bitmask, thisArg, newPartials, newHolders, newPartialsRight,
    newHoldersRight, argPos, ary, arity
  ];

  var result = wrapFunc.apply(undefined, newData);
  if (isLaziable(func)) {
    setData(result, newData);
  }
  result.placeholder = placeholder;
  return setWrapToString(result, func, bitmask);
}

/**
 * Gets the argument placeholder value for `func`.
 *
 * @private
 * @param {Function} func The function to inspect.
 * @returns {*} Returns the placeholder value.
 */
function getHolder(func) {
  var object = func;
  return object.placeholder;
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMin$1 = Math.min;

/**
 * Reorder `array` according to the specified indexes where the element at
 * the first index is assigned as the first element, the element at
 * the second index is assigned as the second element, and so on.
 *
 * @private
 * @param {Array} array The array to reorder.
 * @param {Array} indexes The arranged array indexes.
 * @returns {Array} Returns `array`.
 */
function reorder(array, indexes) {
  var arrLength = array.length,
      length = nativeMin$1(indexes.length, arrLength),
      oldArray = copyArray(array);

  while (length--) {
    var index = indexes[length];
    array[length] = isIndex(index, arrLength) ? oldArray[index] : undefined;
  }
  return array;
}

/** Used as the internal argument placeholder. */
var PLACEHOLDER = '__lodash_placeholder__';

/**
 * Replaces all `placeholder` elements in `array` with an internal placeholder
 * and returns an array of their indexes.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {*} placeholder The placeholder to replace.
 * @returns {Array} Returns the new array of placeholder indexes.
 */
function replaceHolders(array, placeholder) {
  var index = -1,
      length = array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (value === placeholder || value === PLACEHOLDER) {
      array[index] = PLACEHOLDER;
      result[resIndex++] = index;
    }
  }
  return result;
}

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$3 = 1,
    WRAP_BIND_KEY_FLAG$2 = 2,
    WRAP_CURRY_FLAG$2 = 8,
    WRAP_CURRY_RIGHT_FLAG$1 = 16,
    WRAP_ARY_FLAG$1 = 128,
    WRAP_FLIP_FLAG$1 = 512;

/**
 * Creates a function that wraps `func` to invoke it with optional `this`
 * binding of `thisArg`, partial application, and currying.
 *
 * @private
 * @param {Function|string} func The function or method name to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {*} [thisArg] The `this` binding of `func`.
 * @param {Array} [partials] The arguments to prepend to those provided to
 *  the new function.
 * @param {Array} [holders] The `partials` placeholder indexes.
 * @param {Array} [partialsRight] The arguments to append to those provided
 *  to the new function.
 * @param {Array} [holdersRight] The `partialsRight` placeholder indexes.
 * @param {Array} [argPos] The argument positions of the new function.
 * @param {number} [ary] The arity cap of `func`.
 * @param {number} [arity] The arity of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createHybrid(func, bitmask, thisArg, partials, holders, partialsRight, holdersRight, argPos, ary, arity) {
  var isAry = bitmask & WRAP_ARY_FLAG$1,
      isBind = bitmask & WRAP_BIND_FLAG$3,
      isBindKey = bitmask & WRAP_BIND_KEY_FLAG$2,
      isCurried = bitmask & (WRAP_CURRY_FLAG$2 | WRAP_CURRY_RIGHT_FLAG$1),
      isFlip = bitmask & WRAP_FLIP_FLAG$1,
      Ctor = isBindKey ? undefined : createCtor(func);

  function wrapper() {
    var length = arguments.length,
        args = Array(length),
        index = length;

    while (index--) {
      args[index] = arguments[index];
    }
    if (isCurried) {
      var placeholder = getHolder(wrapper),
          holdersCount = countHolders(args, placeholder);
    }
    if (partials) {
      args = composeArgs(args, partials, holders, isCurried);
    }
    if (partialsRight) {
      args = composeArgsRight(args, partialsRight, holdersRight, isCurried);
    }
    length -= holdersCount;
    if (isCurried && length < arity) {
      var newHolders = replaceHolders(args, placeholder);
      return createRecurry(
        func, bitmask, createHybrid, wrapper.placeholder, thisArg,
        args, newHolders, argPos, ary, arity - length
      );
    }
    var thisBinding = isBind ? thisArg : this,
        fn = isBindKey ? thisBinding[func] : func;

    length = args.length;
    if (argPos) {
      args = reorder(args, argPos);
    } else if (isFlip && length > 1) {
      args.reverse();
    }
    if (isAry && ary < length) {
      args.length = ary;
    }
    if (this && this !== root && this instanceof wrapper) {
      fn = Ctor || createCtor(fn);
    }
    return fn.apply(thisBinding, args);
  }
  return wrapper;
}

/**
 * Creates a function that wraps `func` to enable currying.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {number} arity The arity of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createCurry(func, bitmask, arity) {
  var Ctor = createCtor(func);

  function wrapper() {
    var length = arguments.length,
        args = Array(length),
        index = length,
        placeholder = getHolder(wrapper);

    while (index--) {
      args[index] = arguments[index];
    }
    var holders = (length < 3 && args[0] !== placeholder && args[length - 1] !== placeholder)
      ? []
      : replaceHolders(args, placeholder);

    length -= holders.length;
    if (length < arity) {
      return createRecurry(
        func, bitmask, createHybrid, wrapper.placeholder, undefined,
        args, holders, undefined, undefined, arity - length);
    }
    var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
    return apply(fn, this, args);
  }
  return wrapper;
}

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$4 = 1;

/**
 * Creates a function that wraps `func` to invoke it with the `this` binding
 * of `thisArg` and `partials` prepended to the arguments it receives.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} partials The arguments to prepend to those provided to
 *  the new function.
 * @returns {Function} Returns the new wrapped function.
 */
function createPartial(func, bitmask, thisArg, partials) {
  var isBind = bitmask & WRAP_BIND_FLAG$4,
      Ctor = createCtor(func);

  function wrapper() {
    var argsIndex = -1,
        argsLength = arguments.length,
        leftIndex = -1,
        leftLength = partials.length,
        args = Array(leftLength + argsLength),
        fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;

    while (++leftIndex < leftLength) {
      args[leftIndex] = partials[leftIndex];
    }
    while (argsLength--) {
      args[leftIndex++] = arguments[++argsIndex];
    }
    return apply(fn, isBind ? thisArg : this, args);
  }
  return wrapper;
}

/** Used as the internal argument placeholder. */
var PLACEHOLDER$1 = '__lodash_placeholder__';

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$5 = 1,
    WRAP_BIND_KEY_FLAG$3 = 2,
    WRAP_CURRY_BOUND_FLAG$1 = 4,
    WRAP_CURRY_FLAG$3 = 8,
    WRAP_ARY_FLAG$2 = 128,
    WRAP_REARG_FLAG$1 = 256;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMin$2 = Math.min;

/**
 * Merges the function metadata of `source` into `data`.
 *
 * Merging metadata reduces the number of wrappers used to invoke a function.
 * This is possible because methods like `_.bind`, `_.curry`, and `_.partial`
 * may be applied regardless of execution order. Methods like `_.ary` and
 * `_.rearg` modify function arguments, making the order in which they are
 * executed important, preventing the merging of metadata. However, we make
 * an exception for a safe combined case where curried functions have `_.ary`
 * and or `_.rearg` applied.
 *
 * @private
 * @param {Array} data The destination metadata.
 * @param {Array} source The source metadata.
 * @returns {Array} Returns `data`.
 */
function mergeData(data, source) {
  var bitmask = data[1],
      srcBitmask = source[1],
      newBitmask = bitmask | srcBitmask,
      isCommon = newBitmask < (WRAP_BIND_FLAG$5 | WRAP_BIND_KEY_FLAG$3 | WRAP_ARY_FLAG$2);

  var isCombo =
    ((srcBitmask == WRAP_ARY_FLAG$2) && (bitmask == WRAP_CURRY_FLAG$3)) ||
    ((srcBitmask == WRAP_ARY_FLAG$2) && (bitmask == WRAP_REARG_FLAG$1) && (data[7].length <= source[8])) ||
    ((srcBitmask == (WRAP_ARY_FLAG$2 | WRAP_REARG_FLAG$1)) && (source[7].length <= source[8]) && (bitmask == WRAP_CURRY_FLAG$3));

  // Exit early if metadata can't be merged.
  if (!(isCommon || isCombo)) {
    return data;
  }
  // Use source `thisArg` if available.
  if (srcBitmask & WRAP_BIND_FLAG$5) {
    data[2] = source[2];
    // Set when currying a bound function.
    newBitmask |= bitmask & WRAP_BIND_FLAG$5 ? 0 : WRAP_CURRY_BOUND_FLAG$1;
  }
  // Compose partial arguments.
  var value = source[3];
  if (value) {
    var partials = data[3];
    data[3] = partials ? composeArgs(partials, value, source[4]) : value;
    data[4] = partials ? replaceHolders(data[3], PLACEHOLDER$1) : source[4];
  }
  // Compose partial right arguments.
  value = source[5];
  if (value) {
    partials = data[5];
    data[5] = partials ? composeArgsRight(partials, value, source[6]) : value;
    data[6] = partials ? replaceHolders(data[5], PLACEHOLDER$1) : source[6];
  }
  // Use source `argPos` if available.
  value = source[7];
  if (value) {
    data[7] = value;
  }
  // Use source `ary` if it's smaller.
  if (srcBitmask & WRAP_ARY_FLAG$2) {
    data[8] = data[8] == null ? source[8] : nativeMin$2(data[8], source[8]);
  }
  // Use source `arity` if one is not provided.
  if (data[9] == null) {
    data[9] = source[9];
  }
  // Use source `func` and merge bitmasks.
  data[0] = source[0];
  data[1] = newBitmask;

  return data;
}

/** Error message constants. */
var FUNC_ERROR_TEXT$2 = 'Expected a function';

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$6 = 1,
    WRAP_BIND_KEY_FLAG$4 = 2,
    WRAP_CURRY_FLAG$4 = 8,
    WRAP_CURRY_RIGHT_FLAG$2 = 16,
    WRAP_PARTIAL_FLAG$2 = 32,
    WRAP_PARTIAL_RIGHT_FLAG$2 = 64;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$5 = Math.max;

/**
 * Creates a function that either curries or invokes `func` with optional
 * `this` binding and partially applied arguments.
 *
 * @private
 * @param {Function|string} func The function or method name to wrap.
 * @param {number} bitmask The bitmask flags.
 *    1 - `_.bind`
 *    2 - `_.bindKey`
 *    4 - `_.curry` or `_.curryRight` of a bound function
 *    8 - `_.curry`
 *   16 - `_.curryRight`
 *   32 - `_.partial`
 *   64 - `_.partialRight`
 *  128 - `_.rearg`
 *  256 - `_.ary`
 *  512 - `_.flip`
 * @param {*} [thisArg] The `this` binding of `func`.
 * @param {Array} [partials] The arguments to be partially applied.
 * @param {Array} [holders] The `partials` placeholder indexes.
 * @param {Array} [argPos] The argument positions of the new function.
 * @param {number} [ary] The arity cap of `func`.
 * @param {number} [arity] The arity of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createWrap(func, bitmask, thisArg, partials, holders, argPos, ary, arity) {
  var isBindKey = bitmask & WRAP_BIND_KEY_FLAG$4;
  if (!isBindKey && typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT$2);
  }
  var length = partials ? partials.length : 0;
  if (!length) {
    bitmask &= ~(WRAP_PARTIAL_FLAG$2 | WRAP_PARTIAL_RIGHT_FLAG$2);
    partials = holders = undefined;
  }
  ary = ary === undefined ? ary : nativeMax$5(toInteger(ary), 0);
  arity = arity === undefined ? arity : toInteger(arity);
  length -= holders ? holders.length : 0;

  if (bitmask & WRAP_PARTIAL_RIGHT_FLAG$2) {
    var partialsRight = partials,
        holdersRight = holders;

    partials = holders = undefined;
  }
  var data = isBindKey ? undefined : getData(func);

  var newData = [
    func, bitmask, thisArg, partials, holders, partialsRight, holdersRight,
    argPos, ary, arity
  ];

  if (data) {
    mergeData(newData, data);
  }
  func = newData[0];
  bitmask = newData[1];
  thisArg = newData[2];
  partials = newData[3];
  holders = newData[4];
  arity = newData[9] = newData[9] === undefined
    ? (isBindKey ? 0 : func.length)
    : nativeMax$5(newData[9] - length, 0);

  if (!arity && bitmask & (WRAP_CURRY_FLAG$4 | WRAP_CURRY_RIGHT_FLAG$2)) {
    bitmask &= ~(WRAP_CURRY_FLAG$4 | WRAP_CURRY_RIGHT_FLAG$2);
  }
  if (!bitmask || bitmask == WRAP_BIND_FLAG$6) {
    var result = createBind(func, bitmask, thisArg);
  } else if (bitmask == WRAP_CURRY_FLAG$4 || bitmask == WRAP_CURRY_RIGHT_FLAG$2) {
    result = createCurry(func, bitmask, arity);
  } else if ((bitmask == WRAP_PARTIAL_FLAG$2 || bitmask == (WRAP_BIND_FLAG$6 | WRAP_PARTIAL_FLAG$2)) && !holders.length) {
    result = createPartial(func, bitmask, thisArg, partials);
  } else {
    result = createHybrid.apply(undefined, newData);
  }
  var setter = data ? baseSetData : setData;
  return setWrapToString(setter(result, newData), func, bitmask);
}

/** Used to compose bitmasks for function metadata. */
var WRAP_CURRY_FLAG$5 = 8;

/**
 * Creates a function that accepts arguments of `func` and either invokes
 * `func` returning its result, if at least `arity` number of arguments have
 * been provided, or returns a function that accepts the remaining `func`
 * arguments, and so on. The arity of `func` may be specified if `func.length`
 * is not sufficient.
 *
 * The `_.curry.placeholder` value, which defaults to `_` in monolithic builds,
 * may be used as a placeholder for provided arguments.
 *
 * **Note:** This method doesn't set the "length" property of curried functions.
 *
 * @static
 * @memberOf _
 * @since 2.0.0
 * @category Function
 * @param {Function} func The function to curry.
 * @param {number} [arity=func.length] The arity of `func`.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {Function} Returns the new curried function.
 * @example
 *
 * var abc = function(a, b, c) {
 *   return [a, b, c];
 * };
 *
 * var curried = _.curry(abc);
 *
 * curried(1)(2)(3);
 * // => [1, 2, 3]
 *
 * curried(1, 2)(3);
 * // => [1, 2, 3]
 *
 * curried(1, 2, 3);
 * // => [1, 2, 3]
 *
 * // Curried with placeholders.
 * curried(1)(_, 3)(2);
 * // => [1, 2, 3]
 */
function curry(func, arity, guard) {
  arity = guard ? undefined : arity;
  var result = createWrap(func, WRAP_CURRY_FLAG$5, undefined, undefined, undefined, undefined, undefined, arity);
  result.placeholder = curry.placeholder;
  return result;
}

// Assign default placeholders.
curry.placeholder = {};

/**
 * Checks if `value` is an integer.
 *
 * **Note:** This method is based on
 * [`Number.isInteger`](https://mdn.io/Number/isInteger).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an integer, else `false`.
 * @example
 *
 * _.isInteger(3);
 * // => true
 *
 * _.isInteger(Number.MIN_VALUE);
 * // => false
 *
 * _.isInteger(Infinity);
 * // => false
 *
 * _.isInteger('3');
 * // => false
 */
function isInteger(value) {
  return typeof value == 'number' && value == toInteger(value);
}

var DEFAULT_CAPTION_LANGUAGE_IDENTIFIER_STRING = 'en-US';

// Simple function to convert HH:MM:SS,MMM or HH:MM:SS.MMM to SS.MMM
// Assume valid, returns 0 on error
/* eslint-disable no-magic-numbers, no-mixed-operators */
function toSeconds(timeIn) {
   var t = timeIn.split(':');

   try {
      var s = t[2].split(',');

      // Just in case a . is decimal separator
      if (s.length === 1) {
         s = t[2].split('.');
      }

      return parseFloat(t[0], 10) * 3600 + parseFloat(t[1], 10) * 60 + parseFloat(s[0], 10) + parseFloat(s[1], 10) / 1000;
   } catch (e) {
      return 0;
   }
}
/* eslint-enable no-magic-numbers, no-mixed-operators */

function nextNonEmptyLine(linesArray, position) {
   var idx = position;
   while (!linesArray[idx] && idx < linesArray.length) {
      idx++;
   }
   return idx;
}

function lastNonEmptyLine(linesArray) {
   var idx = linesArray.length - 1;

   while (idx >= 0 && !linesArray[idx]) {
      idx--;
   }

   return idx;
}

function readCaptionsFromString(input, timeDelimiter, hasIdRow, languageIdentifierString) {
   languageIdentifierString = languageIdentifierString || DEFAULT_CAPTION_LANGUAGE_IDENTIFIER_STRING;

   var captionCollection = CaptionCollection$1.create();
   var i = 0;
   var index = 0;
   var lines;
   var time;
   var text;
   var endIndex;
   var caption;
   var timeSplitRegex = new RegExp('[\\t ]*' + timeDelimiter + '[\\t ]*');
   var SSATagRegex = /\{(\\[\w]+\(?([\w\d]+,?)+\)?)+\}/gi;
   var lessThanRegex = /</g;
   var greaterThanRegex = />/g;
   var newlinePlaceholderRegex = /\\N/gi;

   // Here is where the magic happens
   // Split on line breaks
   lines = input.split(/(?:\r\n|\r|\n)/gm);
   endIndex = lastNonEmptyLine(lines) + 1;

   for (i = 0; i < endIndex; i++) {
      caption = {};
      text = [];

      i = nextNonEmptyLine(lines, i);
      if (hasIdRow) {
         caption.id = parseInt(lines[i++], 10);
      } else {
         caption.id = i;
      }

      // Split on delimiter (often '-->' or ','), trimming spaces as well
      time = lines[i++].split(timeSplitRegex);

      caption.start = toSeconds(time[0]);

      // So as to trim positioning information from end
      index = time[1].indexOf(' ');
      if (index !== -1) {
         time[1] = time[1].substring(0, index);
      }
      caption.end = toSeconds(time[1]);

      // Build single line of text from multi-line subtitle in file
      while (i < endIndex && lines[i]) {
         text.push(lines[i++]);
      }

      // Join into 1 line, SSA-style linebreaks
      // Strip out other SSA-style tags
      caption.text = text.join('\\N').replace(SSATagRegex, '');

      // Escape HTML entities
      caption.text = caption.text.replace(lessThanRegex, '&lt;').replace(greaterThanRegex, '&gt;');

      // Unescape great than and less than when it makes a valid html tag of a supported style (font, b, u, s, i)
      // Modified version of regex from Phil Haack's blog: http://haacked.com/archive/2004/10/25/usingregularexpressionstomatchhtml.aspx
      // Later modified by kev: http://kevin.deldycke.com/2007/03/ultimate-regular-expression-for-html-tag-parsing-with-php/
      //caption.text = caption.text.replace( /&lt;(\/?(font|b|u|i|s))((\s+(\w|\w[\w\-]*\w)(\s*=\s*(?:\".*?\"|'.*?'|[^'\">\s]+))?)+\s*|\s*)(\/?)&gt;/gi, "<$1$3$7>" );
      caption.text = caption.text.replace(newlinePlaceholderRegex, '\n');

      captionCollection.addCaption(Caption$1.create({
         startTime: caption.start * 1000,
         duration: caption.end * 1000 - caption.start * 1000,
         captionTextList: [
            CaptionText$1.create({text: caption.text, languageIdentifierString: languageIdentifierString})
         ],
         horizontalAlignment: 'Center',
         verticalAlignment: 'Bottom'
      }
      ));
   }

   var captionTrack = CaptionTrack$1.create({
      captions: captionCollection.captions,
      languageIdentifierString: languageIdentifierString
   });
   return captionTrack;
}

function padWithZero(inputNumber, width) {
   inputNumber = inputNumber + '';
   return inputNumber.length >= width ? inputNumber : new Array(width - inputNumber.length + 1).join('0') + inputNumber;
}

function formatMillisecondsToTimeCode(inputMilliseconds, options) {
   var date = new Date(inputMilliseconds);

   var hoursPaddingIsSet = options.hoursPadding || options.hoursPadding === 0;
   var hoursPadding = hoursPaddingIsSet ? options.hoursPadding : 2;
   var hours = padWithZero(date.getUTCHours(), hoursPadding);
   var minutes = padWithZero(date.getUTCMinutes(), 2);
   var seconds = padWithZero(date.getUTCSeconds(), 2);
   var rawMilliseconds = date.getUTCMilliseconds().toFixed(3).split('.')[0]; // eslint-disable-line no-magic-numbers
   var milliseconds = padWithZero(rawMilliseconds, 3); // eslint-disable-line no-magic-numbers

   var millisecondDelimiter = options.millisecondDelimiter ? options.millisecondDelimiter : ',';
   return hours + ':' + minutes + ':' + seconds + millisecondDelimiter + milliseconds;
}

function captionsToString(captionTrack, options) {
   function captionToString(output, caption, index) {
      var isBeginningOfString = index === 0;
      if (!isBeginningOfString) {
         output += '\n\n';
      }

      if (options.hasIdRow) {
         var oneBasedIndex = index + 1;
         output += oneBasedIndex;
         output += '\n';
      }

      output += formatMillisecondsToTimeCode(caption.startTime, options) + options.timeDelimiter + formatMillisecondsToTimeCode(caption.endTime, options);

      output += '\n';
      output += caption.text;

      return output;
   }

   return reduce(captionTrack.captionCollection.captions, captionToString, options.accumulator);
}

var baseNewlineParser = {
   readCaptionsFromStringWithIdRow: function(input, timeDelimiter, languageIdentifierString) {
      return readCaptionsFromString(input, timeDelimiter, true, languageIdentifierString);
   },
   readCaptionsFromString: function(input, timeDelimiter, languageIdentifierString) {
      return readCaptionsFromString(input, timeDelimiter, false, languageIdentifierString);
   },
   captionsToString: captionsToString,
   DEFAULT_CAPTION_LANGUAGE_IDENTIFIER_STRING: DEFAULT_CAPTION_LANGUAGE_IDENTIFIER_STRING
};

var registerParser$1 = function() {
   captionParsers.register('SAMI', ['.sami', '.smi'], function() {
      var _xmlUtility = new XmlUtility();

      function getElementText(textElement) {
         var nonBreakingSpace = String.fromCharCode(0xA0); //eslint-disable-line no-magic-numbers
         var text = _xmlUtility.getElementText(textElement).replace(nonBreakingSpace, ' ');
         return text;
      }

      function getSyncTagText(tag) {
         var textElements = _xmlUtility.getElementsByTagName(tag, 'p');
         if (textElements.length < 1) {
            return getElementText(tag);
         }

         var elementsText = map(textElements, getElementText);

         return join(elementsText, '\n');
      }

      function parseSyncTagToCaption(horizontalAlignment, languageIdentifierString, captionCollection, tag) {
         var startTime = +tag.getAttribute('start');

         var captionText = CaptionText$1.create({
            languageIdentifierString: languageIdentifierString,
            text: getSyncTagText(tag)
         });

         var createParams = {
            startTime: startTime,
            duration: 0,
            captionTextList: [captionText],
            horizontalAlignment: capitalize(horizontalAlignment.toLowerCase()),
            verticalAlignment: 'Bottom'
         };

         captionCollection.addCaption(Caption$1.create(createParams));
         return captionCollection;
      }

      function assignCaptionDurationsByFillingCaptionStartTimeGaps(captionCollection) {
         for (var i = 0; i < captionCollection.captions.length; i++) {
            var currentCaption = captionCollection.captions[i];

            var nextCaption = null;
            if (i + 1 < captionCollection.captions.length) {
               nextCaption = captionCollection.captions[i + 1];
            }

            var defaultDuration = 3000;
            var duration = nextCaption ? nextCaption.startTime - currentCaption.endTime : defaultDuration;
            currentCaption.setDuration(duration);
         }

         return captionCollection;
      }

      function removeHtmlComments(input) {
         return input.replace(/<!--|-->/g, '');
      }

      function cssColorToRGB(cssColorValue) {
         if (!cssColorValue) {
            return null;
         }
         var result = tinycolor(cssColorValue);
         if (!result.isValid()) {
            return null;
         }
         return result.toRgb();
      }

      function parseStyle(text) {
         var fontSizeNoDescriptorRegex = /font-size: *(\d+);/g;
         text = text.replace(fontSizeNoDescriptorRegex, 'font-size: $1px;');

         var doc = document.implementation.createHTMLDocument('');
         var styleElement = doc.createElement('style');
         styleElement.textContent = text;
         doc.body.appendChild(styleElement);
         return styleElement.sheet.cssRules;
      }

      function getCaptionTrackCreateParams(xmlDoc) {
         var styleElements = xmlDoc.getElementsByTagName('style');
         if (styleElements.length < 1) {
            return {};
         }

         var styles = parseStyle(removeHtmlComments(styleElements[0].textContent));

         var captionStyle = find(styles, function(s) {
            return s.selectorText.toLowerCase() === 'p';
         });

         captionStyle = captionStyle || head(styles);

         return {
            fgColor: cssColorToRGB(captionStyle.style.color),
            bgColor: cssColorToRGB(captionStyle.style.backgroundColor),
            fontFamily: trim(captionStyle.style.fontFamily, '"\''),
            fontSize: captionStyle.style.fontSize,
            horizontalAlignment: captionStyle.style.textAlign || 'Center'
         };
      }

      function readCaptionsFromString(input, languageIdentifierString) {
         languageIdentifierString = languageIdentifierString || baseNewlineParser.DEFAULT_CAPTION_LANGUAGE_IDENTIFIER_STRING;

         var captionCollection = CaptionCollection$1.create();
         var xmlDoc = _xmlUtility.parseXml(input, 'text/html');
         if (!xmlDoc) {
            throw new Error('xmlDoc is null');
         }

         var syncElements = xmlDoc.getElementsByTagName('sync');

         var createCaptionTrackParams = getCaptionTrackCreateParams(xmlDoc);

         var parseSyncTagToCaptionWithHorizontalAlignment = curry(parseSyncTagToCaption)(createCaptionTrackParams.horizontalAlignment, languageIdentifierString);
         captionCollection = reduce(syncElements, parseSyncTagToCaptionWithHorizontalAlignment, captionCollection);
         captionCollection = assignCaptionDurationsByFillingCaptionStartTimeGaps(captionCollection);

         createCaptionTrackParams.captions = captionCollection.captions;
         createCaptionTrackParams.languageIdentifierString = languageIdentifierString;

         var captionTrack = CaptionTrack$1.create(createCaptionTrackParams);
         return captionTrack;
      }

      function captionToString(output, caption, nextCaption) {
         var text = caption.text;
         if (text) {
            text += '\n';
         }

         output += '<SYNC start="' + caption.startTime + '"><P class="ENCC">' + text + '</P></SYNC>';
         if (nextCaption && nextCaption.startTime > caption.endTime) {
            output += '<SYNC start="' + caption.endTime + '"><P class="ENCC"></P></SYNC>';
         }
         return output;
      }

      function getTextAlignment(captionTrack) {
         var textAlignments = reduce(captionTrack.captionCollection.captions, function(alignments, caption) {
            var alignment = caption.horizontalAlignment;
            if (!alignment || has(alignments, alignment)) {
               return alignments;
            }
            alignments[alignment] = true;
            return alignments;
         }, {});

         var allAlignments = keys(textAlignments);
         return allAlignments.length === 1 ? allAlignments[0].toLowerCase() : 'center';
      }

      function captionsToString(captionTrack) {
         var fontSize = isInteger(captionTrack.fontSize) ? captionTrack.fontSize + 'pt' : captionTrack.fontSize;
         var fontFamily = captionTrack.fontFamily;
         var fgHexColor = captionTrack.fgHexColor;
         var bgHexColor = captionTrack.bgHexColor;
         var textAlign = getTextAlignment(captionTrack);

         /* eslint-disable no-tabs */
         var samiCaptionHeader =
            '<SAMI>' +
            '<HEAD><TITLE>Captions</TITLE><STYLE TYPE="text/css">' +
            '<!--' +
            'P {	margin-left:  1pt;' +
            'margin-right: 1pt;' +
            'margin-bottom: 2pt;' +
            'margin-top: 2pt;' +
            'text-align: ' + textAlign + ';' +
            'font-size: ' + fontSize + ';' +
            'font-family: ' + fontFamily + ';' +
            'font-weight: normal;' +
            'font-style: normal;' +
            'background-color: ' + bgHexColor + ';' +
            'color: ' + fgHexColor + '; }' +
            '.ENCC {Name: English; lang: en-US; SAMI_Type: CC;}' +
            '-->' +
            '</STYLE></HEAD>' +
            '<BODY>';
         var samiCaptionFooter =
            '</BODY>' +
            '</SAMI>';
         /* eslint-enable no-tabs */

         var output = samiCaptionHeader;

         var captionCollection = captionTrack.captionCollection;
         for (var i = 0; i < captionCollection.captions.length; i++) {
            var currentCaption = captionCollection.captions[i];

            var isLastCaption = i === captionCollection.captions.length - 1;
            var nextCaption = isLastCaption ? null : captionCollection.captions[i + 1];

            output = captionToString(output, currentCaption, nextCaption);
         }

         output += samiCaptionFooter;
         return output;
      }

      return {
         deserialize: readCaptionsFromString,
         serialize: captionsToString
      };
   });
};

var samiParser = {
   registerParser: registerParser$1
};

var registerParser$2 = function() {
   captionParsers.register('SBV', ['.sbv'], function() {
      var sbvTimeDelimiter = ',';

      function readCaptionsFromString(input, languageIdentifierString) {
         return baseNewlineParser.readCaptionsFromString(input, sbvTimeDelimiter, languageIdentifierString);
      }

      function captionsToString(captionCollection) {
         return baseNewlineParser.captionsToString(captionCollection, {
            timeDelimiter: sbvTimeDelimiter,
            accumulator: '',
            hoursPadding: 1
         });
      }

      return {
         deserialize: readCaptionsFromString,
         serialize: captionsToString
      };
   });
};

var sbvParser = {
   registerParser: registerParser$2
};

var registerParser$3 = function() {
   captionParsers.register('SRT', ['.srt'], function() {
      var srtEndOfFileMarker = '\n\n\n';
      var srtTimeDelimiter = ' --> ';

      function readCaptionsFromString(input, languageIdentifierString) {
         return baseNewlineParser.readCaptionsFromStringWithIdRow(input, srtTimeDelimiter.trim(), languageIdentifierString);
      }

      function captionsToString(captionCollection) {
         var captionString = baseNewlineParser.captionsToString(captionCollection, {
            timeDelimiter: srtTimeDelimiter,
            accumulator: '\n\n',
            hasIdRow: true
         });
         return captionString + srtEndOfFileMarker;
      }

      return {
         deserialize: readCaptionsFromString,
         serialize: captionsToString
      };
   });
};

var srtParser = {
   registerParser: registerParser$3
};

var registerParser$4 = function() {
   captionParsers.register('TTML', ['.ttml', '.dfxp'], function() {
      // Code below is taken from https://github.com/mozilla/popcorn-js/blob/master/parsers/parserTTML/popcorn.parserTTML.js

      var rWhitespace = /^[\s]+|[\s]+$/gm;
      var rLineBreak = /(?:\r\n|\r|\n)/gm;
      var rBreakTag = /(<br *\/?>)/gm;

      // Parse the children of the given node
      function parseChildren(node, timeOffset, region) {
         var currNode = node.firstChild;
         var currRegion = getNodeRegion(node, region);
         var retVal = [];
         var newOffset;

         while (currNode) {
            if (currNode.nodeType === 1) {
               if (currNode.nodeName === 'p') {
                  // p is a textual node, process contents as subtitle
                  retVal.push(parseNode(currNode, timeOffset, currRegion));
               } else if (currNode.nodeName === 'div') {
                  // div is container for subtitles, recurse
                  newOffset = toSeconds(currNode.getAttribute('begin'));

                  if (newOffset < 0) {
                     newOffset = timeOffset;
                  }

                  retVal.push.apply(retVal, parseChildren(currNode, newOffset, currRegion));
               }
            }

            currNode = currNode.nextSibling;
         }

         return retVal;
      }

      // Get the "region" attribute of a node, to know where to put the subtitles
      function getNodeRegion(node, defaultTo) {
         var region = node.getAttribute('region');

         if (region !== null) {
            return region;
         }
         return defaultTo || '';
      }

      // Parse a node for text content
      function parseNode(node, timeOffset, region) {
         var sub = {};

         // Trim left and right whitespace from text and convert non-explicit line breaks
         sub.text = (node.textContent || node.text).replace(rWhitespace, '').replace(rLineBreak, '<br />');
         sub.id = node.getAttribute('xml:id') || node.getAttribute('id');
         sub.start = toSeconds(node.getAttribute('begin'), timeOffset);
         sub.end = toSeconds(node.getAttribute('end'), timeOffset);
         sub.target = getNodeRegion(node, region);

         if (sub.end < 0) {
            // No end given, infer duration if possible
            // Otherwise, give end as MAX_VALUE
            sub.end = toSeconds(node.getAttribute('duration'), 0);

            if (sub.end >= 0) {
               sub.end += sub.start;
            } else {
               sub.end = Number.MAX_VALUE;
            }
         }

         return {subtitle: sub};
      }

      function toTimelineSeconds(timeStr, framerate) {
         // Hours and minutes are optional
         // Seconds must be specified
         // Seconds can be followed by milliseconds OR by the frame information
         var validTimeFormat = /^([0-9]+:){0,2}[0-9]+([.;][0-9]+)?$/;
         var errorMessage = 'Invalid time format';
         var digitPairs;
         var lastIndex;
         var lastPair;
         var firstPair;
         var frameInfo;
         var frameTime;

         if (typeof timeStr === 'number') {
            return timeStr;
         }

         if (typeof timeStr === 'string' && !validTimeFormat.test(timeStr)) {
            throw errorMessage;
         }

         digitPairs = timeStr.split(':');
         lastIndex = digitPairs.length - 1;
         lastPair = digitPairs[lastIndex];

         // Fix last element:
         if (lastPair.indexOf(';') > -1) {
            frameInfo = lastPair.split(';');
            frameTime = 0;

            if (framerate && typeof framerate === 'number') {
               frameTime = parseFloat(frameInfo[1], 10) / framerate;
            }

            digitPairs[lastIndex] = parseInt(frameInfo[0], 10) + frameTime;
         }

         firstPair = digitPairs[0];

         /* eslint-disable no-magic-numbers, no-mixed-operators */
         return {

            1: parseFloat(firstPair, 10),

            2: parseInt(firstPair, 10) * 60 +
               parseFloat(digitPairs[1], 10),

            3: parseInt(firstPair, 10) * 3600 +
               parseInt(digitPairs[1], 10) * 60 +
               parseFloat(digitPairs[2], 10)

         }[digitPairs.length || 1];
         /* eslint-enable no-magic-numbers, no-mixed-operators */
      }

      // Convert time expression to SS.mmm
      // Expression may be absolute to timeline (hh:mm:ss.ms)
      //   or relative ( decimal followed by metric ) ex: 3.4s, 5.7m
      // Returns -1 if invalid
      function toSeconds(timeIn, offset) {
         var i;

         if (!timeIn) {
            return -1;
         }

         try {
            return toTimelineSeconds(timeIn);
         } catch (e) {
            i = getMetricIndex(timeIn);
            return parseFloat(timeIn.substring(0, i)) * getMultipler(timeIn.substring(i)) + (offset || 0);
         }
      }

      // In a time string such as 3.4ms, get the index of the first character (m) of the time metric (ms)
      function getMetricIndex(timeIn) {
         var i = timeIn.length - 1;

         while (i >= 0 && timeIn[i] <= '9' && timeIn[i] >= '0') {
            i--;
         }

         return i;
      }

      // Determine multiplier for metric relative to seconds
      function getMultipler(metric) {
         return {
            h: 3600,
            m: 60,
            s: 1,
            ms: 0.001
         }[metric] || -1;
      }

      function readCaptionsFromString(input, languageIdentifierString) {
         languageIdentifierString = languageIdentifierString || baseNewlineParser.DEFAULT_CAPTION_LANGUAGE_IDENTIFIER_STRING;

         var captionCollection = CaptionCollection$1.create();
         var node;
         var xmlDocument;

         // Null checks
         if (!input) {
            return null;
         }

         input = input.replace(/<head>.*<\/head>/, '');
         input = input.replace(rBreakTag, '\\N');


         var _xmlUtility = new XmlUtility();
         xmlDocument = _xmlUtility.parseXml(input);

         if (!xmlDocument) {
            return captionCollection;
         }

         node = xmlDocument.documentElement.firstChild;

         // Find body tag
         while (node.nodeName !== 'body') {
            node = node.nextSibling;
         }

         if (node) {
            var captions = parseChildren(node, 0);

            map(captions, function(caption) {
               caption = caption.subtitle;
               var createParams = {
                  startTime: caption.start * 1000,
                  duration: caption.end * 1000 - caption.start * 1000,
                  captionTextList: [
                     CaptionText$1.create({text: caption.text.replace('\\N', '\n'), languageIdentifierString: languageIdentifierString})
                  ],
                  horizontalAlignment: 'Center',
                  verticalAlignment: 'Bottom'
               };

               captionCollection.addCaption(Caption$1.create(createParams));
            });
         }

         var captionTrack = CaptionTrack$1.create({
            captions: captionCollection.captions,
            languageIdentifierString: languageIdentifierString
         });
         return captionTrack;
      }

      function captionToString(output, caption, index) {
         var oneBasedIndex = index + 1;
         output += '<p xml:id="subtitle' + oneBasedIndex + '" begin="' + caption.startTime / 1000 + 's" end="' + caption.endTime / 1000 + 's">' + caption.text + '</p>';
         return output;
      }

      function captionsToString(captionTrack) {
         var ttmlHeader = '<?xml version="1.0" encoding="UTF-8"?><tt xmlns:tts="http://www.w3.org/2006/04/ttaf1#styling" xmlns="http://www.w3.org/2006/04/ttaf1">' +
            '<head>' +
            '<metadata xmlns:ttm="http://www.w3.org/ns/ttml#metadata"><ttm:title>Captions</ttm:title></metadata>' +
            '<styling xmlns:tts="http://www.w3.org/ns/ttml#styling"><style xml:id="s1" tts:color="white" tts:fontFamily="proportionalSansSerif" tts:fontSize="22px" tts:textAlign="center" /></styling>' +
            '<layout xmlns:tts="http://www.w3.org/ns/ttml#styling"><region xml:id="subtitleArea" style="s1" tts:extent="560px 62px" tts:padding="5px 3px" tts:backgroundColor="black" tts:displayAlign="after" /></layout>' +
            '</head>' +
            '<body region="subtitleArea">' +
            '<div>';

         var ttmlFooter = '</div></body></tt>';

         var output = ttmlHeader;

         for (var i = 0; i < captionTrack.captionCollection.captions.length; i++) {
            var currentCaption = captionTrack.captionCollection.captions[i];

            output = captionToString(output, currentCaption, i);
         }

         output += ttmlFooter;
         return output;
      }

      return {
         deserialize: readCaptionsFromString,
         serialize: captionsToString
      };
   });
};

var ttmlParser = {
   registerParser: registerParser$4
};

var registerParser$5 = function() {
   captionParsers.register('VTT', ['.vtt'], function() {
      var vttTimeDelimiter = '-->';

      // [HH:]MM:SS.mmm string to SS.mmm float
      // Throws exception if invalid
      /* eslint-disable no-magic-numbers, no-mixed-operators */
      function toSeconds(timeIn) {
         var t = timeIn.split(':');
         var l = timeIn.length;
         var time;

         // Invalid time string provided
         if (l !== 12 && l !== 9) {
            throw new Error('Bad cue');
         }

         l = t.length - 1;

         try {
            time = parseInt(t[l - 1], 10) * 60 + parseFloat(t[l], 10);

            // Hours were given
            if (l === 2) {
               time += parseInt(t[0], 10) * 3600;
            }
         } catch (e) {
            throw new Error('Bad cue');
         }

         return time;
      }
      /* eslint-enable no-magic-numbers, no-mixed-operators */

      function parseCueHeader(line) {
         var lineSegments;
         var sub = {};
         var rToken = /-->/;
         var rWhitespace = /[\t ]+/;

         if (!line || line.indexOf(vttTimeDelimiter) === -1) {
            throw new Error('Bad cue');
         }

         lineSegments = line.replace(rToken, ' ' + vttTimeDelimiter + ' ').split(rWhitespace);

         if (lineSegments.length < 2) {
            throw new Error('Bad cue');
         }

         sub.id = line;
         sub.start = toSeconds(lineSegments[0]);
         sub.end = toSeconds(lineSegments[2]);

         return sub;
      }

      function skipWhitespace(lines, len, i) {
         while (i < len && !lines[i]) {
            i++;
         }

         return i;
      }

      function skipNonWhitespace(lines, len, i) {
         while (i < len && lines[i]) {
            i++;
         }

         return i;
      }

      function skipToCueHeader(lines, len, i) {
         while (i < len && lines[i].indexOf(vttTimeDelimiter) === -1) {
            i++;
         }
         return i;
      }

      function readCaptionsFromString(input, languageIdentifierString) {
         languageIdentifierString = languageIdentifierString || baseNewlineParser.DEFAULT_CAPTION_LANGUAGE_IDENTIFIER_STRING;

         var captionCollection = CaptionCollection$1.create();
         var i = 0;
         var len = 0;
         var lines;
         var text;
         var sub;
         var newlineRegex = /(?:\r\n|\r|\n)/gm;
         var breakTagRegex = /(<br *\/?>)/gm;

         // Here is where the magic happens
         // Split on line breaks
         lines = input.split(newlineRegex);
         len = lines.length;

         // Check for BOF token
         if (len === 0 || lines[0] !== 'WEBVTT') {
            return captionCollection;
         }

         i++;

         while (i < len) {
            text = [];

            try {
               i = skipWhitespace(lines, len, i);
               i = skipToCueHeader(lines, len, i);

               sub = parseCueHeader(lines[i++]);

               // Build single line of text from multi-line subtitle in file
               while (i < len && lines[i]) {
                  text.push(lines[i++]);
               }

               // Join lines together to one and build subtitle text
               sub.text = text.join('\n').replace(breakTagRegex, '\n');
               sub.text = (new window.DOMParser()).parseFromString(sub.text, 'text/html').documentElement.textContent;

               var createParams = {
                  startTime: sub.start * 1000,
                  duration: sub.end * 1000 - sub.start * 1000,
                  captionTextList: [
                     CaptionText$1.create({text: sub.text, languageIdentifierString: languageIdentifierString})
                  ],
                  horizontalAlignment: 'Center',
                  verticalAlignment: 'Bottom'
               };

               captionCollection.addCaption(Caption$1.create(createParams));
            } catch (e) {
               i = skipNonWhitespace(lines, len, i);
            }
         }

         var captionTrack = CaptionTrack$1.create({
            captions: captionCollection.captions,
            languageIdentifierString: languageIdentifierString
         });

         return captionTrack;
      }

      function captionsToString(captionCollection) {
         var captionString = baseNewlineParser.captionsToString(captionCollection, {
            timeDelimiter: vttTimeDelimiter,
            accumulator: 'WEBVTT\n\n',
            hasIdRow: false,
            millisecondDelimiter: '.'
         });
         return captionString;
      }

      return {
         deserialize: readCaptionsFromString,
         serialize: captionsToString
      };
   });
};

var vttParser = {
   registerParser: registerParser$5
};

samiParser.registerParser();
sbvParser.registerParser();
srtParser.registerParser();
ttmlParser.registerParser();
vttParser.registerParser();

export { Caption$1 as Caption, CaptionCollection$1 as CaptionCollection, CaptionController$1 as CaptionController, CaptionText$1 as CaptionText, CaptionTrack$1 as CaptionTrack, ImageRectangle$1 as ImageRectangle, LanguageIdentifier$1 as LanguageIdentifier, TableOfContentsItem$1 as TableOfContentsItem, TableOfContentsTrack$1 as TableOfContentsTrack, UndoAction$1 as UndoAction, xmp as Xmp, captionParsers, languageIdentifiers };
//# sourceMappingURL=tsc-media-extensions.esm.js.map
